import { interventionLetterOptions, defaultInterventionLetterSelections, interventionLetterCoverage, interventionLetterDraftItems } from '../../../../utils/interventionLetterCoverage';
import { canReviseAtDecisionStage } from '../../../../lib/decisionStageRevision';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from 'react-router-dom';
import WorkflowWizard, { WorkflowExitActions } from "../../../../components/WorkflowWizard";
import AssessmentEditButton from "../../../../components/AssessmentEditButton";
import RecordedDecision from "../../../../components/RecordedDecision";
import LetterEditor from "../../../../components/LetterEditor";
import useWorkQueueReturn from "../../../../hooks/useWorkQueueReturn";
import { BoardItem } from "@cloudscape-design/board-components";
import {
  Alert,
  Box,
  Button,
  ButtonDropdown,
  Checkbox,
  ColumnLayout,
  DatePicker,
  FormField,
  Grid,
  Header,
  Input,
  Link,
  Modal,
  Select,
  SpaceBetween,
  StatusIndicator,
  Textarea,
  Autosuggest,
  Multiselect,
  Table,
  Tabs,
} from "@cloudscape-design/components";
import { apiFetch } from "../../../../auth/apiClient";
import { canPerformRegionalReview } from "../../../../lib/reviewWorkflow";
import useReviewWorkflowAttribution, { attributedLabel } from "../../../../hooks/useReviewWorkflowAttribution";
import { boardItemI18nStrings } from "../../widgets/common";
import { useCaseWorkspace } from "../CaseWorkspaceContext.jsx";
import useCurrentUser from "../../../../hooks/useCurrentUser.js";
import useScopedInterventionForm from '../../../../hooks/useScopedInterventionForm';
import { interventionAssessmentScope, packInterventionAssessmentDraft, restoreInterventionAssessmentDraft } from '../../../../utils/interventionAssessmentDraft';
import useInterventionAssessmentNarratives from '../../../../hooks/useInterventionAssessmentNarratives';
import { INTERVENTION_NARRATIVE_FIELDS, hasInterventionAssessmentNarratives, readInterventionAssessmentNarratives } from '../../../../lib/interventionAssessmentNarratives';
import { findOptionByValue } from "../../../finance/widgets/paymentOptions";
import { getCurrencyInputDisplayValue } from "../../../../utils/currencyFormat";
import {
  normalizeInterventionStatus,
  resolveInterventionApprovalLetterFollowUp,
  resolveInterventionStateFields,
} from "../../../../utils/interventionStatus.js";
import {
  isEducationInterventionCode as isEducationCode,
  isEmployerInterventionCode as isEmployerCode,
  isWageSubsidyInterventionCode as isWageSubsidyCode,
  requiresExternalPartnerForInterventionCode as requiresExternalPartnerForCode,
  requiresNocForInterventionCode as requiresNocForCode,
} from "../../../../utils/interventionCodeRules.js";
import { getInterventionPlanFundingIssues, resolveInterventionReviewEiEligibility } from "../../../../utils/interventionEiEligibility.js";
import {
  canEditInterventionAssessmentBody,
  canRecallInterventionAssessmentSubmission,
} from "../../../../utils/interventionAssessmentEditAccess.js";
import { getInterventionWorkingPlans, isActionPlanSelectableForApplication, resolveInterventionWorkApplicationId } from "../../../../utils/interventionApplicationPlanScope.js";
import { selectLatestSupportedSigningWorkflow } from "../../../../lib/signingWorkflowAvailability.js";
import { revisionSourceId } from '../../../../lib/interventionRevisionPolicy';
import { resolveInterventionRevisionAction } from '../../../../utils/interventionRevisionAction';
import { pendingInterventionCompletions, approvedSourceForRevision } from '../../../../utils/interventionCompletionSelection';
import { carryWorkQueueOrigin } from '../../../../utils/workQueueReturn';
import { buildApprovalWorkspacePath } from '../../../../utils/approvalWorkspaceEntry';
import styles from "./InterventionAssessmentWidget.module.css";

const BARRIER_OPTIONS = [
  { value: "education", label: "Education" },
  { value: "lack_of_skills", label: "Lack of marketable skills" },
  { value: "lack_of_experience", label: "Lack of work experience" },
  { value: "remoteness", label: "Remoteness" },
  { value: "transportation", label: "Lack of transportation" },
  { value: "economic", label: "Economic" },
  { value: "language", label: "Language" },
  { value: "dependent_care", label: "Dependent care" },
  { value: "health", label: "Health" },
  { value: "other", label: "Other" },
];

const SUBMITTED_PROPOSAL_DECIDER_ROLE_KEYS = new Set([
  "systemadministrator",
  "nwacadministrator",
]);

const HIGH_VALUE_FUNDING_APPROVAL_THRESHOLD = 20000;
const HIGH_VALUE_FUNDING_APPROVER_EMAIL = "sstacey@nwac.ca";

const INTERVENTION_REVIEW_STAGES = {
  rmReview: "rm_review",
  nwacReview: "nwac_review",
  returnedToRm: "returned_to_rm",
  returnedToSubmitter: "returned_to_submitter",
  finalDecisionRecorded: "final_decision_recorded",
  withdrawn: "withdrawn",
};

const INTERVENTION_REVIEW_ACTIONS = {
  rmReturnToSubmitter: "rm_return_to_submitter",
  rmSubmitToNwac: "rm_submit_to_nwac",
  rmForwardChangesToSubmitter: "rm_forward_changes_to_submitter",
};

const normalizeRoleKey = value =>
  String(value || "").trim().toLowerCase().replace(/[\s_-]+/g, "");

const normalizeFundingStream = value => {
  if (!value) return "";
  const normalized = String(value).trim().toUpperCase();
  if (normalized.includes("CRF")) return "CRF";
  if (normalized.includes("EI")) return "EI";
  return normalized;
};

const BASE_STEP_IDS = [
  "plan",
  "framing",
  "rationale",
  "otherFunding",
  "childcare",
  "cost",
  "docs",
  "review",
];
const SUBMITTED_STEP_IDS = ["decision"];
const ALL_STEP_IDS = [...BASE_STEP_IDS, ...SUBMITTED_STEP_IDS];
const resolveRequestedWizardStep = (requestedStep, stepIds = ALL_STEP_IDS) => {
  const normalized = String(requestedStep || "").trim();
  if (!normalized) return null;
  if (stepIds.includes(normalized)) return normalized;
  if (normalized === "decision" && stepIds.includes("review")) return "review";
  return null;
};
const STEP_LABELS = {
  plan: "Action plan",
  framing: "What is being proposed?",
  rationale: "Assessment and rationale",
  otherFunding: "Other funding sources",
  childcare: "Does the client need childcare?",
  cost: "What will it cost?",
  docs: "Do you have the right supporting documents?",
  review: "Review and submit",
  decision: "Record of decision",
  communication: "Approval letters",
};
const REQUIRED_STEP_IDS = ["plan", "framing", "rationale", "cost"];

const getProposalValidationFeedback = errors => {
  const firstMessage = value => typeof value === 'string' ? value
    : value && typeof value === 'object' ? Object.values(value).map(firstMessage).find(Boolean) : null;
  const groups = [
    ['plan', errors.actionPlanId],
    ['framing', errors.interventions],
    ['rationale', [errors.rationale, errors.narratives, ...INTERVENTION_NARRATIVE_FIELDS.map(({ key }) => errors[key])]],
    ['cost', errors.costLines],
  ];
  for (const [step, errorsForStep] of groups) {
    const message = firstMessage(errorsForStep);
    if (message) return { step, message: `${STEP_LABELS[step]}: ${message}` };
  }
  return null;
};

const RATIONALE_WORD_LIMIT = 400;
const OTHER_FUNDING_INVOLVED_OPTIONS = [
  { label: "No", value: "no" },
  { label: "Yes", value: "yes" },
  { label: "Unknown", value: "unknown" },
];
const OTHER_FUNDER_STATUS_CONFIRMED = "confirmed";
const OTHER_FUNDER_STATUS_OPTIONS = [
  {
    label: "Confirmed",
    value: OTHER_FUNDER_STATUS_CONFIRMED,
    description: "This funder has confirmed coverage.",
  },
  {
    label: "Pending",
    value: "pending",
    description: "A request or application is still waiting for a decision.",
  },
  {
    label: "Denied",
    value: "denied",
    description: "Funding was requested and denied.",
  },
  {
    label: "Unknown / not confirmed",
    value: "unknown",
    description: "The source is known, but coverage or amount is not confirmed.",
  },
];
const OTHER_FUNDER_STATUS_VALUE_SET = new Set(OTHER_FUNDER_STATUS_OPTIONS.map(option => option.value));
const OTHER_FUNDER_TYPE_OPTIONS = [
  {
    label: "ISET Holder",
    value: "iset_holder",
    description: "Another ISET holder funding part of the plan.",
  },
  {
    label: "Federal Program",
    value: "federal_program",
    description: "Federal funding program outside ISET.",
  },
  {
    label: "Prov/Terr Program",
    value: "provincial_territorial_program",
    description: "Provincial or territorial grant/support.",
  },
  {
    label: "Indigenous Government",
    value: "indigenous_government_org",
    description: "Band, Tribal Council, Métis/Inuit/regional Indigenous org.",
  },
  {
    label: "Employer",
    value: "employer",
    description: "Employer-funded training, wage support, or sponsorship.",
  },
  {
    label: "Bursary/Scholarship",
    value: "education_bursary_scholarship",
    description: "Education bursary, scholarship, or award.",
  },
  {
    label: "Nonprofit/Charity",
    value: "nonprofit_charity",
    description: "Foundation, charity, or community nonprofit support.",
  },
  {
    label: "Insurance/Compensation",
    value: "insurance_compensation",
    description: "Insurance, WCB/WSIB, settlement, or compensation support.",
  },
  {
    label: "Personal/Family",
    value: "personal_family",
    description: "Self-funded or family-funded support.",
  },
  {
    label: "Other Public",
    value: "other_public",
    description: "Municipal or other public agency support.",
  },
  {
    label: "Other",
    value: "other",
    description: "Any other funding source.",
  },
];
const OTHER_FUNDER_TYPE_VALUE_SET = new Set(OTHER_FUNDER_TYPE_OPTIONS.map(option => option.value));
const resolveOtherFunderTypeLabel = value =>
  OTHER_FUNDER_TYPE_OPTIONS.find(option => option.value === normalizeOtherFunderType(value))?.label || "Other";
const normalizeOtherFunderStatus = (value, source = {}) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (normalized === "not_confirmed" || normalized === "notconfirmed") return "unknown";
  if (OTHER_FUNDER_STATUS_VALUE_SET.has(normalized)) return normalized;
  return String(source?.coverage || "").trim() ? OTHER_FUNDER_STATUS_CONFIRMED : "unknown";
};
const resolveOtherFunderStatusLabel = value =>
  OTHER_FUNDER_STATUS_OPTIONS.find(option => option.value === normalizeOtherFunderStatus(value))?.label ||
  "Unknown / not confirmed";
const isConfirmedOtherFundingSource = source =>
  normalizeOtherFunderStatus(source?.status, source) === OTHER_FUNDER_STATUS_CONFIRMED;

const defaultFormState = {
  actionPlanId: "",
  rationale: "",
  ...readInterventionAssessmentNarratives(),
  narrativeScope: null,
  narrativesFromLegacySnapshot: false,
  otherFundingInvolved: "",
  otherFundingSources: [],
  otherFundingNwacCoverage: "",
  otherFundingNotes: "",
  childcareNeed: "",
  childcareFunding: "",
  barriers: [],
  proposedInterventions: [],
  eiVerificationStatus: "",
  eiVerificationNotes: "",
  decisionOutcome: "",
  decisionNotes: "",
  eiVerificationDocumentId: null,
};

const DEFAULT_ORG_NAME = "NWAC ISET Program";
const SUPPORT_LABEL_OVERRIDES = {
  TuitionFeesDirect: "tuition",
  TuitionFeesReimbursement: "tuition",
  BooksMaterialsDirect: "books or program materials",
  BooksMaterialsReimbursement: "books or program materials",
  LivingAllowance: "living allowance",
  ResidenceCost: "residence costs",
  Childcare: "childcare",
  Transportation: "transportation",
  WageSubsidyEmployer: "targeted wage subsidy",
  SpecializedEquipmentAdvance: "specialized equipment",
  SpecializedEquipmentReimbursement: "specialized equipment",
  JCPProjectCost: "project costs",
  SEBSupport: "SEB support",
  OtherEligibleCost: "other eligible cost",
};

const toSafeFileToken = (value, fallback = "letter") => {
  const token = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return token || fallback;
};

const toTitleCaseWords = value =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());

const toSentenceCaseWords = value => {
  const normalized = String(value || "").replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
};

const formatCostTypeForLetter = type => {
  const direct = String(type || "").trim();
  if (!direct) return "Approved support";
  if (SUPPORT_LABEL_OVERRIDES[direct]) {
    return toSentenceCaseWords(SUPPORT_LABEL_OVERRIDES[direct]);
  }
  return toSentenceCaseWords(toTitleCaseWords(direct));
};

const formatCaseManagerSignatureLines = ({ caseManagerName = "", caseManagerEmail = "", caseManagerPhone = "" } = {}) => {
  const lines = [];
  lines.push("NATIVE WOMEN'S ASSOCIATION OF CANADA (ISET Program)");
  lines.push("");
  if (caseManagerName) lines.push(caseManagerName);
  if (caseManagerEmail) lines.push(caseManagerEmail);
  if (caseManagerPhone) lines.push(caseManagerPhone);
  return lines.join("\n");
};

const buildInstitutionApprovalLetters = ({
  interventions = [],
  applicantName = "",
  decisionDate = "",
  caseManagerName = "",
  caseManagerEmail = "",
  caseManagerPhone = "",
  isRevision = false,
} = {}) => {
  const normalizePayeeType = value => String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
  const resolveInstitutionPayeeCategory = (line, institutionName) => {
    const payee = line?.payee && typeof line.payee === "object" ? line.payee : {};
    const payeeTypeKey = normalizePayeeType(payee.type);
    const payeeTypeRaw = String(payee.type || "").trim().toLowerCase();
    const payeeName = String(payee.name || "").trim().toLowerCase();
    const institutionKey = String(institutionName || "").trim().toLowerCase();
    const paymentType = String(line?.type || "").trim().toLowerCase();
    const payeeHint = `${payeeTypeKey} ${payeeTypeRaw} ${payeeName}`.trim();

    if (/(participant|client|student|applicant)/.test(payeeHint)) return "client";
    if (/(institution|university|college|school|training)/.test(payeeHint)) return "institution";
    if (/(employer|supplier|vendor|provider|organization|organisation|community|nonprofit|charity)/.test(payeeHint)) {
      return "other";
    }
    if (
      payeeTypeKey === "accreditededucationaltraininginstitution" ||
      payeeTypeKey === "traininginstitution" ||
      payeeTypeKey === "traininginstitute"
    ) {
      return "institution";
    }
    if (institutionKey && payeeName && payeeName.includes(institutionKey)) return "institution";
    if (paymentType.includes("tuition")) return "institution";
    if (paymentType.includes("reimbursement")) return "client";
    if (
      paymentType.includes("living") ||
      paymentType.includes("transport") ||
      paymentType.includes("childcare")
    ) {
      return "client";
    }
    if (paymentType.includes("books") || paymentType.includes("materials") || paymentType.includes("equipment")) {
      if (paymentType.includes("direct")) return "other";
      return "client";
    }
    return "other";
  };

  const byInstitution = new Map();
  (Array.isArray(interventions) ? interventions : []).forEach(intervention => {
    const institution = String(intervention?.institution || "").trim();
    if (!institution) return;
    const interventionId = intervention?.id;
    if (!byInstitution.has(institution)) {
      byInstitution.set(institution, {
        institution,
        programs: new Set(),
        terms: new Set(),
        lineItems: [],
      });
    }
    const target = byInstitution.get(institution);
    const programName = String(intervention?.programName || "").trim();
    if (programName) target.programs.add(programName);
    const termLabel = formatInterventionDates(intervention?.startDate, intervention?.endDate);
    if (termLabel && termLabel !== "—") target.terms.add(termLabel);
    const costLines = Array.isArray(intervention?.costLines) ? intervention.costLines : [];
    costLines.forEach((line, lineIndex) => {
      const amount = parseCurrencyToNumber(line?.amount);
      if (!(amount > 0)) return;
      const label = formatCostTypeForLetter(line?.type);
      target.lineItems.push({
        id: `${interventionId || "intervention"}-${line?.id || lineIndex + 1}`,
        label,
        amount,
        termLabel,
        type: line?.type || "",
        payee: line?.payee && typeof line.payee === "object" ? { ...line.payee } : null,
      });
    });
  });

  return Array.from(byInstitution.values()).map((item, index) => {
    const programText = item.programs.size ? Array.from(item.programs).join(", ") : "approved training supports";
    const termText = item.terms.size ? Array.from(item.terms).join("; ") : "As assessed";
    const fundingLines = item.lineItems.length
      ? item.lineItems
      : [{
          id: `total-${index + 1}`,
          label: "Approved training costs",
          amount: 0,
          termLabel: termText,
          type: "TuitionFeesDirect",
          payee: { type: "AccreditedEducationalTrainingInstitution", name: item.institution },
        }];
    const formatFundingLine = line => `- ${line.label}: $${Number(line.amount || 0).toFixed(2)}  Term/Dates: ${line.termLabel || termText}`;
    const institutionPayLines = [];
    const clientPayLines = [];
    const otherPayLines = [];
    fundingLines.forEach(line => {
      const category = resolveInstitutionPayeeCategory(line, item.institution);
      if (category === "institution") institutionPayLines.push(line);
      else if (category === "client") clientPayLines.push(line);
      else otherPayLines.push(line);
    });
    const fundingSectionParts = [];
    if (institutionPayLines.length) {
      fundingSectionParts.push(
        isRevision
          ? `The revised approved costs payable directly to ${item.institution} on behalf of the student are:`
          : `The NWAC ISET Program has approved payment of the following costs directly to ${item.institution} on behalf of the student:`,
        institutionPayLines.map(formatFundingLine).join("\n")
      );
    }
    if (clientPayLines.length) {
      fundingSectionParts.push(
        isRevision
          ? "The following revised supports are also approved for payment directly to the student:"
          : "The following supports have also been approved for payment directly to the student:",
        clientPayLines.map(formatFundingLine).join("\n")
      );
    }
    if (otherPayLines.length) {
      fundingSectionParts.push(
        isRevision
          ? "The following revised supports are also approved for payment to other eligible payees:"
          : "The following supports have also been approved for payment to other eligible payees:",
        otherPayLines.map(formatFundingLine).join("\n")
      );
    }
    const signatureBlock = formatCaseManagerSignatureLines({
      caseManagerName,
      caseManagerEmail,
      caseManagerPhone,
    });
    const body = [
      isRevision ? "Funding Revision Letter (Institution)" : "Letter of Approval (Institution)",
      `Date: ${decisionDate || ""}`,
      "",
      item.institution,
      "",
      "To Whom It May Concern,",
      "",
      isRevision
        ? "This letter is to formally confirm that the Native Women's Association of Canada (NWAC), through its Indigenous Skills and Employment Training (ISET) Program, has approved a revision to the education-related funding previously approved on behalf of the following student:"
        : "This letter is to formally confirm that the Native Women's Association of Canada (NWAC), through its Indigenous Skills and Employment Training (ISET) Program, has approved education-related funding on behalf of the following student:",
      "",
      `Student Name: ${applicantName || "Student"}`,
      `Training Institution: ${item.institution}`,
      `Program of Study: ${programText}`,
      "",
      fundingSectionParts.join("\n\n"),
      "",
      isRevision
        ? "These revised funds are provided under the ISET Program and are intended solely to support the student's participation in the approved training program noted above. Please note that all payments are made on behalf of the student. In the event of an overpayment, withdrawal, or change in enrollment status, any unused or refunded funds must be returned directly to the NWAC ISET Program and not issued to the student."
        : "These funds are provided under the ISET Program and are intended solely to support the student's participation in the approved training program noted above. Please note that all payments are made on behalf of the student. In the event of an overpayment, withdrawal, or change in enrollment status, any unused or refunded funds must be returned directly to the NWAC ISET Program and not issued to the student.",
      "",
      "Should you require additional documentation or clarification, please do not hesitate to contact the undersigned.",
      "",
      "Sincerely,",
      signatureBlock,
    ].join("\n");
    return {
      id: `institution-${index + 1}`,
      recipientName: item.institution,
      title: isRevision
        ? `Institution Funding Revision Letter — ${item.institution}`
        : `Institution Letter — ${item.institution}`,
      fileName: `institution-letter-${toSafeFileToken(item.institution, `recipient-${index + 1}`)}.txt`,
      body,
    };
  });
};

const buildCoFunderApprovalLetters = ({
  fundingSources = [],
  nwacCoverage = "",
  notes = "",
  interventions = [],
  applicantName = "",
  trackingReference = "",
  decisionDate = "",
  caseManagerName = "",
  caseManagerEmail = "",
  caseManagerPhone = "",
  isRevision = false,
} = {}) => {
  const normalizeInlineText = value => String(value || "").replace(/\s+/g, " ").trim();
  const approvedTotal = (Array.isArray(interventions) ? interventions : []).reduce((sum, intervention) => {
    const lines = Array.isArray(intervention?.costLines) ? intervention.costLines : [];
    return sum + lines.reduce((lineSum, line) => {
      const amount = parseCurrencyToNumber(line?.amount);
      return lineSum + (amount > 0 ? amount : 0);
    }, 0);
  }, 0);
  const institutionSet = new Set();
  const programSet = new Set();
  const termSet = new Set();
  const nwacFundingBreakdownLines = [];
  (Array.isArray(interventions) ? interventions : []).forEach(intervention => {
    const institution = String(intervention?.institution || "").trim();
    const program = String(intervention?.programName || "").trim();
    if (institution) institutionSet.add(institution);
    if (program) programSet.add(program);
    const term = formatInterventionDates(intervention?.startDate, intervention?.endDate);
    if (term && term !== "—") termSet.add(term);
    const costLines = Array.isArray(intervention?.costLines) ? intervention.costLines : [];
    costLines.forEach(line => {
      const amount = parseCurrencyToNumber(line?.amount);
      if (!(amount > 0)) return;
      const lineLabel = formatCostTypeForLetter(line?.type);
      const payee = line?.payee && typeof line.payee === "object" ? line.payee : {};
      const payeeType = String(payee.type || deriveDefaultPayeeTypeForCostLine(line?.type) || "").trim();
      const explicitPayeeName = normalizeInlineText(payee.name || "");
      const defaultPayeeName = normalizeInlineText(
        deriveDefaultPayeeNameForCostLine(payeeType, intervention, applicantName || "")
      );
      const payeeName = explicitPayeeName || defaultPayeeName;
      const payeeTypeKey = normalizePayeeTypeKey(payeeType);
      const payeePhrase = (() => {
        if (payeeName) return payeeName;
        if (payeeTypeKey === "participantclient" || payeeTypeKey === "client") return applicantName || "the student";
        const target = PAYEE_TYPE_DETAIL_TARGET_BY_KEY[payeeTypeKey] || "";
        return target ? `the ${target}` : "the approved payee";
      })();
      const termLabel = term && term !== "—" ? term : "";
      nwacFundingBreakdownLines.push(
        `- ${lineLabel}: $${Number(amount).toFixed(2)} payable to ${payeePhrase}${termLabel ? ` (Term/Dates: ${termLabel})` : ""}`
      );
    });
  });
  const institutionText = institutionSet.size ? Array.from(institutionSet).join("; ") : "the approved training institution";
  const programText = programSet.size ? Array.from(programSet).join("; ") : "the approved program";
  const termText = termSet.size ? Array.from(termSet).join("; ") : "the approved term(s)";
  const signatureBlock = formatCaseManagerSignatureLines({
    caseManagerName,
    caseManagerEmail,
    caseManagerPhone,
  });

  return (Array.isArray(fundingSources) ? fundingSources.filter(isConfirmedOtherFundingSource) : [])
    .map((source, index) => {
      const funderName = String(source?.name || "").trim();
      if (!funderName) return null;
      const funderTypeLabel = resolveOtherFunderTypeLabel(source?.type);
      const sourceCoverage = String(source?.coverage || "").trim();
      const sourceAmount = formatOtherFundingAmountDisplay(source?.amount);
      const body = [
        isRevision ? "Funding Revision Letter (Other Funding Source)" : "Letter of Approval (Other Funding Source)",
        `Date: ${decisionDate || ""}`,
        "",
        funderName,
        "",
        "To Whom It May Concern,",
        "",
        isRevision
          ? `I am writing to let you know that the Native Women's Association of Canada (NWAC), through its Indigenous Skills and Employment Training (ISET) Program, has approved a revision to the funding for ${applicantName || "the student"} for ${termText} in ${programText} at ${institutionText}.`
          : `I am writing to let you know that the Native Women's Association of Canada (NWAC), through its Indigenous Skills and Employment Training (ISET) Program, will be funding ${applicantName || "the student"} for ${termText} in ${programText} at ${institutionText}.`,
        "",
        approvedTotal > 0
          ? isRevision
            ? `The revised approved NWAC funding is $${Number(approvedTotal).toFixed(2)} for eligible costs under this intervention plan, which will be paid directly to approved payees as specified in the assessment.`
            : `I have approved funding in the amount of $${Number(approvedTotal).toFixed(2)} for eligible costs under this intervention plan, which will be paid directly to approved payees as specified in the assessment.`
          : isRevision
            ? "The approved NWAC funding for eligible costs under this intervention plan has been revised and will be paid directly to approved payees as specified in the assessment."
            : "I have approved funding for eligible costs under this intervention plan, which will be paid directly to approved payees as specified in the assessment.",
        nwacFundingBreakdownLines.length ? "" : null,
        nwacFundingBreakdownLines.length ? (isRevision ? "Revised NWAC funding breakdown:" : "NWAC funding breakdown:") : null,
        nwacFundingBreakdownLines.length ? nwacFundingBreakdownLines.join("\n") : null,
        "",
        `As documented in the assessment records, ${funderName} (${funderTypeLabel}) is identified as funding the following:`,
        sourceCoverage ? `- ${normalizeInlineText(sourceCoverage)}` : "- Funding details to be confirmed through your office.",
        sourceAmount ? `- Confirmed other funding amount: ${sourceAmount}` : "",
        nwacCoverage ? `- NWAC coverage summary: ${normalizeInlineText(nwacCoverage)}` : "",
        notes ? `- Coordination notes: ${normalizeInlineText(notes)}` : "",
        trackingReference ? `- File reference: ${normalizeInlineText(trackingReference)}` : "",
        "",
        "If you have any questions, please do not hesitate to contact me directly.",
        "",
        "Sincerely,",
        signatureBlock,
      ]
        .map(line => (line === null || typeof line === "undefined" ? "" : String(line)))
        .join("\n");
      return {
        id: source?.id || `funder-${index + 1}`,
        recipientName: funderName,
        title: isRevision
          ? `Other Funding Source Revision Letter — ${funderName}`
          : `Other Funding Source Letter — ${funderName}`,
        fileName: `other-funding-source-letter-${toSafeFileToken(funderName, `recipient-${index + 1}`)}.txt`,
        body,
      };
    })
    .filter(Boolean);
};

const buildLoanProviderApprovalLetters = ({
  interventions = [],
  applicantName = "",
  trackingReference = "",
  decisionDate = "",
  caseManagerName = "",
  caseManagerEmail = "",
  caseManagerPhone = "",
  isRevision = false,
} = {}) => {
  const normalizeInlineText = value => String(value || "").replace(/\s+/g, " ").trim();
  const formatCurrency = value => `$${Number(value || 0).toFixed(2)}`;
  const groupedLetters = new Map();

  (Array.isArray(interventions) ? interventions : []).forEach(intervention => {
    const termLabel = formatInterventionDates(intervention?.startDate, intervention?.endDate);
    const costLines = Array.isArray(intervention?.costLines) ? intervention.costLines : [];
    costLines.forEach((line, lineIndex) => {
      if (normalizePaymentTypeCode(line?.type) !== "StudentLoanRepayment") return;
      const amount = parseCurrencyToNumber(line?.amount);
      if (!(amount > 0)) return;
      const payee = line?.payee && typeof line.payee === "object" ? line.payee : {};
      const payeeType = String(payee.type || deriveDefaultPayeeTypeForCostLine(line?.type) || "").trim();
      const explicitPayeeName = normalizeInlineText(payee.name || "");
      const defaultPayeeName = normalizeInlineText(
        deriveDefaultPayeeNameForCostLine(payeeType, intervention, applicantName || "")
      );
      const payeeName = explicitPayeeName || defaultPayeeName || "Student loan provider";
      const accountNumber = normalizeInlineText(payee.reference || "");
      const groupKey = `${payeeName.toLowerCase()}::${accountNumber.toLowerCase() || "no-account"}`;
      if (!groupedLetters.has(groupKey)) {
        groupedLetters.set(groupKey, {
          payeeName,
          accountNumber,
          totalAmount: 0,
          lineItems: [],
        });
      }
      const target = groupedLetters.get(groupKey);
      target.totalAmount += amount;
      target.lineItems.push({
        id: `${intervention?.id || "intervention"}-${line?.id || lineIndex + 1}`,
        label: formatCostTypeForLetter(line?.type),
        amount,
        termLabel: termLabel && termLabel !== "—" ? termLabel : "",
      });
    });
  });

  const signatureBlock = formatCaseManagerSignatureLines({
    caseManagerName,
    caseManagerEmail,
    caseManagerPhone,
  });

  return Array.from(groupedLetters.values()).map((item, index) => {
    const applicantPossessive = applicantName ? `${applicantName}'s` : "the participant's";
    const fundingLines = item.lineItems.map(line =>
      `- ${line.label}: ${formatCurrency(line.amount)}${line.termLabel ? ` (Term/Dates: ${line.termLabel})` : ""}`
    );
    const body = [
      isRevision ? "Funding Revision Letter (Loan Provider)" : "Letter of Approval (Loan Provider)",
      `Date: ${decisionDate || ""}`,
      "",
      item.payeeName,
      "",
      "To Whom It May Concern,",
      "",
      isRevision
        ? `This letter is to formally confirm that the Native Women's Association of Canada (NWAC), through its Indigenous Skills and Employment Training (ISET) Program, has approved a revision to the repayment support for ${applicantPossessive} eligible student loan. The revised total amount is ${formatCurrency(item.totalAmount)}. Please apply this payment to the repayable portion of the account noted below.`
        : `This letter is to formally confirm that the Native Women's Association of Canada (NWAC), through its Indigenous Skills and Employment Training (ISET) Program, will repay a portion of ${applicantPossessive} eligible student loan in the total amount of ${formatCurrency(item.totalAmount)}. Please apply this payment to the repayable portion of the account noted below.`,
      "",
      `Student Name: ${applicantName || "Student"}`,
      item.accountNumber
        ? `Loan Account Number: ${item.accountNumber}`
        : "Loan Account Number: To be confirmed by case manager",
      trackingReference ? `File Reference: ${normalizeInlineText(trackingReference)}` : null,
      fundingLines.length ? "" : null,
      fundingLines.length ? (isRevision ? "Revised approved repayment lines:" : "Approved repayment lines:") : null,
      fundingLines.length ? fundingLines.join("\n") : null,
      "",
      "If there is an overpayment, or if the participant withdraws from the approved program, any refunded funds must be returned directly to the NWAC ISET Program and not to the participant.",
      "",
      "Please let me know if you have any questions.",
      "",
      "Sincerely,",
      signatureBlock,
    ]
      .map(line => (line === null || typeof line === "undefined" ? "" : String(line)))
      .join("\n");
    return {
      id: `loan-provider-${index + 1}`,
      recipientName: item.payeeName,
      title: isRevision
        ? `Loan Provider Revision Letter — ${item.payeeName}`
        : `Loan Provider Letter — ${item.payeeName}`,
      fileName: `loan-provider-letter-${toSafeFileToken(item.payeeName, `recipient-${index + 1}`)}.txt`,
      body,
    };
  });
};

const formatDate = value => {
  if (!value) return "";
  if (typeof value === "string" && value.length >= 10) return value.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const parseIsoDateToUtc = value => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? null
      : Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
  }
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(/\//g, "-");
  const parts = normalized.split("-");
  if (parts.length !== 3) return null;
  const [yyyy, mm, dd] = parts.map(part => Number.parseInt(part, 10));
  if (![yyyy, mm, dd].every(Number.isFinite)) return null;
  return Date.UTC(yyyy, mm - 1, dd);
};

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const formatShortDate = value => {
  const normalized = formatDate(value);
  if (!normalized) return "";
  const [yyyy, mm, dd] = normalized.split("-");
  const monthIndex = Number(mm) - 1;
  if (!yyyy || !dd || !Number.isInteger(monthIndex) || monthIndex < 0 || monthIndex > 11) return "";
  const monthLabel = MONTH_LABELS[monthIndex];
  return `${dd.padStart(2, "0")} ${monthLabel} ${yyyy}`;
};

const formatInterventionDates = (startDate, endDate) => {
  const normalizedStart = formatDate(startDate);
  const normalizedEnd = formatDate(endDate);
  const start = formatShortDate(normalizedStart);
  const end = formatShortDate(normalizedEnd);
  if (!start) return "—";
  if (!end || (normalizedStart && normalizedStart === normalizedEnd)) return start;
  return `${start}-${end}`;
};

const calculateDurationDays = (start, end) => {
  const startUtc = parseIsoDateToUtc(start);
  const endUtc = parseIsoDateToUtc(end);
  if (startUtc === null || endUtc === null) return null;
  const diff = Math.round((endUtc - startUtc) / (1000 * 60 * 60 * 24)) + 1;
  if (!Number.isFinite(diff) || diff < 0) return null;
  return diff;
};

const MAX_INTERVENTION_DURATION_DAYS = 999;
const clampInterventionDurationDaysForIlmp = value => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return null;
  return Math.min(Math.round(numeric), MAX_INTERVENTION_DURATION_DAYS);
};

const addMonthsUtc = (startDate, monthsToAdd) => {
  const startUtc = parseIsoDateToUtc(startDate);
  if (startUtc === null) return "";
  const base = new Date(startUtc);
  const monthIndex = base.getUTCMonth() + monthsToAdd;
  base.setUTCMonth(monthIndex);
  if (Number.isNaN(base.getTime())) return "";
  return base.toISOString().slice(0, 10);
};

const deriveEndDateFromOccurrences = (startDate, occurrences) => {
  if (!startDate || !Number.isFinite(occurrences) || occurrences <= 0) return "";
  return addMonthsUtc(startDate, occurrences - 1);
};

const resolveCaseContext = caseData => {
  if (caseData?.caseContext && typeof caseData.caseContext === "object") {
    return caseData.caseContext;
  }
  if (caseData?.case_context && typeof caseData.case_context === "object") {
    return caseData.case_context;
  }
  const rawContext = caseData?.case_context_json;
  if (typeof rawContext === "string" && rawContext.trim()) {
    try {
      const parsed = JSON.parse(rawContext);
      if (parsed && typeof parsed === "object") return parsed;
    } catch (_) {
      return {};
    }
  }
  return {};
};

const autoOccurrencesFromDates = (startDate, endDate, period) => {
  if (!startDate || !endDate || !period) return null;
  const startUtc = parseIsoDateToUtc(startDate);
  const endUtc = parseIsoDateToUtc(endDate);
  if (startUtc === null || endUtc === null || endUtc < startUtc) return null;
  const start = new Date(startUtc);
  const end = new Date(endUtc);
  const monthCount =
    (end.getUTCFullYear() - start.getUTCFullYear()) * 12 +
    (end.getUTCMonth() - start.getUTCMonth()) +
    1;
  if (period === "monthly") return Math.max(1, monthCount);
  if (period === "quarterly") return Math.max(1, Math.ceil(monthCount / 3));
  const diffDays = Math.floor((endUtc - startUtc) / (1000 * 60 * 60 * 24)) + 1;
  if (!Number.isFinite(diffDays) || diffDays < 1) return null;
  const periodDays = period === "bi_weekly" ? 14 : period === "weekly" ? 7 : null;
  if (!periodDays) return null;
  return Math.max(1, Math.ceil(diffDays / periodDays));
};

const mergeRecurrenceDefaults = (base, overrides = {}) => {
  const pick = (value, fallback) =>
    value === "" || value === null || typeof value === "undefined" ? fallback : value;
  return {
    ...base,
    ...overrides,
    startDate: pick(overrides.startDate, base.startDate),
    endDate: pick(overrides.endDate, base.endDate),
    occurrences: pick(overrides.occurrences, base.occurrences),
    amountPerPeriod: pick(overrides.amountPerPeriod, base.amountPerPeriod),
  };
};

const buildUuid = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

export const beginRetainedSecureMessageSendAttempt = ({
  inFlightRef,
  attemptRef,
  fingerprint,
  createPayload,
} = {}) => {
  if (!inFlightRef || !attemptRef || typeof createPayload !== "function") {
    throw new Error("secure_message_send_attempt_invalid");
  }
  if (inFlightRef.current) return null;
  inFlightRef.current = true;

  let attempt = attemptRef.current?.fingerprint === fingerprint
    ? attemptRef.current
    : null;
  try {
    if (!attempt) {
      attempt = {
        fingerprint,
        payload: createPayload(),
      };
      attemptRef.current = attempt;
    }
  } catch (error) {
    inFlightRef.current = false;
    throw error;
  }

  let finished = false;
  return {
    payload: { ...attempt.payload },
    finish: ({ committed = false } = {}) => {
      if (finished) return;
      finished = true;
      if (committed && attemptRef.current === attempt) {
        attemptRef.current = null;
      }
      inFlightRef.current = false;
    },
  };
};

const normalizeOtherFundingInvolved = value => {
  if (value === null || typeof value === "undefined") return "";
  const normalized = String(value).trim().toLowerCase();
  if (normalized === "yes") return "yes";
  if (normalized === "no") return "no";
  if (normalized === "unknown") return "unknown";
  return "";
};

const normalizeOtherFunderType = value => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (!normalized) return "other";
  if (OTHER_FUNDER_TYPE_VALUE_SET.has(normalized)) return normalized;
  return "other";
};

const buildEmptyOtherFundingSource = (overrides = {}) => {
  const base = {
    id: overrides.id || buildUuid(),
    name: "",
    type: "other",
    status: OTHER_FUNDER_STATUS_CONFIRMED,
    amount: "",
    coverage: "",
    notes: "",
    ...overrides,
  };
  return {
    ...base,
    type: normalizeOtherFunderType(base.type || "other"),
    status: normalizeOtherFunderStatus(base.status, base),
  };
};

const buildOtherFundingSourceModalState = (overrides = {}) => ({
  visible: false,
  mode: "add",
  sourceId: null,
  draft: buildEmptyOtherFundingSource(),
  original: null,
  ...overrides,
});

const validateOtherFundingSourceDraft = draft => {
  const next = buildEmptyOtherFundingSource(draft || {});
  const errors = {};
  if (!String(next.name || "").trim()) {
    errors.name = "Funder name is required.";
  }
  if (isConfirmedOtherFundingSource(next) && !String(next.coverage || "").trim()) {
    errors.coverage = "Coverage details are required for confirmed funding.";
  }
  const rawAmount = String(next.amount || "").trim();
  const parsedAmount = parseCurrencyInput(rawAmount);
  if (rawAmount && (parsedAmount === null || !Number.isFinite(parsedAmount) || parsedAmount < 0)) {
    errors.amount = "Enter a valid amount in dollars, or leave it blank.";
  }
  return errors;
};

const parseLegacyOtherFundingSummary = value => {
  const text = String(value || "").trim();
  if (!text) return {};
  const involvedMatch = text.match(/Other funding involved:\s*(Yes|No|Unknown)\.?/i);
  const involved = involvedMatch ? involvedMatch[1].toLowerCase() : "";
  const notesMatch = text.match(/(?:^|\s)Notes:\s*([\s\S]*)$/i);
  const notes = notesMatch
    ? notesMatch[1].trim()
    : text.replace(/Other funding involved:\s*(Yes|No|Unknown)\.?\s*/i, "").trim();
  return {
    involved,
    sources: [],
    notes: notes || text,
  };
};

const normalizeOtherFundingSources = (value, { keepEmpty = false, preserveWhitespace = false } = {}) => {
  if (!Array.isArray(value)) return [];
  return value
    .map(entry => {
      if (!entry || typeof entry !== "object") return null;
      const rawName = String(entry.name || "");
      const rawCoverage = String(entry.coverage || "");
      const rawAmount = String(entry.amount ?? entry.fundingAmount ?? entry.funding_amount ?? "");
      const rawNotes = String(entry.notes || "");
      const status = normalizeOtherFunderStatus(
        entry.status || entry.fundingStatus || entry.funding_status,
        entry
      );
      const normalized = buildEmptyOtherFundingSource({
        id: entry.id || buildUuid(),
        name: preserveWhitespace ? rawName : rawName.trim(),
        type: entry.type || "other",
        status,
        amount: preserveWhitespace ? rawAmount : rawAmount.trim(),
        coverage: preserveWhitespace ? rawCoverage : rawCoverage.trim(),
        notes: preserveWhitespace ? rawNotes : rawNotes.trim(),
      });
      const hasValues =
        rawName.trim() ||
        rawCoverage.trim() ||
        rawAmount.trim() ||
        rawNotes.trim() ||
        entry.status ||
        entry.fundingStatus ||
        entry.funding_status;
      return hasValues || keepEmpty ? normalized : null;
    })
    .filter(Boolean);
};

const normalizeOtherFundingDetails = (rawDetails, options = {}) => {
  const source =
    rawDetails && typeof rawDetails === "object"
      ? rawDetails
      : (typeof rawDetails === "string" ? parseLegacyOtherFundingSummary(rawDetails) : {});
  const keepEmptySources = Boolean(options.keepEmptySources);
  const preserveWhitespace = Boolean(options.preserveWhitespace);
  const involved = normalizeOtherFundingInvolved(source.involved);
  const populatedSources = normalizeOtherFundingSources(
    source.sources,
    { preserveWhitespace }
  );
  const sources = keepEmptySources
    ? normalizeOtherFundingSources(
        source.sources,
        { keepEmpty: true, preserveWhitespace }
      )
    : populatedSources;
  const rawNwacCoverage = String(source.nwacCoverage || "");
  const nwacCoverage = preserveWhitespace ? rawNwacCoverage : rawNwacCoverage.trim();
  const rawNotes = String(source.notes || "");
  const notes = preserveWhitespace ? rawNotes : rawNotes.trim();

  const resolvedInvolved =
    involved ||
    (populatedSources.length || String(nwacCoverage || "").trim() || String(notes || "").trim() ? "yes" : "");

  return {
    involved: resolvedInvolved,
    sources,
    nwacCoverage,
    notes,
  };
};

const normalizeId = value => {
  if (value === null || typeof value === "undefined") return "";
  return String(value);
};

const idsMatch = (left, right) => {
  const leftId = normalizeId(left);
  const rightId = normalizeId(right);
  if (!leftId || !rightId) return false;
  return leftId === rightId;
};

const parseCurrencyInput = value => {
  if (value === null || typeof value === "undefined") return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const lastDot = raw.lastIndexOf(".");
  const lastComma = raw.lastIndexOf(",");
  let cleaned = raw.replace(/[^\d.,]/g, "");
  if (!cleaned) return null;
  if (lastComma > lastDot) {
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else {
    cleaned = cleaned.replace(/,/g, "");
  }
  if (!cleaned) return null;
  const num = Number.parseFloat(cleaned);
  return Number.isFinite(num) ? num : null;
};

const parseCurrencyToNumber = value => {
  if (value === null || typeof value === "undefined") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const cleaned = String(value).replace(/[^0-9.+-]/g, "");
  if (!cleaned) return 0;
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrencyDisplay = value => {
  const num = parseCurrencyInput(value);
  if (num === null) return "";
  return `$ ${num.toFixed(2)}`;
};

const parseSignedCurrencyInput = value => {
  if (value === null || typeof value === "undefined") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const raw = String(value).trim();
  if (!raw) return null;
  const parsed = parseCurrencyInput(raw);
  if (parsed === null) return null;
  return /^-/.test(raw) ? -Math.abs(parsed) : parsed;
};

const formatCompactCurrencyDisplay = value => {
  const num = parseSignedCurrencyInput(value);
  if (num === null) return "";
  try {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch (_) {
    return `$${num.toFixed(2)}`;
  }
};

const formatSignedCompactCurrencyDisplay = value => {
  const num = parseSignedCurrencyInput(value);
  if (num === null) return "";
  const absoluteDisplay = formatCompactCurrencyDisplay(Math.abs(num));
  if (num > 0) return `+${absoluteDisplay}`;
  if (num < 0) return `-${absoluteDisplay}`;
  return formatCompactCurrencyDisplay(0);
};

const resolveInterventionCostTotal = intervention => {
  if (!intervention || typeof intervention !== "object") return null;
  const costLines = Array.isArray(intervention.costLines) ? intervention.costLines : [];
  if (costLines.length) {
    const total = costLines.reduce((sum, line) => sum + parseCurrencyToNumber(line?.amount), 0);
    return Number.isFinite(total) ? total : null;
  }
  const metadata = intervention.metadata && typeof intervention.metadata === "object" ? intervention.metadata : {};
  const snapshot = metadata.snapshot && typeof metadata.snapshot === "object" ? metadata.snapshot : {};
  const candidates = [
    intervention.plannedCost,
    intervention.cost,
    intervention.budgetAmount,
    intervention.approvedAmount,
    intervention.interventionCost,
    intervention.intervention_cost,
    metadata.cost,
    snapshot.cost,
  ];
  for (const candidate of candidates) {
    const parsed = parseCurrencyInput(candidate);
    if (parsed !== null && Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const formatOtherFundingAmountDisplay = value => {
  const amount = parseCurrencyInput(value);
  return amount !== null && Number.isFinite(amount) ? formatCurrencyDisplay(amount) : "";
};

const sanitizeCurrencyInput = (value, options = {}) => {
  const { preserveTrailingDecimal = false } = options;
  if (value === null || value === undefined) return "";
  const raw = String(value);
  const cleaned = raw.replace(/[^\d.,]/g, "");
  if (!cleaned) return "";
  const lastDot = cleaned.lastIndexOf(".");
  const lastComma = cleaned.lastIndexOf(",");
  const separatorIndex = Math.max(lastDot, lastComma);
  if (separatorIndex === -1) {
    return cleaned.replace(/[^\d]/g, "");
  }
  const whole = cleaned.slice(0, separatorIndex).replace(/[^\d]/g, "");
  const decimalRaw = cleaned.slice(separatorIndex + 1).replace(/[^\d]/g, "");
  const hasTrailingSeparator = separatorIndex === cleaned.length - 1;
  const decimals = decimalRaw.slice(0, 2);
  if (hasTrailingSeparator) {
    return preserveTrailingDecimal ? `${whole}.` : whole;
  }
  return decimals.length ? `${whole}.${decimals}` : whole;
};

const recalcRecurringAmounts = ({ amount, amountPerPeriod, occurrences, adjustMode }) => {
  const occ = Number(occurrences);
  if (!Number.isFinite(occ) || occ <= 0) {
    return { amount, amountPerPeriod };
  }
  const totalValue = parseCurrencyInput(amount);
  const perPeriodValue = parseCurrencyInput(amountPerPeriod);
  const normalize = value => (value === null || typeof value === "undefined" ? "" : formatCurrencyDisplay(value));
  if (adjustMode === "total") {
    if (Number.isFinite(perPeriodValue)) {
      return { amount: normalize(perPeriodValue * occ), amountPerPeriod };
    }
    if (Number.isFinite(totalValue)) {
      return { amount, amountPerPeriod: normalize(totalValue / occ) };
    }
    return { amount, amountPerPeriod };
  }
  if (Number.isFinite(totalValue)) {
    return { amount, amountPerPeriod: normalize(totalValue / occ) };
  }
  if (Number.isFinite(perPeriodValue)) {
    return { amount: normalize(perPeriodValue * occ), amountPerPeriod };
  }
  return { amount, amountPerPeriod };
};

const normalizeInterventionCodeValue = value => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
};

const RECURRENCE_MODE_REQUIRED = "required";
const RECURRENCE_MODE_OPTIONAL = "optional";
const RECURRENCE_MODE_NOT_ALLOWED = "not_allowed";
const SUBMISSION_TIMING_INTERVENTION_START = "intervention_start";
const SUBMISSION_TIMING_INTERVENTION_END = "intervention_end";
const SUBMISSION_TIMING_RECURRENCE_SCHEDULE = "recurrence_schedule";
const SUBMISSION_TIMING_MANUAL_TRIGGER = "manual_trigger";
const DEFAULT_SUBMISSION_TIMING_BY_TYPE = {
  LivingAllowance: SUBMISSION_TIMING_RECURRENCE_SCHEDULE,
  ResidenceCost: SUBMISSION_TIMING_INTERVENTION_START,
  TuitionFeesDirect: SUBMISSION_TIMING_INTERVENTION_START,
  TuitionFeesReimbursement: SUBMISSION_TIMING_INTERVENTION_END,
  SpecializedEquipmentAdvance: SUBMISSION_TIMING_INTERVENTION_START,
  SpecializedEquipmentReimbursement: SUBMISSION_TIMING_INTERVENTION_END,
  WageSubsidyEmployer: SUBMISSION_TIMING_RECURRENCE_SCHEDULE,
  Childcare: SUBMISSION_TIMING_RECURRENCE_SCHEDULE,
  Transportation: SUBMISSION_TIMING_RECURRENCE_SCHEDULE,
  BooksMaterialsDirect: SUBMISSION_TIMING_INTERVENTION_START,
  BooksMaterialsReimbursement: SUBMISSION_TIMING_INTERVENTION_END,
  JCPProjectCost: SUBMISSION_TIMING_MANUAL_TRIGGER,
  SEBSupport: SUBMISSION_TIMING_RECURRENCE_SCHEDULE,
  OtherEligibleCost: SUBMISSION_TIMING_MANUAL_TRIGGER,
};
const PAYMENT_TYPE_ALIASES = {
  wagesubsidyemployer: "WageSubsidyEmployer",
  wagesubsidy: "WageSubsidyEmployer",
  targetedwagesubsidyemployer: "WageSubsidyEmployer",
  targetedwagesubsidy: "WageSubsidyEmployer",
  residencecost: "ResidenceCost",
  residencecosts: "ResidenceCost",
  residencefee: "ResidenceCost",
  residencefees: "ResidenceCost",
  residence: "ResidenceCost",
  housing: "ResidenceCost",
};
const PAYEE_TYPE_PARTICIPANT_CLIENT = "ParticipantClient";
const PAYEE_TYPES_DEFAULT_FROM_INTERVENTION = new Set([
  "AccreditedEducationalTrainingInstitution",
  "EmployerWageSubsidyPartner",
  "CommunityNonProfitOrganization",
]);
const PAYMENT_TYPE_DEFAULT_PAYEE_TYPE = {
  LivingAllowance: PAYEE_TYPE_PARTICIPANT_CLIENT,
  ResidenceCost: "AccreditedEducationalTrainingInstitution",
  TuitionFeesReimbursement: PAYEE_TYPE_PARTICIPANT_CLIENT,
  SpecializedEquipmentReimbursement: PAYEE_TYPE_PARTICIPANT_CLIENT,
  Transportation: PAYEE_TYPE_PARTICIPANT_CLIENT,
  BooksMaterialsReimbursement: PAYEE_TYPE_PARTICIPANT_CLIENT,
  TuitionFeesDirect: "AccreditedEducationalTrainingInstitution",
  WageSubsidyEmployer: "EmployerWageSubsidyPartner",
  Childcare: "ChildcareProvider",
  BooksMaterialsDirect: "TrainingRelatedSupplier",
  SpecializedEquipmentAdvance: "TrainingRelatedSupplier",
  JCPProjectCost: "CommunityNonProfitOrganization",
  StudentLoanRepayment: "StudentLoanServicer",
};
const normalizePayeeTypeKey = value =>
  String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
const PAYEE_TYPE_DETAIL_TARGET_BY_KEY = {
  participantclient: "client",
  client: "client",
  vendor: "vendor",
  traininginstitution: "institution",
  traininginstitute: "institution",
  traininginstitue: "institution",
  accreditededucationaltraininginstitution: "institution",
  employer: "employer",
  employerwagesubsidypartner: "employer",
  childcareprovider: "childcare provider",
  communitynonprofitorganization: "community organization",
  trainingrelatedsupplier: "supplier",
  professionalbusinessservicesprovider: "service provider",
  studentloanservicer: "student loan provider",
  other: "other payee",
};

const normalizePaymentTypeCode = value => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const key = raw.toLowerCase().replace(/[^a-z0-9]+/g, "");
  return PAYMENT_TYPE_ALIASES[key] || raw;
};
const deriveDefaultPayeeTypeForCostLine = costLineType => {
  const normalizedType = normalizePaymentTypeCode(costLineType);
  if (!normalizedType) return "";
  return PAYMENT_TYPE_DEFAULT_PAYEE_TYPE[normalizedType] || "";
};
const deriveDefaultPayeeNameForCostLine = (payeeType, intervention, participantLegalName) => {
  const normalizedType = String(payeeType || "").trim();
  if (!normalizedType) return "";
  if (normalizedType === PAYEE_TYPE_PARTICIPANT_CLIENT) {
    return participantLegalName || "";
  }
  if (PAYEE_TYPES_DEFAULT_FROM_INTERVENTION.has(normalizedType)) {
    return String(intervention?.institution || "").trim();
  }
  return "";
};
const applyCostLinePayeeDefaults = (draft, intervention, participantLegalName, options = {}) => {
  if (!draft || typeof draft !== "object") return draft;
  const { allowTypeAutofill = true } = options;
  const payee = draft.payee && typeof draft.payee === "object" ? draft.payee : {};
  let payeeType = String(payee.type || "").trim();
  if (!payeeType && allowTypeAutofill) {
    payeeType = deriveDefaultPayeeTypeForCostLine(draft.type);
  }
  const existingName = String(payee.name || "").trim();
  const defaultName = deriveDefaultPayeeNameForCostLine(payeeType, intervention, participantLegalName);
  const nextPayee = {
    type: payeeType,
    name: existingName,
    reference: String(payee.reference || "").trim(),
  };
  if (payeeType === PAYEE_TYPE_PARTICIPANT_CLIENT) {
    nextPayee.name = defaultName || existingName;
    nextPayee.reference = "";
  } else if (!nextPayee.name && defaultName) {
    nextPayee.name = defaultName;
  }
  return { ...draft, payee: nextPayee };
};

const normalizeRecurrenceMode = value => {
  if (typeof value !== "string") return RECURRENCE_MODE_NOT_ALLOWED;
  const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (normalized === RECURRENCE_MODE_REQUIRED) return RECURRENCE_MODE_REQUIRED;
  if (normalized === RECURRENCE_MODE_OPTIONAL) return RECURRENCE_MODE_OPTIONAL;
  if (normalized === RECURRENCE_MODE_NOT_ALLOWED || normalized === "disabled") {
    return RECURRENCE_MODE_NOT_ALLOWED;
  }
  return RECURRENCE_MODE_NOT_ALLOWED;
};

const normalizeSubmissionTiming = value => {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (normalized === SUBMISSION_TIMING_INTERVENTION_START) return SUBMISSION_TIMING_INTERVENTION_START;
  if (normalized === SUBMISSION_TIMING_INTERVENTION_END) return SUBMISSION_TIMING_INTERVENTION_END;
  if (normalized === SUBMISSION_TIMING_RECURRENCE_SCHEDULE) return SUBMISSION_TIMING_RECURRENCE_SCHEDULE;
  if (normalized === SUBMISSION_TIMING_MANUAL_TRIGGER) return SUBMISSION_TIMING_MANUAL_TRIGGER;
  return null;
};

const buildPaymentTypeMappingLookup = mapping => {
  const lookup = new Map();
  if (!mapping || !Array.isArray(mapping.interventions)) return lookup;
  mapping.interventions.forEach(entry => {
    const code = normalizeInterventionCodeValue(entry?.code);
    if (!code) return;
    const types = Array.isArray(entry.availablePaymentTypes)
      ? entry.availablePaymentTypes.filter(Boolean)
      : [];
    lookup.set(code, new Set(types));
  });
  return lookup;
};

const normalizePaymentTypeMappingPayload = payload => {
  if (!payload || typeof payload !== "object") {
    return { enabled: false, paymentTypes: [], payeeTypes: [], interventions: [] };
  }
  const paymentTypesRaw = Array.isArray(payload.paymentTypes)
    ? payload.paymentTypes
    : Array.isArray(payload.payment_types)
      ? payload.payment_types
      : [];
  const payeeTypesRaw = Array.isArray(payload.payeeTypes)
    ? payload.payeeTypes
    : Array.isArray(payload.payee_types)
      ? payload.payee_types
      : [];
  const paymentTypes = paymentTypesRaw
    .map(entry => {
      if (!entry || typeof entry !== "object") return null;
      const code = normalizePaymentTypeCode(
        entry.code || entry.value || entry.paymentType || entry.payment_type,
      );
      if (!code) return null;
      return {
        code,
        label:
          typeof entry.label === "string" && entry.label.trim()
            ? entry.label.trim()
            : typeof entry.name === "string" && entry.name.trim()
              ? entry.name.trim()
              : code,
        submissionTiming: normalizeSubmissionTiming(
          entry.submissionTiming || entry.submission_timing,
        ),
      };
    })
    .filter(Boolean);
  const payeeTypes = payeeTypesRaw
    .map(entry => {
      if (!entry || typeof entry !== "object") return null;
      const codeRaw = entry.code || entry.value || entry.payeeType || entry.payee_type;
      const code = typeof codeRaw === "string" ? codeRaw.trim() : "";
      if (!code) return null;
      return {
        code,
        label:
          typeof entry.label === "string" && entry.label.trim()
            ? entry.label.trim()
            : typeof entry.name === "string" && entry.name.trim()
              ? entry.name.trim()
              : code,
        description:
          typeof entry.description === "string" && entry.description.trim()
            ? entry.description.trim()
            : typeof entry.helpText === "string" && entry.helpText.trim()
              ? entry.helpText.trim()
              : null,
      };
    })
    .filter(Boolean);
  const interventionsRaw = Array.isArray(payload.interventions) ? payload.interventions : [];
  const interventions = interventionsRaw
    .map(entry => {
      if (!entry || typeof entry !== "object") return null;
      const code = normalizeInterventionCodeValue(
        entry.code || entry.interventionCode || entry.intervention_code,
      );
      if (!code) return null;
      const typesRaw =
        entry.availablePaymentTypes ||
        entry.available_payment_types ||
        entry.paymentTypes ||
        entry.payment_types ||
        [];
      const types = Array.isArray(typesRaw)
        ? Array.from(new Set(typesRaw.map(value => String(value || "").trim()).filter(Boolean)))
        : [];
      return {
        code,
        name: entry.name || entry.label || null,
        availablePaymentTypes: types,
      };
    })
    .filter(Boolean);
  return {
    enabled: payload.enabled !== false,
    paymentTypes,
    payeeTypes,
    interventions,
  };
};

const normalizeCostingDefaults = payload => {
  if (!payload || payload.enabled === false) return { enabled: false };
  const interventionsRaw = Array.isArray(payload.interventions) ? payload.interventions : [];
  const paymentTypesRaw = Array.isArray(payload.paymentTypes) ? payload.paymentTypes : [];
  const interventions = interventionsRaw
    .map(entry => {
      if (!entry || typeof entry !== "object") return null;
      const code = normalizeInterventionCodeValue(entry.code || entry.interventionCode || entry.intervention_code);
      if (!code) return null;
      const suggested = Array.isArray(entry.suggested || entry.suggestedItems || entry.suggested_items)
        ? entry.suggested || entry.suggestedItems || entry.suggested_items
        : [];
      const normalizedSuggested = suggested
        .map(item => {
          if (typeof item === "string") return { type: item };
          if (item && typeof item === "object") {
            return {
              type: normalizePaymentTypeCode(item.type || item.paymentType || item.payment_type) || "",
              notes: item.notes || "",
              recurrenceEnabled: typeof item.recurrenceEnabled === "boolean" ? item.recurrenceEnabled : undefined,
            };
          }
          return null;
        })
        .filter(item => item && item.type);
      return {
        code,
        suggested: normalizedSuggested,
      };
    })
    .filter(Boolean);
  const paymentTypes = paymentTypesRaw
    .map(entry => {
      if (!entry || typeof entry !== "object") return null;
      const code = normalizePaymentTypeCode(entry.code || entry.paymentType || entry.payment_type);
      if (!code) return null;
      const recurrence = entry.recurrence && typeof entry.recurrence === "object" ? entry.recurrence : {};
      return {
        code,
        recurrence: {
          mode: normalizeRecurrenceMode(
            recurrence.mode || recurrence.rule || entry.recurrenceMode || entry.recurrence_mode,
          ),
        },
      };
    })
    .filter(Boolean);
  return {
    enabled: payload.enabled !== false,
    strategy: payload.strategy || "allowed",
    interventions,
    paymentTypes,
  };
};

const buildEmptyCostLine = overrides => ({
  id: buildUuid(),
  type: "",
  amount: "",
  notes: "",
  payee: {
    type: "",
    name: "",
    reference: "",
  },
  recurrence: {
    enabled: false,
    startDate: "",
    endDate: "",
    occurrences: "",
    amountPerPeriod: "",
  },
  ...(overrides || {}),
});

const buildEmptyIntervention = overrides => ({
  id: buildUuid(),
  code: "",
  startDate: "",
  endDate: "",
  deliveryMode: "partner",
  institution: "",
  programName: "",
  itpDetails: "",
  wageSubsidyDetails: "",
  interventionNoc: "",
  interventionNocVersion: "",
  suggestionsSeeded: false,
  costLines: [],
  ...(overrides || {}),
});

const normalizeCostLine = raw => {
  if (!raw || typeof raw !== "object") return null;
  const recurrenceRaw = raw.recurrence && typeof raw.recurrence === "object" ? raw.recurrence : {};
  const payeeRaw = raw.payee && typeof raw.payee === "object" ? raw.payee : {};
  return {
    id: raw.id || buildUuid(),
    type: normalizePaymentTypeCode(raw.type || raw.paymentType || raw.payment_type) || "",
    amount:
      raw.amount === null || typeof raw.amount === "undefined"
        ? ""
        : String(raw.amount),
    notes: raw.notes || raw.description || "",
    payee: {
      type: String(payeeRaw.type || raw.payeeType || raw.payee_type || "").trim(),
      name: String(payeeRaw.name || raw.payeeName || raw.payee_name || "").trim(),
      reference: String(payeeRaw.reference || raw.payeeReference || raw.payee_reference || "").trim(),
    },
    recurrence: {
      enabled: Boolean(recurrenceRaw.enabled),
      startDate: recurrenceRaw.startDate || "",
      endDate: recurrenceRaw.endDate || "",
      occurrences:
        recurrenceRaw.occurrences === null || typeof recurrenceRaw.occurrences === "undefined"
          ? ""
          : String(recurrenceRaw.occurrences),
      amountPerPeriod:
        recurrenceRaw.amountPerPeriod === null || typeof recurrenceRaw.amountPerPeriod === "undefined"
          ? ""
          : String(recurrenceRaw.amountPerPeriod),
    },
  };
};

const hasApprovedFundingAmount = line => {
  if (!line || typeof line !== "object") return false;
  const totalAmount = parseCurrencyToNumber(line.amount);
  if (totalAmount > 0) return true;
  const recurrence = line.recurrence && typeof line.recurrence === "object" ? line.recurrence : {};
  const amountPerPeriod = parseCurrencyToNumber(recurrence.amountPerPeriod);
  return amountPerPeriod > 0;
};

export const interventionApprovalIncludesFundingPackage = interventions =>
  (Array.isArray(interventions) ? interventions : []).some(intervention => {
    const costLines = Array.isArray(intervention?.costLines) ? intervention.costLines : [];
    return costLines.some(hasApprovedFundingAmount);
  });

const normalizeProposedIntervention = raw => {
  if (!raw || typeof raw !== "object") return null;
  const costLines = Array.isArray(raw.costLines)
    ? raw.costLines.map(normalizeCostLine).filter(Boolean)
    : [];
  return {
    id: raw.id || buildUuid(),
    code: raw.code || "",
    startDate: raw.startDate || "",
    endDate: raw.endDate || "",
    deliveryMode: raw.deliveryMode === "in_house" ? "in_house" : "partner",
    institution: raw.institution || "",
    programName: raw.programName || "",
    itpDetails: raw.itpDetails || "",
    wageSubsidyDetails: raw.wageSubsidyDetails || "",
    interventionNoc: raw.interventionNoc || raw.interventionNocCode || raw.intervention_noc || "",
    interventionNocVersion: raw.interventionNocVersion || raw.interventionNocVersionCode || "",
    suggestionsSeeded: Boolean(raw.suggestionsSeeded),
    costLines,
  };
};

const normalizeProposedInterventions = raw => {
  const list = Array.isArray(raw) ? raw : [];
  const normalized = list.map(normalizeProposedIntervention).filter(Boolean);
  return normalized.length ? normalized : [];
};

const normalizeRevisionContext = raw => {
  if (!raw || typeof raw !== "object") return null;
  const sourceInterventionId = raw.sourceInterventionId || raw.source_intervention_id || null;
  if (!sourceInterventionId) return null;
  const sourceActionPlanId = raw.sourceActionPlanId || raw.source_action_plan_id || null;
  return {
    kind: raw.kind || "approved_intervention",
    sourceInterventionId: String(sourceInterventionId),
    sourceActionPlanId: sourceActionPlanId ? String(sourceActionPlanId) : "",
    sourceStatus: normalizeInterventionStatus(raw.sourceStatus || raw.source_status, null),
    sourceTitle: raw.sourceTitle || raw.source_title || "Intervention",
    openedAt: raw.openedAt || raw.opened_at || null,
  };
};

const isRecurrenceScheduleComplete = line => {
  const recurrence = line?.recurrence || {};
  if (!recurrence.enabled) return false;
  const startDate = formatDate(recurrence.startDate);
  if (!startDate) return false;
  const occurrencesValue =
    recurrence.occurrences === "" || recurrence.occurrences === null || typeof recurrence.occurrences === "undefined"
      ? null
      : Number(recurrence.occurrences);
  const occurrences = Number.isFinite(occurrencesValue) ? occurrencesValue : null;
  if (!occurrences || occurrences <= 0) return false;
  const endDate = formatDate(recurrence.endDate) || deriveEndDateFromOccurrences(startDate, occurrences);
  if (!endDate) return false;
  const startUtc = parseIsoDateToUtc(startDate);
  const endUtc = parseIsoDateToUtc(endDate);
  if (startUtc !== null && endUtc !== null && endUtc < startUtc) return false;
  return true;
};

const InterventionAssessmentWidget = ({ actions, metadata = {}, toggleHelpPanel, workspaceEntry, proposalRequest }) => {
  const history = useHistory();
  const currentUser = useCurrentUser();
  const approvalWorkspaceEntry =
    workspaceEntry?.mode === "approval" && workspaceEntry?.approvalType === "intervention"
      ? workspaceEntry
      : null;
  const approvalRequestedStep = approvalWorkspaceEntry?.step || null;
  const {
    caseId: workspaceCaseId,
    caseData,
    refresh,
    createIntervention,
    reviseIntervention,
    deleteIntervention,
    updateIntervention: updateInterventionRecord,
    interventionCodes,
    interventionCodesLoading,
    loadInterventionCodes,
    nocVersions,
    nocVersionsLoading,
    loadNocVersions,
    selectedActionPlanId,
    setSelectedActionPlanId,
    selectedInterventionId,
    setSelectedInterventionId,
    getInterventionWizardStep,
    setInterventionWizardStep,
    getInterventionWizardDraft,
    setInterventionWizardDraft,
    clearInterventionWizardStep,
    clearInterventionWizardDraft,
  } = useCaseWorkspace();

  const formScope = interventionAssessmentScope({
    caseId: workspaceCaseId ?? caseData?.id ?? caseData?.case_id,
    applicationId: resolveInterventionWorkApplicationId(caseData?.actionPlans, {
      interventionId: selectedInterventionId,
      planId: selectedActionPlanId,
      applicationId: caseData?.applicationId ?? caseData?.application_id ?? null,
    }),
    planId: selectedActionPlanId,
    interventionId: selectedInterventionId,
  });
  const { form, setForm, hydrateForm, ready: formScopeReady, isCurrentScope } =
    useScopedInterventionForm(formScope, defaultFormState);
  const [newProposalScope, setNewProposalScope] = useState(null);
  const appliedProposalRequestRef = useRef(null);
  const [openingRevision, setOpeningRevision] = useState(false);
  const [showCancelRevision, setShowCancelRevision] = useState(false);
  const [cancellingRevision, setCancellingRevision] = useState(false);
  const [cancelRevisionError, setCancelRevisionError] = useState(null);
  const cancellingRevisionRef = useRef(false);
  const cancelledRevisionRef = useRef(false);
  const discardingDraftRef = useRef(false);
  const generatedSupplementaryLettersRef = useRef('[[],[],[]]');
  const [letterApprovalVersion, setLetterApprovalVersion] = useState(null);
  const [additionalLetterSelections, setAdditionalLetterSelections] = useState({ primaryId: null, ids: [] });
  const [preparedApprovalCoverage, setPreparedApprovalCoverage] = useState([]);
  const [approvalAttribution, setApprovalAttribution] = useState(null);
  const [showRevisionNavigationConfirm, setShowRevisionNavigationConfirm] = useState(false);
  const revisionStartInFlightRef = useRef(false);
  const [currentStep, setCurrentStep] = useState(BASE_STEP_IDS[0]);
  const [attemptedSteps, setAttemptedSteps] = useState({});
  const [completionNote, setCompletionNote] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRecallConfirmModal, setShowRecallConfirmModal] = useState(false);
  const [isRecallingSubmission, setIsRecallingSubmission] = useState(false);
  const [reviewWorkflowNote, setReviewWorkflowNote] = useState("");
  const [reviewWorkflowActionLoading, setReviewWorkflowActionLoading] = useState(null);
  const [dismissedReviewNoticeKey, setDismissedReviewNoticeKey] = useState(null);
  const [selectedDraftId, setSelectedDraftId] = useState(null);
  const [hydratedDraftId, setHydratedDraftId] = useState(null);
  const [hydratedDraftUpdatedAt, setHydratedDraftUpdatedAt] = useState(null);
  const [currentInterventionStatus, setCurrentInterventionStatus] = useState(null);
  const [revisionContext, setRevisionContext] = useState(null);
  const [interventionModal, setInterventionModal] = useState({
    visible: false,
    mode: "view",
    interventionId: null,
    draft: null,
    original: null,
  });
  const [interventionModalErrors, setInterventionModalErrors] = useState({});
  const [interventionDeleteId, setInterventionDeleteId] = useState(null);
  const [otherFundingSourceModal, setOtherFundingSourceModal] = useState(() =>
    buildOtherFundingSourceModalState()
  );
  const [otherFundingSourceModalErrors, setOtherFundingSourceModalErrors] = useState({});
  const [proposedInterventionsTableVersion, setProposedInterventionsTableVersion] = useState(0);
  const [costLineModal, setCostLineModal] = useState({
    visible: false,
    mode: "view",
    interventionId: null,
    lineId: null,
    draft: null,
    original: null,
  });
  const [costLineModalErrors, setCostLineModalErrors] = useState({});
  const [costLineAmountFocused, setCostLineAmountFocused] = useState(false);
  const [costLineAmountPerPeriodFocused, setCostLineAmountPerPeriodFocused] = useState(false);
  const [inlineAmountEditingId, setInlineAmountEditingId] = useState(null);
  const [decisionBlockerVisible, setDecisionBlockerVisible] = useState(false);
  const [decisionBlockerReasons, setDecisionBlockerReasons] = useState([]);
  const [decisionBlockerTargetStep, setDecisionBlockerTargetStep] = useState(null);
  const [letterWorkflows, setLetterWorkflows] = useState({ approval: null });
  const [letterWorkflowsLoading, setLetterWorkflowsLoading] = useState(false);
  const [letterWorkflowsError, setLetterWorkflowsError] = useState(null);
  const [approvalLetterPackTabId, setApprovalLetterPackTabId] = useState("client");
  const [approvalLetterPackGenerated, setApprovalLetterPackGenerated] = useState(false);
  const [clientLetterBody, setClientLetterBody] = useState("");
  const [editableInstitutionApprovalLetters, setEditableInstitutionApprovalLetters] = useState([]);
  const [editableCoFunderApprovalLetters, setEditableCoFunderApprovalLetters] = useState([]);
  const [editableLoanProviderApprovalLetters, setEditableLoanProviderApprovalLetters] = useState([]);
  const [sendingLetter, setSendingLetter] = useState(false);
  const [sendingLetterError, setSendingLetterError] = useState(null);
  const decisionLetterSendInFlightRef = useRef(false);
  const decisionLetterSendAttemptRef = useRef(null);
  const [showSendApprovalLetterConfirmModal, setShowSendApprovalLetterConfirmModal] = useState(false);
  const initialFormRef = useRef(defaultFormState);
  const wizardStepRestoreKeyRef = useRef(null);
  const wizardStepRestoreStepsRef = useRef(null);
  const approvalSelectionAppliedRef = useRef(null);
  const pendingApprovalStepRef = useRef(null);

  useEffect(() => {
    setInterventionModal({ visible: false, mode: "view", interventionId: null, draft: null, original: null });
    setCostLineModal({ visible: false, mode: "view", interventionId: null, lineId: null, draft: null, original: null });
    setOtherFundingSourceModal(buildOtherFundingSourceModalState());
    setInterventionDeleteId(null);
    setInterventionModalErrors({});
    setCostLineModalErrors({});
    setOtherFundingSourceModalErrors({});
    setIsSubmitting(false);
    setIsRecallingSubmission(false);
    setReviewWorkflowActionLoading(null);
    setReviewWorkflowNote("");
    setShowRecallConfirmModal(false);
    setCompletionNote(null);
    setRevisionContext(null);
    setCurrentInterventionStatus(null);
    setDecisionRevisionEditing(false);
    setApprovalLetterPackGenerated(false);
    setClientLetterBody("");
    setError(null);
    setSuccessMessage("");
  }, [formScope]);

  const caseId = useMemo(
    () => workspaceCaseId ?? caseData?.id ?? caseData?.case_id ?? null,
    [workspaceCaseId, caseData]
  );

  const participantLegalName = useMemo(() => {
    const caseContext = resolveCaseContext(caseData);
    const personal = caseContext.applicationPersonal && typeof caseContext.applicationPersonal === "object"
      ? caseContext.applicationPersonal
      : {};
    const answers = caseContext.applicationAnswers && typeof caseContext.applicationAnswers === "object"
      ? caseContext.applicationAnswers
      : {};
    const client = caseData?.client && typeof caseData.client === "object" ? caseData.client : {};
    const clientDetails = client.details && typeof client.details === "object" ? client.details : {};
    const normalizeNamePart = value => {
      if (value === null || typeof value === "undefined") return "";
      const trimmed = String(value).trim();
      return trimmed || "";
    };
    const buildFullName = (first, last) => {
      const firstName = normalizeNamePart(first);
      const lastName = normalizeNamePart(last);
      if (!firstName || !lastName) return "";
      return `${firstName} ${lastName}`;
    };
    const normalizeFullName = value => {
      const text = normalizeNamePart(value);
      if (!text) return "";
      return text.includes(" ") ? text : "";
    };
    const candidates = [
      normalizeFullName(caseData?.applicant_legal_name || caseData?.applicantLegalName),
      buildFullName(
        caseData?.submission_first_name || caseData?.submissionFirstName || caseData?.first_name || caseData?.firstName,
        caseData?.submission_last_name || caseData?.submissionLastName || caseData?.last_name || caseData?.lastName
      ),
      buildFullName(client.firstName, client.lastName),
      buildFullName(
        clientDetails.first_name || clientDetails.firstName || clientDetails.given_name || clientDetails.givenName,
        clientDetails.last_name || clientDetails.lastName || clientDetails.family_name || clientDetails.familyName
      ),
      buildFullName(
        caseContext.first_name || caseContext.firstName || caseContext.given_name || caseContext.givenName,
        caseContext.last_name || caseContext.lastName || caseContext.family_name || caseContext.familyName
      ),
      buildFullName(
        personal.first_name || personal.firstName || personal.given_name || personal.givenName,
        personal.last_name || personal.lastName || personal.family_name || personal.familyName
      ),
      buildFullName(
        answers["first-name"] || answers.first_name || answers["personal-first-name"] || answers.personal_first_name,
        answers["last-name"] || answers.last_name || answers["personal-last-name"] || answers.personal_last_name
      ),
      normalizeFullName(
        client.fullName || client.full_name || clientDetails.full_name || clientDetails.fullName || caseData?.applicant_name || caseData?.applicantName
      ),
    ];
    return candidates.find(Boolean) || "";
  }, [caseData]);
  const applicantSalutationName = useMemo(() => {
    const caseContext = resolveCaseContext(caseData);
    const personal = caseContext.applicationPersonal && typeof caseContext.applicationPersonal === "object"
      ? caseContext.applicationPersonal
      : {};
    const answers = caseContext.applicationAnswers && typeof caseContext.applicationAnswers === "object"
      ? caseContext.applicationAnswers
      : {};
    const normalize = value => {
      if (value === null || typeof value === "undefined") return "";
      return String(value).trim();
    };
    const firstToken = value => {
      const normalized = normalize(value);
      if (!normalized) return "";
      return normalized.split(/\s+/)[0] || "";
    };
    const preferredCandidates = [
      caseData?.preferred_name,
      caseData?.preferredName,
      caseContext.preferredName,
      caseContext.preferred_name,
      answers["preferred-name"],
      answers.preferred_name,
      personal.preferred_name,
      personal.preferredName,
    ];
    const preferred = preferredCandidates.map(normalize).find(Boolean);
    if (preferred) return preferred;
    const firstNameCandidates = [
      caseData?.submission_first_name,
      caseData?.submissionFirstName,
      caseData?.first_name,
      caseData?.firstName,
      caseContext.first_name,
      caseContext.firstName,
      personal.first_name,
      personal.firstName,
      answers["first-name"],
      answers.first_name,
      answers["personal-first-name"],
      answers.personal_first_name,
    ];
    const firstName = firstNameCandidates.map(normalize).find(Boolean);
    if (firstName) return firstName;
    return firstToken(participantLegalName);
  }, [caseData, participantLegalName]);
  const trackingReference = useMemo(() => {
    const candidates = [
      caseData?.agreementNumber,
      caseData?.caseNumber,
      caseData?.tracking_id,
      caseData?.trackingId,
      caseData?.submission_reference,
      caseData?.reference_number,
    ];
    return candidates.map(v => (typeof v === "string" ? v.trim() : v)).find(Boolean) || "";
  }, [caseData]);
  const currentUserName = useMemo(
    () => String(currentUser?.displayName || currentUser?.name || "").trim(),
    [currentUser]
  );
  const currentUserEmail = useMemo(
    () => String(currentUser?.email || "").trim(),
    [currentUser]
  );
  const applicationId = useMemo(
    () => resolveInterventionWorkApplicationId(caseData?.actionPlans, {
      interventionId: selectedInterventionId,
      planId: selectedActionPlanId,
      applicationId: caseData?.applicationId ?? caseData?.application_id ?? null,
    }),
    [caseData, selectedInterventionId, selectedActionPlanId]
  );
  const workingPlans = useMemo(
    () => getInterventionWorkingPlans(caseData?.actionPlans, applicationId, { includeClosed: true }),
    [caseData?.actionPlans, applicationId]
  );
  useEffect(() => {
    if (!participantLegalName) return;
    setCostLineModal(prev => {
      if (!prev?.visible || !prev?.draft) return prev;
      const payee = prev.draft.payee && typeof prev.draft.payee === "object" ? prev.draft.payee : {};
      if (String(payee.type || "").trim() !== PAYEE_TYPE_PARTICIPANT_CLIENT) return prev;
      if (String(payee.name || "").trim() === participantLegalName) return prev;
      return {
        ...prev,
        draft: {
          ...prev.draft,
          payee: {
            ...payee,
            name: participantLegalName,
          },
        },
      };
    });
  }, [participantLegalName]);

  const resolveStoredStep = useCallback(
    (key, stepIds = ALL_STEP_IDS) => {
      if (!key || typeof getInterventionWizardStep !== "function") return null;
      const stored = getInterventionWizardStep(key);
      if (!stored) return null;
      return stepIds.includes(stored) ? stored : null;
    },
    [getInterventionWizardStep]
  );

  const resolveStoredDraft = useCallback(
    key => {
      if (!key || typeof getInterventionWizardDraft !== "function") return null;
      const stored = getInterventionWizardDraft(key);
      return stored && typeof stored === "object" ? stored : null;
    },
    [getInterventionWizardDraft]
  );

  const hasMeaningfulDraft = useCallback(draft => {
    if (!draft || typeof draft !== "object") return false;
    if (Array.isArray(draft.proposedInterventions) && draft.proposedInterventions.length) return true;
    const textKeys = [
      "rationale",
      ...INTERVENTION_NARRATIVE_FIELDS.map(field => field.key),
      "otherFundingInvolved",
      "otherFundingNwacCoverage",
      "otherFundingNotes",
      "childcareNeed",
      "childcareFunding",
      "eiVerificationStatus",
      "decisionOutcome",
      "decisionNotes",
    ];
    if (textKeys.some(key => String(draft[key] || "").trim())) return true;
    if (Array.isArray(draft.otherFundingSources) && draft.otherFundingSources.length) return true;
    if (Array.isArray(draft.barriers) && draft.barriers.length) return true;
    return false;
  }, []);

  const mergeStoredDraft = useCallback((baseForm, storedDraft) => {
    if (!storedDraft) return baseForm;
    const merged = { ...baseForm, ...storedDraft };
    if (Array.isArray(storedDraft.proposedInterventions)) {
      merged.proposedInterventions = storedDraft.proposedInterventions;
    }
    const normalizedOtherFunding = normalizeOtherFundingDetails(
      {
        involved: merged.otherFundingInvolved,
        sources: merged.otherFundingSources,
        nwacCoverage: merged.otherFundingNwacCoverage,
        notes: merged.otherFundingNotes,
      },
      { keepEmptySources: true, preserveWhitespace: true }
    );
    merged.otherFundingInvolved = normalizedOtherFunding.involved;
    merged.otherFundingSources = normalizedOtherFunding.sources;
    merged.otherFundingNwacCoverage = normalizedOtherFunding.nwacCoverage;
    merged.otherFundingNotes = normalizedOtherFunding.notes;
    return merged;
  }, []);

  const completionScope = `${caseId || ''}:${applicationId || ''}:${selectedActionPlanId || ''}`;
  const explicitlyCreatingProposal = newProposalScope === completionScope;
  const pendingCompletions = useMemo(
    () => pendingInterventionCompletions(workingPlans, selectedActionPlanId),
    [workingPlans, selectedActionPlanId]
  );

  const activeInterventionId = useMemo(
    () => selectedInterventionId ?? selectedDraftId ?? hydratedDraftId ?? null,
    [selectedInterventionId, selectedDraftId, hydratedDraftId]
  );

  const activeInterventionIdValue = useMemo(
    () => (activeInterventionId ? String(activeInterventionId) : null),
    [activeInterventionId]
  );

  const activeInterventionRecord = useMemo(() => {
    if (!activeInterventionIdValue) return null;
    const plans = Array.isArray(caseData?.actionPlans) ? caseData.actionPlans : [];
    for (const plan of plans) {
      const interventions = Array.isArray(plan?.interventions) ? plan.interventions : [];
      const match = interventions.find(item => String(item?.id) === activeInterventionIdValue);
      if (match) return match;
    }
    return null;
  }, [activeInterventionIdValue, caseData]);

  const wizardStepKey = useMemo(() => {
    if (!caseId) return null;
    const keyId = selectedInterventionId ? String(selectedInterventionId) : null;
    if (keyId) return `${caseId}:${keyId}`;
    return `${caseId}:draft:${applicationId || ""}:${selectedActionPlanId || ""}`;
  }, [caseId, selectedInterventionId, applicationId, selectedActionPlanId]);

  const hasBlockingReviewStage = useMemo(() => {
    const plans = workingPlans;
    return plans.some(plan =>
      (plan.interventions || []).some(intervention => {
        if (revisionSourceId(intervention)) return false;
        const state = resolveInterventionStateFields(intervention);
        return state.reviewStatus === "submitted" || state.reviewStatus === "in_review";
      })
    );
  }, [workingPlans]);

  const hasBlockingDraft = useMemo(() => {
    const plans = workingPlans;
    return plans.some(plan =>
      (plan.interventions || []).some(intervention => {
        if (revisionSourceId(intervention)) return false;
        const state = resolveInterventionStateFields(intervention);
        return state.reviewStatus === "draft" || state.reviewStatus === "changes_requested";
      })
    );
  }, [workingPlans]);

  const hasBlockingProposal = hasBlockingReviewStage || hasBlockingDraft;

  const currentInterventionState = resolveInterventionStateFields(currentInterventionStatus);
  const statusValue = currentInterventionState.reviewStatus || currentInterventionState.effectiveStatus;
  const isDraftStatus = currentInterventionState.reviewStatus === "draft";
  const isSubmittedStatus = currentInterventionState.reviewStatus === "submitted";
  const isInReviewStatus = currentInterventionState.reviewStatus === "in_review";
  const isReviewStageStatus = isSubmittedStatus || isInReviewStatus;
  const isChangesRequestedStatus = currentInterventionState.reviewStatus === "changes_requested";
  const isRevisionMode = Boolean(revisionContext?.sourceInterventionId);
  const revisionSourceInterventionId = revisionContext?.sourceInterventionId || null;
  const revisionSourceActionPlanId = revisionContext?.sourceActionPlanId || "";
  const revisionSourceStatus = revisionContext?.sourceStatus || "approved";
  const revisionSourceTitle = revisionContext?.sourceTitle || "this approved intervention";
  const decisionSubjectLabel = isRevisionMode ? "intervention change" : "new intervention proposal";
  const decisionSubjectWithArticle = isRevisionMode ? "the proposed intervention change" : "the new intervention proposal";
  const decisionSubjectThis = isRevisionMode ? "this intervention change" : "this new intervention proposal";
  const decisionOptions = useMemo(
    () => [
      {
        value: "approved",
        label: isRevisionMode ? "Approve intervention change" : "Approve new intervention proposal",
      },
      {
        value: "changes_requested",
        label: isRevisionMode
          ? "Request changes to intervention change"
          : "Request changes to new intervention proposal",
      },
      {
        value: "rejected",
        label: isRevisionMode ? "Deny intervention change" : "Deny new intervention proposal",
      },
    ],
    [isRevisionMode]
  );
  const decisionOutcomeKey = String(form.decisionOutcome || "").trim().toLowerCase();
  const isApprovedDecisionOutcome = decisionOutcomeKey === "approved";
  const showDecisionLettersPanel = Boolean(
    completionNote &&
      (completionNote.showDecisionLetters !== false || completionNote.decisionLetterSent) &&
      isApprovedDecisionOutcome
  );
  const decisionLetterAlreadySent = Boolean(completionNote?.decisionLetterSent);
  const [decisionRevisionEditing, setDecisionRevisionEditing] = useState(false);
  const decisionRevisionBaseline = useRef(null);
  const role = currentUser?.role || null;
  const roleKey = normalizeRoleKey(role);
  const canDecideSubmittedProposal = SUBMITTED_PROPOSAL_DECIDER_ROLE_KEYS.has(roleKey);
  const reviewWorkflow =
    activeInterventionRecord?.reviewWorkflow ||
    activeInterventionRecord?.review_workflow ||
    null;
  const reviewStage = reviewWorkflow?.currentStage || reviewWorkflow?.current_stage || null;
  const reviewAttribution = useReviewWorkflowAttribution(reviewWorkflow);
  const twoStepReviewEnabled = Boolean(
    activeInterventionRecord?.twoStepReviewEnabled ||
    activeInterventionRecord?.two_step_review_enabled ||
    reviewWorkflow
  );
  const hasReviewWorkflow = Boolean(reviewWorkflow?.id || reviewWorkflow?.subjectKey || reviewWorkflow?.subject_key);
  const isReviewWithRegionalManager =
    twoStepReviewEnabled &&
    (
      reviewStage === INTERVENTION_REVIEW_STAGES.rmReview ||
      reviewStage === INTERVENTION_REVIEW_STAGES.returnedToRm
    );
  const isReviewWithNwac =
    twoStepReviewEnabled &&
    reviewStage === INTERVENTION_REVIEW_STAGES.nwacReview;
  const canRegionalManagerReview = canPerformRegionalReview(roleKey) && isReviewWithRegionalManager;
  const rmReviewNote = String(reviewWorkflow?.rmReviewNote || reviewWorkflow?.rm_review_note || "").trim();
  const nwacDecisionNote = String(reviewWorkflow?.nwacDecisionNote || reviewWorkflow?.nwac_decision_note || "").trim();

  const activeInterventionIsWorking = !activeInterventionIdValue || workingPlans.some(plan =>
    plan.interventions.some(item => String(item.id) === activeInterventionIdValue)
  );
  const canReviseAssessment = activeInterventionIsWorking && canReviseAtDecisionStage({ role, workflow: reviewWorkflow });
  const isDecisionRevisionEditing = canReviseAssessment && decisionRevisionEditing;
  const isProposalBodyEditable = formScopeReady && (isDecisionRevisionEditing || (activeInterventionIsWorking && canEditInterventionAssessmentBody({
    role,
    reviewStatus: statusValue,
    reviewWorkflow,
    assignedStaffProfileId: caseData?.owner?.id,
    createdByStaffProfileId:
      activeInterventionRecord?.createdByStaffProfileId ||
      activeInterventionRecord?.created_by_staff_profile_id ||
      null,
    currentStaffProfileId:
      currentUser?.staffProfileId ||
      currentUser?.staff_profile_id ||
      null,
    hasExistingIntervention: Boolean(activeInterventionRecord?.id),
    hasBlockingProposal,
  })));
  const isDecisionEditable =
    formScopeReady && !isDecisionRevisionEditing && activeInterventionIsWorking &&
    isReviewStageStatus &&
    canDecideSubmittedProposal &&
    (!twoStepReviewEnabled || isReviewWithNwac);
  const isEditable = isProposalBodyEditable || isDecisionEditable;
  const isFormLocked = !isProposalBodyEditable || isSubmitting;
  const narrativeLoading = useInterventionAssessmentNarratives({
    caseId, interventionId: activeInterventionIdValue,
    active: formScopeReady && !completionNote && Boolean(explicitlyCreatingProposal || (hydratedDraftId && String(hydratedDraftId) === activeInterventionIdValue)),
    editable: isProposalBodyEditable, form, setForm, initialFormRef,
  });
  const isDecisionReadOnly = !isDecisionEditable || isSubmitting;
  const canRecallSubmittedProposal =
    isReviewStageStatus &&
    !completionNote &&
    canRecallInterventionAssessmentSubmission({
      role,
      reviewWorkflow: hasReviewWorkflow ? reviewWorkflow : null,
      createdByStaffProfileId:
        activeInterventionRecord?.createdByStaffProfileId ||
        activeInterventionRecord?.created_by_staff_profile_id ||
        null,
      currentStaffProfileId:
        currentUser?.staffProfileId ||
        currentUser?.staff_profile_id ||
        null,
    }) &&
    Boolean(activeInterventionIdValue);
  const activeStepIds = useMemo(() => {
    if (!isReviewStageStatus || !canDecideSubmittedProposal || (twoStepReviewEnabled && !isReviewWithNwac)) return BASE_STEP_IDS;
    return [...BASE_STEP_IDS, ...SUBMITTED_STEP_IDS];
  }, [canDecideSubmittedProposal, isReviewStageStatus, isReviewWithNwac, twoStepReviewEnabled]);

  const codeOptions = useMemo(() => {
    if (!Array.isArray(interventionCodes) || interventionCodes.length === 0) return [];
    return interventionCodes.map(item => ({
      value: String(item.code),
      label: `${item.code} — ${item.label}`,
      codeLabel: item.label,
    }));
  }, [interventionCodes]);

  const interventionCodeLookup = useMemo(() => {
    const map = new Map();
    codeOptions.forEach(option => {
      if (!option?.value) return;
      map.set(String(option.value), option);
    });
    return map;
  }, [codeOptions]);

  const resolveInterventionLabel = useCallback(
    code => {
      if (!code) return "";
      const normalized = String(code);
      const match = interventionCodeLookup.get(normalized);
      if (match?.label) return match.label.replace(/^\s*\d+\s*–\s*/, "");
      return normalized;
    },
    [interventionCodeLookup]
  );

  const selectablePlans = useMemo(() => workingPlans.filter(plan =>
    isActionPlanSelectableForApplication(plan, applicationId) ||
    plan.interventions.some(item => String(item.id) === activeInterventionIdValue)
  ), [workingPlans, applicationId, activeInterventionIdValue]);

  const planOptions = useMemo(
    () =>
      selectablePlans.map(plan => ({
        value: String(plan.id),
        label: plan.title || `Action Plan ${plan.id}`,
        description: plan.status ? `Status: ${plan.status}` : "",
      })),
    [selectablePlans]
  );
  const isPlanStepBlocked = currentStep === "plan" && planOptions.length === 0;

  const activePlanId = useMemo(() => {
    const active = selectablePlans.find(plan => String(plan.status || "").toLowerCase() === "active");
    return active?.id || null;
  }, [selectablePlans]);

  const selectedPlan = useMemo(() => {
    if (!selectablePlans.length) return null;
    const selectedId = form.actionPlanId || selectedActionPlanId;
    if (selectedId) {
      return selectablePlans.find(plan => String(plan.id) === String(selectedId)) || null;
    }
    return null;
  }, [selectablePlans, form.actionPlanId, selectedActionPlanId]);

  const selectedPlanFundingStream = useMemo(
    () => normalizeFundingStream(selectedPlan?.fundingStream || selectedPlan?.funding_stream),
    [selectedPlan]
  );
  const selectedPlanBudgetPot = useMemo(
    () => (selectedPlan?.budgetPot || selectedPlan?.budget_pot ? String(selectedPlan?.budgetPot || selectedPlan?.budget_pot) : ""),
    [selectedPlan]
  );
  const selectedPlanPostingContext = useMemo(
    () => String(selectedPlan?.postingContext || selectedPlan?.posting_context || "external").trim().toLowerCase() || "external",
    [selectedPlan]
  );
  useEffect(() => {
    if (form.actionPlanId || selectedInterventionId) return;
    const planId = selectedActionPlanId || activePlanId;
    if (!planId) return;
    setForm(prev => ({ ...prev, actionPlanId: String(planId) }));
    if (!selectedActionPlanId && typeof setSelectedActionPlanId === "function") setSelectedActionPlanId(planId);
  }, [activePlanId, form.actionPlanId, selectedActionPlanId, selectedInterventionId, setSelectedActionPlanId, setForm]);

  useEffect(() => {
    if (planOptions.length && error === "Create an action plan before proposing interventions.") {
      setError(null);
    }
  }, [planOptions.length, error]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = event => {
      if (event?.detail?.id !== "interventionAssessment") return;
      if (typeof clearInterventionWizardStep === "function") {
        clearInterventionWizardStep();
      }
      if (typeof clearInterventionWizardDraft === "function") {
        clearInterventionWizardDraft();
      }
    };
    window.addEventListener("iset-case-workspace:widget-removed", handler);
    return () => window.removeEventListener("iset-case-workspace:widget-removed", handler);
  }, [clearInterventionWizardStep, clearInterventionWizardDraft]);

  useEffect(() => {
    if (!wizardStepKey) return;
    const stepSignature = activeStepIds.join("|");
    const keyChanged = wizardStepRestoreKeyRef.current !== wizardStepKey;
    const stepsChanged = wizardStepRestoreStepsRef.current !== stepSignature;
    if (!keyChanged && !stepsChanged) return;
    wizardStepRestoreKeyRef.current = wizardStepKey;
    wizardStepRestoreStepsRef.current = stepSignature;
    const requestedStep = resolveRequestedWizardStep(pendingApprovalStepRef.current, activeStepIds);
    if (requestedStep) {
      if (requestedStep !== currentStep) {
        setCurrentStep(requestedStep);
      }
      return;
    }
    const storedStep = resolveStoredStep(wizardStepKey, activeStepIds);
    if (storedStep && activeStepIds.includes(storedStep) && storedStep !== currentStep) {
      setCurrentStep(storedStep);
      return;
    }
    if (stepsChanged && !activeStepIds.includes(currentStep)) {
      setCurrentStep(BASE_STEP_IDS[0]);
    }
  }, [wizardStepKey, activeStepIds, currentStep, resolveStoredStep]);

  useEffect(() => {
    const requestedStep = resolveRequestedWizardStep(pendingApprovalStepRef.current, activeStepIds);
    if (!wizardStepKey || !requestedStep) return;
    const requestedInterventionId = approvalWorkspaceEntry?.interventionId
      ? String(approvalWorkspaceEntry.interventionId)
      : null;
    const loadedInterventionIds = [
      selectedInterventionId,
      selectedDraftId,
      hydratedDraftId,
    ]
      .map(value => (value ? String(value) : null))
      .filter(Boolean);
    if (requestedInterventionId && !loadedInterventionIds.includes(requestedInterventionId)) {
      return;
    }
    if (currentStep !== requestedStep) {
      setCurrentStep(requestedStep);
      return;
    }
    pendingApprovalStepRef.current = null;
  }, [
    activeStepIds,
    approvalWorkspaceEntry,
    currentStep,
    hydratedDraftId,
    selectedDraftId,
    selectedInterventionId,
    wizardStepKey,
  ]);

  useEffect(() => {
    if (!formScopeReady || cancelledRevisionRef.current || discardingDraftRef.current || !wizardStepKey || typeof setInterventionWizardStep !== "function") return;
    setInterventionWizardStep(wizardStepKey, currentStep);
  }, [wizardStepKey, currentStep, setInterventionWizardStep, formScopeReady]);

  useEffect(() => {
    if (!formScopeReady || cancelledRevisionRef.current || discardingDraftRef.current || !wizardStepKey || typeof setInterventionWizardDraft !== "function") return;
    if (!hasMeaningfulDraft(form)) return;
    setInterventionWizardDraft(wizardStepKey, packInterventionAssessmentDraft(formScope, form, initialFormRef.current));
  }, [wizardStepKey, form, formScope, formScopeReady, setInterventionWizardDraft, hasMeaningfulDraft]);

  useEffect(() => {
    if (!wizardStepKey || selectedInterventionId || hasBlockingProposal || formScopeReady) return;
    const baseline = { ...defaultFormState, actionPlanId: String(selectedActionPlanId || "") };
    const storedDraft = restoreInterventionAssessmentDraft(resolveStoredDraft(wizardStepKey), formScope, baseline);
    hydrateForm(mergeStoredDraft(baseline, storedDraft));
    initialFormRef.current = baseline;
    setSelectedDraftId(null);
    setHydratedDraftId(null);
    setHydratedDraftUpdatedAt(null);
    setCurrentInterventionStatus(null);
    setRevisionContext(null);
    setCompletionNote(null);
    setDecisionRevisionEditing(false);
  }, [wizardStepKey, selectedInterventionId, selectedActionPlanId, hasBlockingProposal, formScopeReady,
    formScope, resolveStoredDraft, hydrateForm, mergeStoredDraft]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const updateOtherFundingFields = useCallback(
    updater => {
      setForm(prev => {
        const next = typeof updater === "function" ? updater(prev) : { ...prev, ...(updater || {}) };
        const normalizedOtherFunding = normalizeOtherFundingDetails(
          {
            involved: next.otherFundingInvolved,
            sources: next.otherFundingSources,
            nwacCoverage: next.otherFundingNwacCoverage,
            notes: next.otherFundingNotes,
          },
          { keepEmptySources: true, preserveWhitespace: true }
        );
        return {
          ...next,
          otherFundingInvolved: normalizedOtherFunding.involved,
          otherFundingSources: normalizedOtherFunding.sources,
          otherFundingNwacCoverage: normalizedOtherFunding.nwacCoverage,
          otherFundingNotes: normalizedOtherFunding.notes,
        };
      });
    },
    [setForm]
  );

  const addOtherFundingSource = useCallback(
    sourceDraft => {
      const nextSource = buildEmptyOtherFundingSource(sourceDraft || {});
      updateOtherFundingFields(prev => ({
        ...prev,
        otherFundingSources: [
          ...(Array.isArray(prev.otherFundingSources) ? prev.otherFundingSources : []),
          nextSource,
        ],
      }));
    },
    [updateOtherFundingFields]
  );

  const updateOtherFundingSource = useCallback(
    (sourceId, updates) => {
      updateOtherFundingFields(prev => ({
        ...prev,
        otherFundingSources: (Array.isArray(prev.otherFundingSources) ? prev.otherFundingSources : []).map(source =>
          idsMatch(source.id, sourceId)
            ? buildEmptyOtherFundingSource({ ...source, ...(updates || {}) })
            : source
        ),
      }));
    },
    [updateOtherFundingFields]
  );

  const removeOtherFundingSource = useCallback(
    sourceId => {
      updateOtherFundingFields(prev => ({
        ...prev,
        otherFundingSources: (Array.isArray(prev.otherFundingSources) ? prev.otherFundingSources : []).filter(
          source => !idsMatch(source.id, sourceId)
        ),
      }));
    },
    [updateOtherFundingFields]
  );

  const resetOtherFundingSourceModal = useCallback(() => {
    setOtherFundingSourceModal(buildOtherFundingSourceModalState());
    setOtherFundingSourceModalErrors({});
  }, []);

  const openAddOtherFundingSourceModal = useCallback(() => {
    setOtherFundingSourceModal(
      buildOtherFundingSourceModalState({
        visible: true,
        mode: "add",
        sourceId: null,
        draft: buildEmptyOtherFundingSource(),
        original: null,
      })
    );
    setOtherFundingSourceModalErrors({});
  }, []);

  const openEditOtherFundingSourceModal = useCallback(
    sourceId => {
      const current = Array.isArray(form.otherFundingSources) ? form.otherFundingSources : [];
      const source = current.find(item => idsMatch(item?.id, sourceId));
      if (!source) return;
      const normalized = buildEmptyOtherFundingSource(source);
      setOtherFundingSourceModal(
        buildOtherFundingSourceModalState({
          visible: true,
          mode: "edit",
          sourceId: normalized.id,
          draft: normalized,
          original: normalized,
        })
      );
      setOtherFundingSourceModalErrors({});
    },
    [form.otherFundingSources]
  );

  const updateOtherFundingSourceModalDraft = useCallback(updates => {
    setOtherFundingSourceModal(prev => {
      if (!prev?.draft) return prev;
      return {
        ...prev,
        draft: buildEmptyOtherFundingSource({
          ...prev.draft,
          ...(updates || {}),
        }),
      };
    });
  }, []);

  const saveOtherFundingSourceModal = useCallback(() => {
    const draft = buildEmptyOtherFundingSource(otherFundingSourceModal.draft || {});
    const errors = validateOtherFundingSourceDraft(draft);
    if (Object.keys(errors).length > 0) {
      setOtherFundingSourceModalErrors(errors);
      return;
    }
    if (otherFundingSourceModal.mode === "edit" && otherFundingSourceModal.sourceId) {
      updateOtherFundingSource(otherFundingSourceModal.sourceId, draft);
      resetOtherFundingSourceModal();
      return;
    }
    addOtherFundingSource(draft);
    resetOtherFundingSourceModal();
  }, [
    addOtherFundingSource,
    otherFundingSourceModal.draft,
    otherFundingSourceModal.mode,
    otherFundingSourceModal.sourceId,
    resetOtherFundingSourceModal,
    updateOtherFundingSource,
  ]);

  useEffect(() => {
    if (!interventionCodesLoading && (!interventionCodes || interventionCodes.length === 0)) {
      loadInterventionCodes().catch(() => {});
    }
  }, [interventionCodes, interventionCodesLoading, loadInterventionCodes]);

  useEffect(() => {
    if (!nocVersionsLoading && (!nocVersions || nocVersions.length === 0)) {
      loadNocVersions().catch(() => {});
    }
  }, [nocVersions, nocVersionsLoading, loadNocVersions]);

  const nocVersionOptions = useMemo(() => {
    if (!Array.isArray(nocVersions)) return [];
    return nocVersions
      .map(item => ({
        value: item.value || item.code || "",
        label: item.label || item.code || "",
        description: item.description || "",
      }))
      .filter(item => item.value && item.label);
  }, [nocVersions]);

  const [nocSuggestions, setNocSuggestions] = useState([]);
  const [nocSuggestionsLoading, setNocSuggestionsLoading] = useState(false);

  const fetchNocSuggestions = useCallback(
    async (queryText, version) => {
      if (!version) {
        setNocSuggestions([]);
        return;
      }
      const query = typeof queryText === "string" ? queryText.trim() : "";
      if (query.length < 2) {
        setNocSuggestions([]);
        return;
      }
      setNocSuggestionsLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("limit", "25");
        params.set("q", query);
        params.set("version", version);
        const response = await apiFetch(`/api/reference/noc-codes?${params.toString()}`, { method: "GET" });
        if (!response.ok) throw new Error(`Failed to load NOC codes (${response.status})`);
        const data = await response.json();
        const options = Array.isArray(data?.codes)
          ? data.codes
              .map(item => ({
                value: item?.code ? String(item.code).trim() : null,
                label: item?.title ? `${item.code} - ${item.title}` : String(item.code || ""),
                description: item?.title || null,
              }))
              .filter(option => option.value && option.label)
          : [];
        setNocSuggestions(options);
      } catch (_) {
        setNocSuggestions([]);
      } finally {
        setNocSuggestionsLoading(false);
      }
    },
    []
  );

  const [paymentTypeMapping, setPaymentTypeMapping] = useState(null);
  const [paymentTypeMappingLoading, setPaymentTypeMappingLoading] = useState(false);
  const [costingDefaults, setCostingDefaults] = useState(null);
  const [costingDefaultsLoading, setCostingDefaultsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadMapping = async () => {
      setPaymentTypeMappingLoading(true);
      try {
        const response = await apiFetch("/api/finance/payment-intervention-type-map", { method: "GET" });
        if (!response.ok) throw new Error(`Failed to load payment mapping (${response.status})`);
        const payload = await response.json().catch(() => null);
        if (!cancelled) setPaymentTypeMapping(normalizePaymentTypeMappingPayload(payload));
      } catch (_) {
        if (!cancelled) setPaymentTypeMapping(null);
      } finally {
        if (!cancelled) setPaymentTypeMappingLoading(false);
      }
    };
    loadMapping();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadDefaults = async () => {
      setCostingDefaultsLoading(true);
      try {
        const response = await apiFetch("/api/config/runtime/assessment-costing", { method: "GET" });
        if (!response.ok) throw new Error(`Failed to load costing defaults (${response.status})`);
        const payload = await response.json().catch(() => null);
        if (!cancelled) setCostingDefaults(normalizeCostingDefaults(payload));
      } catch (_) {
        if (!cancelled) setCostingDefaults(null);
      } finally {
        if (!cancelled) setCostingDefaultsLoading(false);
      }
    };
    loadDefaults();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadWorkflows = async () => {
      setLetterWorkflowsLoading(true);
      setLetterWorkflowsError(null);
      try {
        const resp = await apiFetch("/api/workflows");
        if (!resp.ok) throw new Error(`Failed to load letter workflows (${resp.status})`);
        const rows = await resp.json().catch(() => []);
        const list = Array.isArray(rows) ? rows : Array.isArray(rows?.data) ? rows.data : [];
        const approval = selectLatestSupportedSigningWorkflow(
          list,
          "assessment_approval_letter"
        );
        if (!cancelled) {
          setLetterWorkflows({
            approval: approval?.id ? Number(approval.id) : null,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setLetterWorkflows({ approval: null });
          setLetterWorkflowsError(err?.message || "Failed to load letter workflows.");
        }
      } finally {
        if (!cancelled) setLetterWorkflowsLoading(false);
      }
    };
    loadWorkflows();
    return () => {
      cancelled = true;
    };
  }, []);

  const paymentTypeMappingLookup = useMemo(
    () => buildPaymentTypeMappingLookup(paymentTypeMapping),
    [paymentTypeMapping]
  );

  const configuredPaymentTypeOptions = useMemo(() => {
    const list = Array.isArray(paymentTypeMapping?.paymentTypes) ? paymentTypeMapping.paymentTypes : [];
    return list
      .map(entry => {
        const code = normalizePaymentTypeCode(entry?.code);
        if (!code) return null;
        return {
          value: code,
          label: entry?.label || code,
        };
      })
      .filter(Boolean);
  }, [paymentTypeMapping]);
  const configuredPayeeTypeOptions = useMemo(() => {
    const list = Array.isArray(paymentTypeMapping?.payeeTypes) ? paymentTypeMapping.payeeTypes : [];
    return list
      .map(entry => {
        const code = typeof entry?.code === "string" ? entry.code.trim() : "";
        if (!code) return null;
        return {
          value: code,
          label: entry?.label || code,
          description: entry?.description || undefined,
        };
      })
      .filter(Boolean);
  }, [paymentTypeMapping]);

  const paymentTypeLabelLookup = useMemo(() => {
    const map = new Map();
    configuredPaymentTypeOptions.forEach(option => {
      if (!option?.value) return;
      map.set(String(option.value), option.label || option.value);
    });
    return map;
  }, [configuredPaymentTypeOptions]);

  const effectiveCostingDefaults = useMemo(() => {
    if (costingDefaults && costingDefaults.enabled !== false) return costingDefaults;
    return { enabled: false, strategy: "allowed", interventions: [], paymentTypes: [] };
  }, [costingDefaults]);

  const recurrenceModeByType = useMemo(() => {
    const map = new Map();
    if (effectiveCostingDefaults && Array.isArray(effectiveCostingDefaults.paymentTypes)) {
      effectiveCostingDefaults.paymentTypes.forEach(entry => {
        const code = normalizePaymentTypeCode(entry?.code);
        if (!code) return;
        const mode = normalizeRecurrenceMode(entry?.recurrence?.mode);
        map.set(code, mode);
      });
    }
    return map;
  }, [effectiveCostingDefaults]);

  const getRecurrenceModeForType = useCallback(
    type => {
      if (!type) return RECURRENCE_MODE_NOT_ALLOWED;
      const normalized = normalizePaymentTypeCode(type);
      return recurrenceModeByType.get(normalized) || RECURRENCE_MODE_NOT_ALLOWED;
    },
    [recurrenceModeByType]
  );
  const submissionTimingByType = useMemo(() => {
    const map = new Map();
    const paymentTypes = Array.isArray(paymentTypeMapping?.paymentTypes)
      ? paymentTypeMapping.paymentTypes
      : [];
    paymentTypes.forEach(entry => {
      const code = normalizePaymentTypeCode(entry?.code || entry?.paymentType || entry?.payment_type);
      if (!code) return;
      const timing =
        normalizeSubmissionTiming(entry?.submissionTiming || entry?.submission_timing) ||
        DEFAULT_SUBMISSION_TIMING_BY_TYPE[code] ||
        SUBMISSION_TIMING_MANUAL_TRIGGER;
      map.set(code, timing);
    });
    Object.entries(DEFAULT_SUBMISSION_TIMING_BY_TYPE).forEach(([code, timing]) => {
      if (!map.has(code)) map.set(code, timing);
    });
    return map;
  }, [paymentTypeMapping]);
  const getSubmissionTimingForType = useCallback(
    type => {
      const code = normalizePaymentTypeCode(type);
      if (!code) return SUBMISSION_TIMING_MANUAL_TRIGGER;
      return (
        submissionTimingByType.get(code) ||
        DEFAULT_SUBMISSION_TIMING_BY_TYPE[code] ||
        SUBMISSION_TIMING_MANUAL_TRIGGER
      );
    },
    [submissionTimingByType]
  );

  const getAllowedPaymentTypesForIntervention = useCallback(
    code => {
      const normalized = normalizeInterventionCodeValue(code);
      if (!normalized) return [];
      const allowed = paymentTypeMappingLookup.get(normalized);
      if (!allowed) return [];
      return Array.from(allowed);
    },
    [paymentTypeMappingLookup]
  );
  const getCostLineDetailsText = useCallback(
    (line, intervention = null) => {
      const recurrenceMode = getRecurrenceModeForType(line?.type);
      const recurrence = line?.recurrence || {};
      const recurrenceEnabled =
        recurrenceMode === RECURRENCE_MODE_REQUIRED || Boolean(recurrence.enabled);
      let occurrences = null;
      if (recurrenceEnabled) {
        const occurrencesRaw =
          recurrence.occurrences === "" ||
          recurrence.occurrences === null ||
          typeof recurrence.occurrences === "undefined"
            ? null
            : Number(recurrence.occurrences);
        occurrences = Number.isFinite(occurrencesRaw) && occurrencesRaw > 0 ? occurrencesRaw : null;
        if (!occurrences) {
          const startDate = formatDate(recurrence.startDate);
          const endDate = formatDate(recurrence.endDate);
          if (startDate && endDate) {
            const computed = autoOccurrencesFromDates(startDate, endDate, "monthly");
            if (computed) occurrences = computed;
          }
        }
      }
      const amountPerPeriod = parseCurrencyInput(recurrence.amountPerPeriod);
      const perPeriodText =
        recurrenceEnabled && amountPerPeriod !== null
          ? `${formatCurrencyDisplay(amountPerPeriod)} per month`
          : "";
      const submissionTiming = getSubmissionTimingForType(line?.type);
      const interventionStart = formatDate(intervention?.startDate);
      const interventionEnd = formatDate(intervention?.endDate);
      const recurrenceStart = formatDate(recurrence.startDate);
      const recurrenceEnd = formatDate(recurrence.endDate);
      const explicitPayableDate = formatDate(
        line?.payableDate ||
          line?.payable_date ||
          line?.paymentDate ||
          line?.payment_date ||
          line?.dateDue ||
          line?.date_due
      );
      const firstInstallmentDate =
        recurrenceStart ||
        explicitPayableDate ||
        (submissionTiming === SUBMISSION_TIMING_INTERVENTION_END
          ? interventionEnd || recurrenceEnd
          : interventionStart || recurrenceEnd);
      const firstInstallmentDateLabel = firstInstallmentDate
        ? formatShortDate(firstInstallmentDate)
        : "";
      let payableText = "payable";
      if (recurrenceEnabled) {
        if (occurrences && occurrences > 0) {
          payableText = `payable in ${occurrences} monthly installment${
            occurrences === 1 ? "" : "s"
          }`;
        } else {
          payableText = "payable in monthly installments";
        }
        if (firstInstallmentDateLabel) {
          payableText += ` starting ${firstInstallmentDateLabel}`;
        }
      } else {
        let payableDate = "";
        if (submissionTiming === SUBMISSION_TIMING_INTERVENTION_START) {
          payableDate = formatShortDate(interventionStart || recurrenceStart || explicitPayableDate);
        } else if (submissionTiming === SUBMISSION_TIMING_INTERVENTION_END) {
          payableDate = formatShortDate(interventionEnd || recurrenceEnd || explicitPayableDate);
        } else if (submissionTiming === SUBMISSION_TIMING_RECURRENCE_SCHEDULE) {
          payableDate = formatShortDate(
            recurrenceStart || explicitPayableDate || interventionStart || interventionEnd
          );
        } else if (submissionTiming === SUBMISSION_TIMING_MANUAL_TRIGGER) {
          payableDate = formatShortDate(explicitPayableDate);
        }
        payableText = payableDate ? `payable on ${payableDate}` : "payable";
      }
      const payeeName = String(line?.payee?.name || "").trim();
      const explicitPayeeTypeKey = normalizePayeeTypeKey(line?.payee?.type);
      const inferredPayeeTypeKey = normalizePayeeTypeKey(deriveDefaultPayeeTypeForCostLine(line?.type));
      const payeeTypeKey = explicitPayeeTypeKey || inferredPayeeTypeKey;
      const payeeTarget = PAYEE_TYPE_DETAIL_TARGET_BY_KEY[payeeTypeKey] || "";
      const payeeText = payeeName ? `to ${payeeName}` : payeeTarget ? `to ${payeeTarget}` : "";
      const notesText = String(line?.notes || "").trim();
      let text = payableText || "—";
      if (perPeriodText) {
        text = `${text} (${perPeriodText})`;
      }
      if (payeeText) {
        text = `${text} ${payeeText}`;
      }
      return {
        text,
        notesText
      };
    },
    [getRecurrenceModeForType, getSubmissionTimingForType]
  );

  const buildCostItemOptions = useCallback(
    intervention => {
      const allowed = new Set(getAllowedPaymentTypesForIntervention(intervention?.code));
      return configuredPaymentTypeOptions.filter(option => {
        if (!option?.value) return false;
        if (allowed.size && !allowed.has(option.value)) return false;
        return true;
      });
    },
    [configuredPaymentTypeOptions, getAllowedPaymentTypesForIntervention]
  );

  const buildSuggestedCostLines = useCallback(
    intervention => {
      if (!effectiveCostingDefaults.enabled) return [];
      const code = normalizeInterventionCodeValue(intervention?.code);
      if (!code) return [];
      const allowed = new Set(getAllowedPaymentTypesForIntervention(code));
      const defaultsEntry = Array.isArray(effectiveCostingDefaults.interventions)
        ? effectiveCostingDefaults.interventions.find(entry => entry.code === code)
        : null;
      const hasExplicitDefaults = Boolean(defaultsEntry);
      let suggested = defaultsEntry?.suggested || [];
      if (!suggested.length && effectiveCostingDefaults.strategy === "allowed" && !hasExplicitDefaults) {
        if (!allowed.size) return null;
        suggested = Array.from(allowed).map(type => ({ type }));
      }
      if (!Array.isArray(suggested) || !suggested.length) return [];
      const seen = new Set();
      return suggested
        .map(item => {
          const type = item?.type ? String(item.type).trim() : "";
          if (!type) return null;
          if (allowed.size && !allowed.has(type)) return null;
          if (seen.has(type)) return null;
          seen.add(type);
          const recurrenceMode = getRecurrenceModeForType(type);
          const recurrenceEnabled =
            typeof item?.recurrenceEnabled === "boolean"
              ? item.recurrenceEnabled
              : recurrenceMode === RECURRENCE_MODE_REQUIRED;
          return buildEmptyCostLine({
            type,
            notes: item?.notes || "",
            recurrence: {
              enabled: recurrenceEnabled,
              startDate: intervention?.startDate || "",
              endDate: intervention?.endDate || "",
              occurrences: "",
              amountPerPeriod: "",
            },
          });
        })
        .filter(Boolean);
    },
    [effectiveCostingDefaults, getAllowedPaymentTypesForIntervention, getRecurrenceModeForType]
  );

  const proposedInterventions = useMemo(
    () => (Array.isArray(form.proposedInterventions) ? form.proposedInterventions : []),
    [form.proposedInterventions]
  );
  const isFramingStepBlocked = currentStep === "framing" && proposedInterventions.length === 0;

  useEffect(() => {
    if (proposedInterventions.length && error === "Add at least one proposed intervention before continuing.") {
      setError(null);
    }
  }, [proposedInterventions.length, error]);

  const primaryIntervention = useMemo(() => {
    if (!proposedInterventions.length) return null;
    return (
      proposedInterventions.find(item => item?.code && item?.startDate) ||
      proposedInterventions[0] ||
      null
    );
  }, [proposedInterventions]);

  const updateProposedInterventions = useCallback(
    updater => {
      setForm(prev => {
        const current = Array.isArray(prev.proposedInterventions) ? prev.proposedInterventions : [];
        const next = typeof updater === "function" ? updater(current) : updater;
        return { ...prev, proposedInterventions: next };
      });
    },
    [setForm]
  );

  const interventionTotals = useMemo(() => {
    const totals = new Map();
    proposedInterventions.forEach(intervention => {
      const lines = Array.isArray(intervention.costLines) ? intervention.costLines : [];
      const total = lines.reduce((sum, line) => sum + parseCurrencyToNumber(line.amount), 0);
      totals.set(intervention.id, total);
    });
    return totals;
  }, [proposedInterventions]);

  const overallCostTotal = useMemo(() => {
    let total = 0;
    interventionTotals.forEach(value => {
      if (Number.isFinite(value)) total += value;
    });
    return total;
  }, [interventionTotals]);
  const canApproveHighValueFunding =
    String(currentUserEmail || "").trim().toLowerCase() === HIGH_VALUE_FUNDING_APPROVER_EMAIL;
  const isHighValueFundingApprovalBlocked =
    Number.isFinite(overallCostTotal) &&
    overallCostTotal >= HIGH_VALUE_FUNDING_APPROVAL_THRESHOLD &&
    !canApproveHighValueFunding;
  const highValueFundingApprovalMessage = isHighValueFundingApprovalBlocked
    ? `Only Shelley Stacey (${HIGH_VALUE_FUNDING_APPROVER_EMAIL}) can approve funding of $${HIGH_VALUE_FUNDING_APPROVAL_THRESHOLD.toLocaleString()} or above.`
    : "";
  const decisionOptionsForApproval = useMemo(
    () =>
      highValueFundingApprovalMessage
        ? decisionOptions.map(option =>
            option.value === "approved"
              ? { ...option, disabled: true, description: "Requires Shelley approval at this funding level." }
              : option
          )
        : decisionOptions,
    [decisionOptions, highValueFundingApprovalMessage]
  );

  const revisionSourceIntervention = useMemo(() => {
    if (!isRevisionMode || !revisionSourceInterventionId) return null;
    const plans = Array.isArray(caseData?.actionPlans) ? caseData.actionPlans : [];
    for (const plan of plans) {
      const interventions = Array.isArray(plan?.interventions) ? plan.interventions : [];
      const match = interventions.find(intervention => idsMatch(intervention?.id, revisionSourceInterventionId));
      if (match) return match;
    }
    return null;
  }, [caseData, isRevisionMode, revisionSourceInterventionId]);

  const headerRevisionSource = revisionSourceIntervention || activeInterventionRecord;
  const additionalLetterOptions = useMemo(() => interventionLetterOptions(workingPlans, headerRevisionSource?.id),
    [workingPlans, headerRevisionSource?.id]);
  const selectedAdditionalLetters = useMemo(() => {
    if (String(additionalLetterSelections.primaryId) === String(headerRevisionSource?.id) &&
        additionalLetterSelections.approvalVersion === headerRevisionSource?.approvalVersion) {
      return additionalLetterOptions.filter(option => additionalLetterSelections.ids.includes(option.value));
    }
    return defaultInterventionLetterSelections(headerRevisionSource, additionalLetterOptions);
  }, [additionalLetterOptions, additionalLetterSelections, headerRevisionSource]);
  const approvalLetterCoverage = useMemo(() => interventionLetterCoverage(headerRevisionSource, selectedAdditionalLetters),
    [headerRevisionSource, selectedAdditionalLetters]);
  const approvalLetterCoverageCurrent = JSON.stringify(preparedApprovalCoverage) === JSON.stringify(approvalLetterCoverage);
  const approvalLetterItems = useMemo(() => interventionLetterDraftItems(headerRevisionSource, selectedAdditionalLetters, proposedInterventions),
    [headerRevisionSource, selectedAdditionalLetters, proposedInterventions]);
  const approvalHasFundingPackage = interventionApprovalIncludesFundingPackage(approvalLetterItems);
  const approvalSourceId = headerRevisionSource?.id;
  const approvalSourceVersion = headerRevisionSource?.approvalVersion || null;
  const approvalAttributionKey = `${approvalSourceId || ''}:${approvalSourceVersion || ''}`;
  const showApprovalAttribution = Boolean(completionNote && isApprovedDecisionOutcome && approvalSourceId);
  useEffect(() => {
    let current = true;
    setApprovalAttribution(null);
    if (!showApprovalAttribution) return undefined;
    apiFetch(`/api/interventions/${approvalSourceId}/approval-letter-context`)
      .then(response => response.ok ? response.json() : null)
      .then(context => {
        if (!current || !context || String(context.interventionId) !== String(approvalSourceId)) return;
        if (approvalSourceVersion && context.approvalVersion !== approvalSourceVersion) return;
        const name = String(context.decisionMakerDisplayName || '').trim();
        if (name) setApprovalAttribution({ key: approvalAttributionKey, name });
      })
      .catch(() => { /* The recorded outcome remains visible if attribution is unavailable. */ });
    return () => { current = false; };
  }, [showApprovalAttribution, approvalSourceId, approvalSourceVersion, approvalAttributionKey]);
  const decisionMakerDisplayName = approvalAttribution?.key === approvalAttributionKey
    ? approvalAttribution.name : null;
  const approvalRemovesFunding = !approvalHasFundingPackage && (
    headerRevisionSource?.metadata?.approvalLetterFollowUp?.fundingRemoved === true ||
    selectedAdditionalLetters.some(option => option.record.metadata?.approvalLetterFollowUp?.fundingRemoved === true)
  );
  const matchingHeaderRevision = useMemo(() => workingPlans
    .flatMap(plan => plan.interventions || [])
    .find(item => String(revisionSourceId(item) || '') === String(headerRevisionSource?.id || '') &&
      ['draft', 'submitted', 'in_review', 'changes_requested'].includes(resolveInterventionStateFields(item).reviewStatus)),
  [workingPlans, headerRevisionSource?.id]);
  const headerRevisionAction = resolveInterventionRevisionAction({
    intervention: headerRevisionSource,
    canModify: workingPlans.some(plan => (plan.interventions || []).some(item => idsMatch(item.id, headerRevisionSource?.id))),
    hasOpenProposal: hasBlockingProposal,
    matchingRevisionDraft: matchingHeaderRevision,
    role,
    currentStaffProfileId: currentUser?.staffProfileId || currentUser?.staff_profile_id,
    assignedStaffProfileId: caseData?.owner?.id,
  });
  const openHeaderRevision = async () => {
    if (!headerRevisionAction.available || revisionStartInFlightRef.current) return;
    revisionStartInFlightRef.current = true;
    setOpeningRevision(true);
    setShowRevisionNavigationConfirm(false);
    setError(null);
    try {
      const draft = headerRevisionAction.draft || await reviseIntervention(headerRevisionSource.id);
      if (!draft?.id) throw new Error('The revision could not be opened. Refresh the intervention and try again.');
      const draftState = resolveInterventionStateFields(draft);
      const step = ['submitted', 'in_review'].includes(draftState.reviewStatus) ? 'review' : 'framing';
      const planId = draft.actionPlanId || draft.action_plan_id;
      setClientLetterBody('');
      setLetterApprovalVersion(null);
      setApprovalLetterPackGenerated(false);
      history.replace(carryWorkQueueOrigin(buildApprovalWorkspacePath({ basePath: `/cases/${caseId}`, approvalType: 'intervention',
        applicationId, interventionId: draft.id, planId, step }), history.location));
      window.dispatchEvent(new CustomEvent('iset:intervention-assessment:select', {
        detail: { interventionId: draft.id, planId, requestedStep: step },
      }));
      dispatchCaseNotesRefresh();
    } catch (err) {
      setError(err?.message || 'Unable to open the revision.');
    } finally {
      revisionStartInFlightRef.current = false;
      setOpeningRevision(false);
    }
  };

  const reviewAmendmentDeltaSummary = useMemo(() => {
    if (!isRevisionMode || !isReviewStageStatus || !canDecideSubmittedProposal) {
      return null;
    }
    const baselineTotal = resolveInterventionCostTotal(revisionSourceIntervention);
    const revisedTotal = proposedInterventions.length ? overallCostTotal : null;
    if (baselineTotal === null || revisedTotal === null) {
      return null;
    }
    const netChange = revisedTotal - baselineTotal;
    return `Net change ${formatSignedCompactCurrencyDisplay(netChange)} · Revised total ${formatCompactCurrencyDisplay(revisedTotal)}`;
  }, [
    canDecideSubmittedProposal,
    isReviewStageStatus,
    isRevisionMode,
    overallCostTotal,
    proposedInterventions.length,
    revisionSourceIntervention,
  ]);

  useEffect(() => {
    if (costingDefaultsLoading || paymentTypeMappingLoading) return;
    updateProposedInterventions(current => {
      let changed = false;
      const next = current.map(intervention => {
        if (intervention.suggestionsSeeded) return intervention;
        const suggestions = buildSuggestedCostLines(intervention) || [];
        if (!suggestions.length) {
          return { ...intervention, suggestionsSeeded: true };
        }
        const existing = Array.isArray(intervention.costLines) ? intervention.costLines : [];
        const merged = [...existing, ...suggestions];
        changed = true;
        return { ...intervention, costLines: merged, suggestionsSeeded: true };
      });
      return changed ? next : current;
    });
  }, [buildSuggestedCostLines, costingDefaultsLoading, paymentTypeMappingLoading, updateProposedInterventions]);

  const updateIntervention = useCallback(
    (interventionId, updates) => {
      updateProposedInterventions(current =>
        current.map(item =>
          idsMatch(item.id, interventionId) ? { ...item, ...updates } : item
        )
      );
    },
    [updateProposedInterventions]
  );

  const addIntervention = useCallback(
    intervention => {
      updateProposedInterventions(current => [...current, intervention]);
    },
    [updateProposedInterventions]
  );

  const confirmInterventionDelete = useCallback(() => {
    if (isRevisionMode) {
      setInterventionDeleteId(null);
      return;
    }
    const deleteId = interventionDeleteId;
    if (deleteId !== null && typeof deleteId !== "undefined") {
      const nextProposedInterventions = proposedInterventions.filter(
        item => !idsMatch(item.id, deleteId)
      );
      const nextForm = { ...form, proposedInterventions: nextProposedInterventions };
      setForm(nextForm);
      if (wizardStepKey && typeof setInterventionWizardDraft === "function") {
        if (hasMeaningfulDraft(nextForm)) {
          setInterventionWizardDraft(wizardStepKey, packInterventionAssessmentDraft(formScope, nextForm, initialFormRef.current));
        } else {
          setInterventionWizardDraft(wizardStepKey, null);
        }
      }
    }
    setInterventionDeleteId(null);
    setProposedInterventionsTableVersion(current => current + 1);
  }, [
    interventionDeleteId,
    proposedInterventions,
    form,
    wizardStepKey,
    formScope,
    setInterventionWizardDraft,
    hasMeaningfulDraft,
    isRevisionMode,
    setForm,
  ]);

  const openAddInterventionModal = useCallback(() => {
    if (isRevisionMode) return;
    setInterventionModal({
      visible: true,
      mode: "add",
      interventionId: null,
      draft: buildEmptyIntervention(),
      original: null,
    });
    setInterventionModalErrors({});
  }, [isRevisionMode]);

  const openViewInterventionModal = useCallback(
    interventionId => {
      const intervention = proposedInterventions.find(item => idsMatch(item.id, interventionId));
      if (!intervention) return;
      setInterventionModal({
        visible: true,
        mode: "view",
        interventionId,
        draft: { ...intervention },
        original: { ...intervention },
      });
      setInterventionModalErrors({});
    },
    [proposedInterventions]
  );

  const startInterventionEdit = () => {
    setInterventionModal(prev => ({ ...prev, mode: "edit" }));
  };

  const resetInterventionModal = () => {
    setInterventionModal({
      visible: false,
      mode: "view",
      interventionId: null,
      draft: null,
      original: null,
    });
    setInterventionModalErrors({});
  };

  const updateInterventionModalDraft = updates => {
    setInterventionModal(prev => ({
      ...prev,
      draft: { ...(prev.draft || {}), ...(updates || {}) },
    }));
  };

  const validateInterventionDraft = draft => {
    const errors = {};
    if (!draft?.code) {
      errors.code = "Select an intervention code.";
    }
    if (!draft?.startDate) {
      errors.startDate = "Start date is required.";
    }
    const startUtc = parseIsoDateToUtc(draft?.startDate);
    const endUtc = parseIsoDateToUtc(draft?.endDate);
    if (startUtc !== null && endUtc !== null && endUtc < startUtc) {
      errors.endDate = "End date cannot be before start date.";
    }
    if (!draft?.code) {
      return errors;
    }
    const requiresNocCode = requiresNocForCode(draft.code);
    if (requiresNocCode) {
      if (!draft.interventionNocVersion) {
        errors.interventionNocVersion = "Select a NOC version for this intervention.";
      }
      if (!draft.interventionNoc) {
        errors.interventionNoc = "Select a NOC code for this intervention.";
      }
    }
    const educationCode = isEducationCode(draft.code);
    const employerCode = isEmployerCode(draft.code);
    const wageSubsidyCode = isWageSubsidyCode(draft.code);
    if (educationCode) {
      if (!draft.institution || !draft.institution.trim()) {
        errors.institution = "Training institution is required for this intervention code.";
      }
      if (!draft.itpDetails || !draft.itpDetails.trim()) {
        errors.itpDetails = "Enter Training plan details for this intervention.";
      }
    }
    if (employerCode) {
      if (!draft.institution || !draft.institution.trim()) {
        errors.institution = "Employer / delivery partner is required for this intervention code.";
      }
      if (wageSubsidyCode && (!draft.wageSubsidyDetails || !draft.wageSubsidyDetails.trim())) {
        errors.wageSubsidyDetails = "Wage subsidy details are required for this intervention code.";
      }
    }
    if (!educationCode && !employerCode && draft.deliveryMode !== "in_house") {
      if (!draft.institution || !draft.institution.trim()) {
        errors.institution = "Delivery partner is required when using external delivery.";
      }
    }
    return errors;
  };

  const saveInterventionModal = () => {
    const draft = interventionModal.draft || null;
    const errors = validateInterventionDraft(draft);
    if (Object.keys(errors).length) {
      setInterventionModalErrors(errors);
      return;
    }
    if (interventionModal.mode === "add" && draft) {
      addIntervention({
        ...draft,
        suggestionsSeeded: false,
        costLines: Array.isArray(draft.costLines) ? draft.costLines : [],
      });
      resetInterventionModal();
      return;
    }
    if (interventionModal.mode === "edit" && interventionModal.interventionId && draft) {
      updateIntervention(interventionModal.interventionId, draft);
      resetInterventionModal();
    }
  };

  const cancelInterventionEdit = () => {
    setInterventionModal(prev => ({
      ...prev,
      mode: "view",
      draft: prev.original,
    }));
    setInterventionModalErrors({});
  };

  const openAddCostLineModal = useCallback(
    interventionId => {
      const intervention = proposedInterventions.find(item => idsMatch(item.id, interventionId));
      if (!intervention) return;
      const draft = applyCostLinePayeeDefaults(
        buildEmptyCostLine({
          recurrence: {
            enabled: false,
            startDate: intervention.startDate || "",
            endDate: intervention.endDate || "",
            occurrences: "",
            amountPerPeriod: "",
          },
        }),
        intervention,
        participantLegalName,
        { allowTypeAutofill: true }
      );
      setCostLineModal({
        visible: true,
        mode: "add",
        interventionId,
        lineId: null,
        draft,
        original: null,
      });
      setCostLineModalErrors({});
      setCostLineAmountFocused(false);
      setCostLineAmountPerPeriodFocused(false);
    },
    [participantLegalName, proposedInterventions]
  );

  const openCostLineModal = useCallback(
    (interventionId, lineId) => {
      const intervention = proposedInterventions.find(item => idsMatch(item.id, interventionId));
      const line = intervention?.costLines?.find(item => idsMatch(item.id, lineId));
      if (!intervention || !line) return;
      const normalizedLine = normalizeCostLine(line) || buildEmptyCostLine();
      const draft = applyCostLinePayeeDefaults(normalizedLine, intervention, participantLegalName, {
        allowTypeAutofill: false,
      });
      setCostLineModal({
        visible: true,
        mode: "view",
        interventionId,
        lineId,
        draft,
        original: applyCostLinePayeeDefaults(
          normalizeCostLine(line) || buildEmptyCostLine(),
          intervention,
          participantLegalName,
          { allowTypeAutofill: false }
        ),
      });
      setCostLineModalErrors({});
      setCostLineAmountFocused(false);
      setCostLineAmountPerPeriodFocused(false);
    },
    [participantLegalName, proposedInterventions]
  );

  const resetCostLineModal = () => {
    setCostLineModal({
      visible: false,
      mode: "view",
      interventionId: null,
      lineId: null,
      draft: null,
      original: null,
    });
    setCostLineModalErrors({});
    setCostLineAmountFocused(false);
    setCostLineAmountPerPeriodFocused(false);
  };

  const updateCostLineDraft = useCallback(updater => {
    setCostLineModal(prev => {
      const current = prev.draft || {};
      const nextDraft =
        typeof updater === "function"
          ? updater(current)
          : { ...current, ...(updater || {}) };
      return {
        ...prev,
        draft: nextDraft,
      };
    });
  }, []);
  const updateCostLinePayeeType = useCallback(
    nextPayeeType => {
      setCostLineModal(prev => {
        if (!prev.draft) return prev;
        const intervention =
          proposedInterventions.find(item => idsMatch(item.id, prev.interventionId)) || null;
        const normalizedPayeeType = String(nextPayeeType || "").trim();
        const nextPayee = {
          ...(prev.draft.payee || {}),
          type: normalizedPayeeType,
        };
        if (normalizedPayeeType === PAYEE_TYPE_PARTICIPANT_CLIENT) {
          nextPayee.name = "";
          nextPayee.reference = "";
        }
        const nextDraft = applyCostLinePayeeDefaults(
          {
            ...prev.draft,
            payee: nextPayee,
          },
          intervention,
          participantLegalName,
          { allowTypeAutofill: false }
        );
        return {
          ...prev,
          draft: nextDraft,
        };
      });
      setCostLineModalErrors({});
    },
    [participantLegalName, proposedInterventions]
  );

  const buildRecurrenceFromIntervention = useCallback(
    (intervention, enabled) => {
      if (!enabled) {
        return {
          enabled: false,
          startDate: "",
          endDate: "",
          occurrences: "",
          amountPerPeriod: "",
        };
      }
      const startDate = intervention?.startDate || "";
      const endDate = intervention?.endDate || "";
      const occurrences = startDate && endDate ? autoOccurrencesFromDates(startDate, endDate, "monthly") : null;
      return {
        enabled: true,
        startDate,
        endDate,
        occurrences: occurrences ? String(occurrences) : "",
        amountPerPeriod: "",
      };
    },
    []
  );

  const updateCostLineType = useCallback(
    nextType => {
      setCostLineModal(prev => {
        if (!prev.draft) return prev;
        const intervention = proposedInterventions.find(item => idsMatch(item.id, prev.interventionId));
        if (!intervention) return prev;
        const recurrenceMode = getRecurrenceModeForType(nextType);
        const submissionTiming = getSubmissionTimingForType(nextType);
        const recurrenceEnabled =
          recurrenceMode === RECURRENCE_MODE_REQUIRED
            ? true
            : recurrenceMode === RECURRENCE_MODE_NOT_ALLOWED
              ? false
              : submissionTiming === SUBMISSION_TIMING_RECURRENCE_SCHEDULE && Boolean(prev.draft.recurrence?.enabled);
        const baseRecurrence = buildRecurrenceFromIntervention(intervention, recurrenceEnabled);
        const mergedRecurrence = mergeRecurrenceDefaults(baseRecurrence, prev.draft.recurrence || {});
        const recurrence = recurrenceEnabled
          ? { ...mergedRecurrence, enabled: true }
          : baseRecurrence;
        const previousPayee = prev.draft.payee && typeof prev.draft.payee === "object" ? prev.draft.payee : {};
        const previousDefaultPayeeType = deriveDefaultPayeeTypeForCostLine(prev.draft.type);
        const nextDefaultPayeeType = deriveDefaultPayeeTypeForCostLine(nextType);
        const previousPayeeType = String(previousPayee.type || "").trim();
        const previousPayeeName = String(previousPayee.name || "").trim();
        const participantName = String(participantLegalName || "").trim();
        const shouldResetPayee =
          Boolean(nextDefaultPayeeType) &&
          (
            !previousPayeeType ||
            previousPayeeType === previousDefaultPayeeType ||
            (
              previousPayeeType === nextDefaultPayeeType &&
              participantName &&
              previousPayeeName === participantName
            )
          );
        const payee = shouldResetPayee
          ? { type: nextDefaultPayeeType, name: "", reference: "" }
          : previousPayee;
        const nextDraft = applyCostLinePayeeDefaults(
          {
            ...prev.draft,
            type: normalizePaymentTypeCode(nextType) || nextType,
            payee,
            recurrence,
          },
          intervention,
          participantLegalName,
          { allowTypeAutofill: true }
        );
        return {
          ...prev,
          draft: nextDraft,
        };
      });
      setCostLineModalErrors({});
    },
    [
      buildRecurrenceFromIntervention,
      getRecurrenceModeForType,
      getSubmissionTimingForType,
      participantLegalName,
      proposedInterventions,
    ]
  );

  const toggleCostLineRecurrence = useCallback(
    enabled => {
      setCostLineModal(prev => {
        if (!prev.draft) return prev;
        const intervention = proposedInterventions.find(item => idsMatch(item.id, prev.interventionId));
        if (!intervention) return prev;
        const recurrenceMode = getRecurrenceModeForType(prev.draft.type);
        const resolvedEnabled =
          recurrenceMode === RECURRENCE_MODE_REQUIRED
            ? true
            : recurrenceMode === RECURRENCE_MODE_NOT_ALLOWED
              ? false
              : enabled;
        const baseRecurrence = buildRecurrenceFromIntervention(intervention, resolvedEnabled);
        const existing = prev.draft.recurrence || {};
        const mergedRecurrence = mergeRecurrenceDefaults(baseRecurrence, existing);
        const recurrence = resolvedEnabled
          ? { ...mergedRecurrence, enabled: true }
          : baseRecurrence;
        return {
          ...prev,
          draft: {
            ...prev.draft,
            recurrence,
          },
        };
      });
      setCostLineModalErrors({});
    },
    [buildRecurrenceFromIntervention, getRecurrenceModeForType, proposedInterventions]
  );

  const updateCostLineAmount = useCallback(
    value => {
      const sanitized = sanitizeCurrencyInput(value, { preserveTrailingDecimal: true });
      updateCostLineDraft(draft => {
        const next = { ...draft, amount: sanitized };
        if (draft.recurrence?.enabled && draft.recurrence?.occurrences) {
          const occ = Number(draft.recurrence.occurrences);
          if (Number.isFinite(occ) && occ > 0) {
            const total = parseCurrencyInput(sanitized);
            next.recurrence = {
              ...draft.recurrence,
              amountPerPeriod: total !== null ? formatCurrencyDisplay(total / occ) : "",
            };
          }
        }
        return next;
      });
    },
    [updateCostLineDraft]
  );

  const blurCostLineAmount = useCallback(() => {
    setCostLineAmountFocused(false);
    updateCostLineDraft(draft => {
      const sanitized = sanitizeCurrencyInput(draft.amount);
      return { ...draft, amount: sanitized || "" };
    });
  }, [updateCostLineDraft]);

  const blurCostLineAmountPerPeriod = useCallback(() => {
    setCostLineAmountPerPeriodFocused(false);
    updateCostLineDraft(draft => {
      const recurrence = { ...(draft.recurrence || {}) };
      recurrence.amountPerPeriod = sanitizeCurrencyInput(recurrence.amountPerPeriod) || "";
      return { ...draft, recurrence };
    });
  }, [updateCostLineDraft]);

  const updateCostLineAmountPerPeriod = useCallback(
    value => {
      const sanitized = sanitizeCurrencyInput(value, { preserveTrailingDecimal: true });
      updateCostLineDraft(draft => {
        const recurrence = { ...(draft.recurrence || {}), amountPerPeriod: sanitized };
        const occ = Number(recurrence.occurrences);
        let amount = draft.amount;
        if (Number.isFinite(occ) && occ > 0) {
          const per = parseCurrencyInput(sanitized);
          if (per !== null) {
            amount = formatCurrencyDisplay(per * occ);
          }
        }
        return { ...draft, amount, recurrence };
      });
    },
    [updateCostLineDraft]
  );

  const updateCostLineOccurrences = useCallback(
    value => {
      const cleaned = String(value || "").replace(/[^\d]/g, "");
      updateCostLineDraft(draft => {
        const recurrence = { ...(draft.recurrence || {}), occurrences: cleaned };
        if (recurrence.startDate && cleaned && !recurrence.endDate) {
          const derivedEnd = deriveEndDateFromOccurrences(recurrence.startDate, Number(cleaned));
          recurrence.endDate = derivedEnd || recurrence.endDate || "";
        }
        const amounts = recalcRecurringAmounts({
          amount: draft.amount,
          amountPerPeriod: recurrence.amountPerPeriod,
          occurrences: recurrence.occurrences,
          adjustMode: "total",
        });
        return {
          ...draft,
          amount: amounts.amount,
          recurrence: { ...recurrence, amountPerPeriod: amounts.amountPerPeriod },
        };
      });
    },
    [updateCostLineDraft]
  );

  const updateCostLineRecurrenceStart = useCallback(
    value => {
      updateCostLineDraft(draft => {
        const recurrence = { ...(draft.recurrence || {}), startDate: value };
        if (recurrence.startDate && recurrence.endDate) {
          const occ = autoOccurrencesFromDates(recurrence.startDate, recurrence.endDate, "monthly");
          recurrence.occurrences = occ ? String(occ) : "";
        }
        const amounts = recalcRecurringAmounts({
          amount: draft.amount,
          amountPerPeriod: recurrence.amountPerPeriod,
          occurrences: recurrence.occurrences,
          adjustMode: "total",
        });
        return {
          ...draft,
          amount: amounts.amount,
          recurrence: { ...recurrence, amountPerPeriod: amounts.amountPerPeriod },
        };
      });
    },
    [updateCostLineDraft]
  );

  const updateCostLineRecurrenceEnd = useCallback(
    value => {
      updateCostLineDraft(draft => {
        const recurrence = { ...(draft.recurrence || {}), endDate: value };
        if (recurrence.startDate && recurrence.endDate) {
          const occ = autoOccurrencesFromDates(recurrence.startDate, recurrence.endDate, "monthly");
          recurrence.occurrences = occ ? String(occ) : "";
        }
        const amounts = recalcRecurringAmounts({
          amount: draft.amount,
          amountPerPeriod: recurrence.amountPerPeriod,
          occurrences: recurrence.occurrences,
          adjustMode: "total",
        });
        return {
          ...draft,
          amount: amounts.amount,
          recurrence: { ...recurrence, amountPerPeriod: amounts.amountPerPeriod },
        };
      });
    },
    [updateCostLineDraft]
  );

  const validateCostLineDraft = draft => {
    const errors = {};
    if (!draft?.type) {
      errors.type = "Select a cost item.";
    }
    const parsedAmount = parseCurrencyInput(draft?.amount);
    if (draft?.amount === "" || parsedAmount === null || !Number.isFinite(parsedAmount) || parsedAmount < 0) {
      errors.amount = "Enter a valid amount in dollars.";
    }
    const recurrenceMode = getRecurrenceModeForType(draft?.type);
    const recurrenceRequired = recurrenceMode === RECURRENCE_MODE_REQUIRED;
    const recurrenceEnabled = Boolean(draft?.recurrence?.enabled);
    if ((recurrenceRequired || recurrenceEnabled) && !isRecurrenceScheduleComplete(draft)) {
      errors.recurrence = "Complete the installments schedule.";
    }
    return errors;
  };

  const commitCostLine = () => {
    const draft = costLineModal.draft || null;
    const intervention = proposedInterventions.find(item =>
      idsMatch(item.id, costLineModal.interventionId)
    );
    const resolvedDraft = applyCostLinePayeeDefaults(draft, intervention || null, participantLegalName, {
      allowTypeAutofill: true,
    });
    const errors = validateCostLineDraft(resolvedDraft);
    if (Object.keys(errors).length) {
      setCostLineModalErrors(errors);
      return;
    }
    updateProposedInterventions(current =>
      current.map(intervention => {
        if (!idsMatch(intervention.id, costLineModal.interventionId)) return intervention;
        const lines = Array.isArray(intervention.costLines) ? intervention.costLines : [];
        if (costLineModal.mode === "add") {
          return { ...intervention, costLines: [...lines, resolvedDraft] };
        }
        if (costLineModal.mode === "edit") {
          return {
            ...intervention,
            costLines: lines.map(line => (idsMatch(line.id, costLineModal.lineId) ? resolvedDraft : line)),
          };
        }
        return intervention;
      })
    );
    resetCostLineModal();
  };

  const removeCostLine = useCallback(
    (interventionId, lineId) => {
      updateProposedInterventions(current =>
        current.map(intervention => {
          if (!idsMatch(intervention.id, interventionId)) return intervention;
          const lines = Array.isArray(intervention.costLines) ? intervention.costLines : [];
          return { ...intervention, costLines: lines.filter(line => !idsMatch(line.id, lineId)) };
        })
      );
    },
    [updateProposedInterventions]
  );

  const handleInlineAmountChange = (interventionId, lineId, value) => {
    const cleaned = sanitizeCurrencyInput(value);
    updateProposedInterventions(current =>
      current.map(intervention => {
        if (!idsMatch(intervention.id, interventionId)) return intervention;
        const lines = Array.isArray(intervention.costLines) ? intervention.costLines : [];
        return {
          ...intervention,
          costLines: lines.map(line => (idsMatch(line.id, lineId) ? { ...line, amount: cleaned } : line)),
        };
      })
    );
  };

  const handleInlineAmountBlur = lineId => {
    if (inlineAmountEditingId === lineId) {
      setInlineAmountEditingId(null);
    }
  };

  useEffect(() => {
    const handleSelect = event => {
      const detail = event?.detail || {};
      const interventionId = detail.interventionId;
      if (!interventionId) return;
      setNewProposalScope(null);
      const requestedStep =
        detail.requestedStep ||
        pendingApprovalStepRef.current ||
        null;
      pendingApprovalStepRef.current = requestedStep;
      const resolvedRequestedStep = resolveRequestedWizardStep(requestedStep, activeStepIds);
      const selectionKey = caseId ? `${caseId}:${interventionId}` : null;
      const storedStep = resolveStoredStep(selectionKey);
      if (typeof setSelectedInterventionId === "function") {
        const numericInterventionId = Number(interventionId);
        setSelectedInterventionId(Number.isFinite(numericInterventionId) ? numericInterventionId : interventionId);
      }
      const planId = detail.planId;
      setSelectedDraftId(interventionId);
      setHydratedDraftId(null);
      setHydratedDraftUpdatedAt(null);
      setCurrentInterventionStatus(null);
      setRevisionContext(null);
      setError(null);
      setSuccessMessage("");
      setCompletionNote(null);
      setAttemptedSteps({});
      setCurrentStep(
        resolvedRequestedStep
          ? resolvedRequestedStep
          : storedStep || BASE_STEP_IDS[0]
      );
      if (planId) {
        const numericPlanId = Number(planId);
        const resolvedPlanId = Number.isFinite(numericPlanId) ? numericPlanId : planId;
        if (typeof setSelectedActionPlanId === "function") {
          setSelectedActionPlanId(resolvedPlanId);
        }
      }
    };

    const handleNew = event => {
      const detail = event?.detail || {};
      const planId = detail.planId;
      pendingApprovalStepRef.current = null;
      if (hasBlockingProposal) {
        setError("A proposal is already in progress. Resume it from the table.");
        setSuccessMessage("");
        return;
      }
      setNewProposalScope(`${caseId || ""}:${applicationId || ""}:${planId || selectedActionPlanId || ""}`);
      setSelectedDraftId(null);
      setHydratedDraftId(null);
      setHydratedDraftUpdatedAt(null);
      setCurrentInterventionStatus(null);
      setRevisionContext(null);
      if (typeof setSelectedInterventionId === "function") {
        setSelectedInterventionId(null);
      }
      setError(null);
      setSuccessMessage("");
      setCompletionNote(null);
      setAttemptedSteps({});
      setCurrentStep(BASE_STEP_IDS[0]);
      setForm(prev => ({
        ...defaultFormState,
        actionPlanId: planId ? String(planId) : prev.actionPlanId,
      }));
      if (planId && typeof setSelectedActionPlanId === "function") {
        const numericPlanId = Number(planId);
        setSelectedActionPlanId(Number.isFinite(numericPlanId) ? numericPlanId : planId);
      }
    };

    if (proposalRequest && appliedProposalRequestRef.current !== proposalRequest) {
      appliedProposalRequestRef.current = proposalRequest;
      handleNew({ detail: proposalRequest });
    }

    if (typeof window !== "undefined") {
      window.addEventListener("iset:intervention-assessment:select", handleSelect);
      window.addEventListener("iset:intervention-assessment:new", handleNew);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("iset:intervention-assessment:select", handleSelect);
        window.removeEventListener("iset:intervention-assessment:new", handleNew);
      }
    };
  }, [
    activeStepIds,
    caseId,
    applicationId,
    selectedActionPlanId,
    proposalRequest,
    hasBlockingProposal,
    resolveStoredStep,
    setSelectedActionPlanId,
    setSelectedInterventionId,
    setForm,
  ]);

  useEffect(() => {
    if (!approvalWorkspaceEntry) return;
    const plans = String(approvalRequestedStep || '').toLowerCase() === 'communication'
      ? getInterventionWorkingPlans(caseData?.actionPlans, applicationId, { includeClosed: true })
      : workingPlans;
    if (!plans.length) return;
    const approvalKey = `${caseId || "case"}:${approvalWorkspaceEntry.key}`;
    if (approvalSelectionAppliedRef.current === approvalKey) return;
    const reviewTargets = plans
      .flatMap(plan =>
        (Array.isArray(plan?.interventions) ? plan.interventions : []).map(intervention => ({
          ...intervention,
          actionPlanId: intervention?.actionPlanId || plan?.id || null,
        }))
      )
      .filter(item => {
        const statusKey = String(item?.status || "").toLowerCase();
        if (["submitted", "in_review"].includes(statusKey)) return true;
        if (['draft', 'changes_requested'].includes(statusKey) &&
          approvalWorkspaceEntry.interventionId &&
          String(item.id) === String(approvalWorkspaceEntry.interventionId)) return true;
        const requestedStepKey = String(approvalRequestedStep || "").toLowerCase();
        if (requestedStepKey !== "communication") return false;
        return resolveInterventionApprovalLetterFollowUp(item).eligible;
      });
    const requestedInterventionId = approvalWorkspaceEntry.interventionId
      ? String(approvalWorkspaceEntry.interventionId)
      : null;
    const target = requestedInterventionId
      ? (reviewTargets.find(item => String(item?.id) === requestedInterventionId) ||
          approvedSourceForRevision(plans, requestedInterventionId))
      : reviewTargets.sort(
        (left, right) =>
          new Date(right?.updatedAt || right?.createdAt || 0).getTime() -
          new Date(left?.updatedAt || left?.createdAt || 0).getTime()
      )[0];
    if (!target?.id) return;
    approvalSelectionAppliedRef.current = approvalKey;
    pendingApprovalStepRef.current = approvalRequestedStep || "decision";
    setSelectedDraftId(target.id);
    setHydratedDraftId(null);
    setHydratedDraftUpdatedAt(null);
    setCurrentInterventionStatus(null);
    setRevisionContext(null);
    setError(null);
    setSuccessMessage("");
    setCompletionNote(null);
    setAttemptedSteps({});
    if (typeof setSelectedInterventionId === "function") {
      setSelectedInterventionId(target.id);
    }
    if (target.actionPlanId && typeof setSelectedActionPlanId === "function") {
      setSelectedActionPlanId(target.actionPlanId);
    }
  }, [
    approvalRequestedStep,
    approvalWorkspaceEntry,
    workingPlans,
    applicationId,
    caseData?.actionPlans,
    caseId,
    setSelectedActionPlanId,
    setSelectedInterventionId,
  ]);

  useEffect(() => {
    if (cancelledRevisionRef.current) return;
    const plans = workingPlans;
    const scopedHistoryPlans = getInterventionWorkingPlans(caseData?.actionPlans, applicationId, { includeClosed: true });
    if (!scopedHistoryPlans.length) return;
    if (pendingApprovalStepRef.current && !selectedDraftId) return;
    const isDraftStatusValue = value => String(value || "").toLowerCase() === "draft";
    const isChangesRequestedValue = value => String(value || "").toLowerCase() === "changes_requested";
    const isSubmittedValue = value => String(value || "").toLowerCase() === "submitted";
    const isInReviewValue = value => String(value || "").toLowerCase() === "in_review";
    const findById = interventionId => {
      const target = String(interventionId);
      for (const plan of scopedHistoryPlans) {
        const list = Array.isArray(plan.interventions) ? plan.interventions : [];
        const match = list.find(item => String(item?.id) === target);
        if (match) return { ...match, actionPlanId: match.actionPlanId || match.action_plan_id || plan.id };
      }
      return null;
    };
    const pickLatestDraft = list => {
      const sorted = list
        .filter(
          item =>
            isDraftStatusValue(item?.status) ||
            isChangesRequestedValue(item?.status) ||
            isSubmittedValue(item?.status) ||
            isInReviewValue(item?.status)
        )
        .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
      return sorted[0] || null;
    };

    const hydrate = draft => {
      if (!draft) return;
      // Resolve the concrete selection first. Never hydrate one record into the
      // transient scope of a previous selection or the unselected new proposal.
      if (!idsMatch(selectedInterventionId, draft.id) || !idsMatch(selectedActionPlanId, draft.actionPlanId)) {
        setSelectedDraftId(draft.id);
        setSelectedInterventionId(draft.id);
        if (draft.actionPlanId) setSelectedActionPlanId(draft.actionPlanId);
        return;
      }
      const metadata = draft.metadata || {};
      const revision = normalizeRevisionContext(metadata.revision);
      const approvalLetterFollowUp = resolveInterventionApprovalLetterFollowUp(draft);
      const appliedRevisionContext =
        approvalLetterFollowUp.eligible && approvalLetterFollowUp.isRevision
          ? {
              kind: "approved_intervention",
              sourceInterventionId: approvalLetterFollowUp.sourceInterventionId || draft.id || null,
              sourceActionPlanId: approvalLetterFollowUp.sourceActionPlanId || draft.actionPlanId || null,
              sourceStatus: approvalLetterFollowUp.sourceStatus || draft.status || "approved",
              sourceTitle: approvalLetterFollowUp.sourceTitle || draft.title || "this approved intervention",
            }
          : null;
      const review = metadata.review && typeof metadata.review === "object" ? metadata.review : {};
      const storedStepKey = caseId && draft.id ? `${caseId}:${draft.id}` : null;

      const proposed = normalizeProposedInterventions(metadata.proposedInterventions || metadata.proposed_interventions);
      let nextProposed = proposed;
      if (!nextProposed.length) {
        nextProposed = [
          buildEmptyIntervention({
            id: revision?.sourceInterventionId || buildUuid(),
            code: metadata.snapshot?.code || draft.code || "",
            startDate: metadata.snapshot?.startDate || draft.startDate || "",
            endDate: metadata.snapshot?.endDate || draft.endDate || "",
            deliveryMode: metadata.snapshot?.deliveryMode === "in_house" ? "in_house" : "partner",
            institution: metadata.snapshot?.institution || draft.institution || "",
            programName: metadata.snapshot?.programName || draft.programName || "",
            itpDetails: metadata.snapshot?.itpDetails || "",
            wageSubsidyDetails: metadata.snapshot?.wageSubsidyDetails || "",
            interventionNoc: metadata.snapshot?.nocCode || draft.noc || "",
            interventionNocVersion: metadata.snapshot?.nocVersion || draft.nocVersion || "",
            costLines: Array.isArray(metadata.snapshot?.costLines)
              ? metadata.snapshot.costLines.map(normalizeCostLine).filter(Boolean)
              : [],
          }),
        ];
      }
      if (revision && nextProposed.length > 1) {
        nextProposed = nextProposed.slice(0, 1);
      }
      const draftHasFundingPackage = interventionApprovalIncludesFundingPackage(nextProposed);
      const mappedBarriers = Array.isArray(metadata.barriers)
        ? metadata.barriers
            .map(val => BARRIER_OPTIONS.find(opt => opt.value === (val.value || val)))
            .filter(Boolean)
        : [];
      const caseContext = resolveCaseContext(caseData);
      const fallbackOtherFundingDetails =
        caseData?.assessment_other_funding_details ||
        caseData?.assessmentOtherFunding ||
        caseContext?.assessmentOtherFunding ||
        caseContext?.otherFunding ||
        null;
      const normalizedOtherFunding = normalizeOtherFundingDetails(
        metadata.otherFundingDetails || fallbackOtherFundingDetails
      );
      const draftActionPlanId = draft.actionPlanId ? String(draft.actionPlanId) : String(form.actionPlanId || "");
      const hydratedForm = {
        ...defaultFormState,
        actionPlanId: draftActionPlanId,
        rationale: metadata.rationale ?? draft.notes ?? "",
        ...readInterventionAssessmentNarratives(metadata),
        narrativeScope: hasInterventionAssessmentNarratives(metadata) ? `${caseId}:${draftActionPlanId}:${draft.id}` : null,
        narrativesFromLegacySnapshot: false,
        otherFundingInvolved: normalizedOtherFunding.involved,
        otherFundingSources: normalizedOtherFunding.sources,
        otherFundingNwacCoverage: normalizedOtherFunding.nwacCoverage,
        otherFundingNotes: normalizedOtherFunding.notes,
        childcareNeed: metadata.childcareNeed || "",
        childcareFunding: metadata.childcareFunding || "",
        barriers: mappedBarriers,
        proposedInterventions: nextProposed,
        eiVerificationStatus: review.eiStatus || "",
        eiVerificationNotes: review.eiNotes || "",
        decisionOutcome: review.decision || (approvalLetterFollowUp.eligible ? "approved" : ""),
        decisionNotes: review.decisionNotes || "",
        eiVerificationDocumentId: review.eiDocumentId || null,
      };
      const storedDraft = restoreInterventionAssessmentDraft(resolveStoredDraft(storedStepKey), formScope,
        hydratedForm, { revision: Boolean(revision) });
      const nextForm = storedDraft ? mergeStoredDraft(hydratedForm, storedDraft) : hydratedForm;
      // Saved review evidence is historical. A stale browser draft cannot replace it.
      nextForm.eiVerificationStatus = hydratedForm.eiVerificationStatus;
      nextForm.eiVerificationNotes = hydratedForm.eiVerificationNotes;
      nextForm.eiVerificationDocumentId = hydratedForm.eiVerificationDocumentId;
      hydrateForm(nextForm);
      initialFormRef.current = hydratedForm;
      setDecisionRevisionEditing(false);
      if (draft.actionPlanId && typeof setSelectedActionPlanId === "function") {
        setSelectedActionPlanId(draft.actionPlanId);
      }
      if (draft.id && typeof setSelectedInterventionId === "function") {
        setSelectedInterventionId(draft.id);
      }
      const draftStatus = String(draft.status || "").toLowerCase();
      const draftReviewWorkflow = draft.reviewWorkflow || draft.review_workflow || null;
      const draftReviewStage = draftReviewWorkflow?.currentStage || draftReviewWorkflow?.current_stage || null;
      const draftTwoStepReviewEnabled = Boolean(
        draft.twoStepReviewEnabled ||
          draft.two_step_review_enabled ||
          draftReviewWorkflow
      );
      const draftCanShowDecisionStep =
        ["submitted", "in_review"].includes(draftStatus) &&
        canDecideSubmittedProposal &&
        (!draftTwoStepReviewEnabled || draftReviewStage === INTERVENTION_REVIEW_STAGES.nwacReview);
      const stepIds = draftCanShowDecisionStep
        ? [...BASE_STEP_IDS, ...SUBMITTED_STEP_IDS]
        : BASE_STEP_IDS;
      const storedStep = resolveStoredStep(storedStepKey, stepIds);
      const requestedStep = resolveRequestedWizardStep(pendingApprovalStepRef.current, stepIds);
      const defaultStep = ["submitted", "in_review"].includes(draftStatus)
        ? (stepIds.includes("decision") ? "decision" : "review")
        : BASE_STEP_IDS[0];
      const nextStep =
        requestedStep ||
        storedStep ||
        defaultStep;
      setHydratedDraftId(draft.id || null);
      setHydratedDraftUpdatedAt(draft.updatedAt || draft.createdAt || null);
      setCurrentInterventionStatus(draftStatus || null);
      setRevisionContext(revision || appliedRevisionContext);
      setCompletionNote(
        approvalLetterFollowUp.eligible
          ? {
              type: "success",
              header: !approvalLetterFollowUp.pendingLetter
                ? approvalLetterFollowUp.isRevision
                  ? "Revision complete"
                  : "Intervention proposal complete"
                : approvalLetterFollowUp.isRevision
                  ? "Revision approved"
                  : "Intervention proposal approved",
              body: approvalLetterFollowUp.historicalResolution
                ? approvalLetterFollowUp.historicalResolution.message
                : approvalLetterFollowUp.letterSent
                ? approvalLetterFollowUp.isRevision
                  ? draftHasFundingPackage
                    ? `The approved revision was applied to ${approvalLetterFollowUp.sourceTitle || "this intervention"}, and the client funding revision letter was sent.`
                    : `The approved change was applied to ${approvalLetterFollowUp.sourceTitle || "this intervention"}, and the client intervention change approval letter was sent.`
                  : "This intervention proposal has been approved, and the client approval letter was sent."
                : approvalLetterFollowUp.isRevision
                  ? draftHasFundingPackage
                    ? `The approved revision was applied to ${approvalLetterFollowUp.sourceTitle || "this intervention"}. Prepare or send the funding revision letter from here.`
                    : `The approved change was applied to ${approvalLetterFollowUp.sourceTitle || "this intervention"}. Prepare or send the client intervention change approval letter from here.`
                  : draftHasFundingPackage
                    ? "This intervention proposal has been approved. Prepare or send the client approval letter and related funding documents from here."
                    : "This intervention proposal has been approved. Prepare or send the client approval letter from here.",
              decisionLetterSent: approvalLetterFollowUp.letterSent,
              showDecisionLetters: approvalLetterFollowUp.pendingLetter,
              persistedApprovalLetterFollowUp: true,
            }
          : null
      );
      setAttemptedSteps({});
      setCurrentStep(nextStep);
    };

    const targetId = selectedInterventionId;
    const resolved = targetId
      ? (findById(targetId) || approvedSourceForRevision(scopedHistoryPlans, targetId))
      : null;
    if (resolved) {
      const updatedAt = resolved.updatedAt || resolved.createdAt || null;
      if (formScopeReady && idsMatch(resolved.id, hydratedDraftId) && updatedAt === hydratedDraftUpdatedAt) return;
      hydrate(resolved);
      return;
    }

    if (targetId && !resolved) return;

    const fallbackDraft = pickLatestDraft(plans.flatMap(plan => (plan.interventions || [])
      .map(item => ({ ...item, actionPlanId: item.actionPlanId || item.action_plan_id || plan.id })))) ||
      (!explicitlyCreatingProposal && pendingCompletions.length === 1 ? pendingCompletions[0] : null);
    if (fallbackDraft) {
      const updatedAt = fallbackDraft.updatedAt || fallbackDraft.createdAt || null;
      if (formScopeReady && idsMatch(fallbackDraft.id, hydratedDraftId) && updatedAt === hydratedDraftUpdatedAt) return;
      hydrate(fallbackDraft);
    }
  }, [
    approvalRequestedStep,
    workingPlans,
    applicationId,
    formScope, formScopeReady, hydrateForm, selectedInterventionId, selectedActionPlanId,
    explicitlyCreatingProposal,
    pendingCompletions,
    caseData,
    caseId,
    canDecideSubmittedProposal,
    form.actionPlanId,
    hasMeaningfulDraft,
    hydratedDraftId,
    hydratedDraftUpdatedAt,
    mergeStoredDraft,
    resolveStoredDraft,
    resolveStoredStep,
    selectedDraftId,
    setSelectedActionPlanId,
    setSelectedInterventionId,
  ]);

  // `handleSave` advances the mutable baseline without changing `form`.
  // Recompute on each render so the loading-state render after a successful
  // save observes the new baseline instead of retaining a stale memoized true.
  const isDirty = JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

  const requiresPrimaryNoc = useMemo(
    () => Boolean(primaryIntervention?.code && requiresNocForCode(primaryIntervention.code)),
    [primaryIntervention]
  );
  const hasPrimaryNoc = useMemo(
    () => Boolean(primaryIntervention?.interventionNocVersion && primaryIntervention?.interventionNoc),
    [primaryIntervention]
  );
  const canAutoSave = useMemo(
    () =>
      Boolean(
        form.actionPlanId &&
          primaryIntervention?.code &&
          primaryIntervention?.startDate &&
          (!requiresPrimaryNoc || hasPrimaryNoc)
      ),
    [form.actionPlanId, primaryIntervention, requiresPrimaryNoc, hasPrimaryNoc]
  );

  const serializeCostLine = useCallback(line => {
    const recurrence = line?.recurrence || {};
    const payee = line?.payee || {};
    const payeeType = typeof payee.type === "string" ? payee.type.trim() : "";
    const payeeName = typeof payee.name === "string" ? payee.name.trim() : "";
    const payeeReference = typeof payee.reference === "string" ? payee.reference.trim() : "";
    const occurrencesValue =
      recurrence.occurrences === "" || recurrence.occurrences === null || typeof recurrence.occurrences === "undefined"
        ? null
        : Number(recurrence.occurrences);
    const occurrences = Number.isFinite(occurrencesValue) ? occurrencesValue : null;
    return {
      id: line?.id || buildUuid(),
      type: line?.type || null,
      amount: parseCurrencyInput(line?.amount),
      notes: line?.notes || null,
      payee:
        payeeType || payeeName || payeeReference
          ? {
              type: payeeType || null,
              name: payeeName || null,
              reference: payeeReference || null,
            }
          : null,
      recurrence: {
        enabled: Boolean(recurrence.enabled),
        startDate: formatDate(recurrence.startDate) || null,
        endDate: formatDate(recurrence.endDate) || null,
        occurrences,
        amountPerPeriod: parseCurrencyInput(recurrence.amountPerPeriod),
      },
    };
  }, []);

  const serializeProposedInterventions = useCallback(
    interventions => {
      const list = Array.isArray(interventions) ? interventions.filter(item => item.code || item.startDate || item.endDate) : [];
      if (!list.length) return [];
      return list.map(item => ({
        id: item.id || buildUuid(),
        code: item.code || null,
        startDate: formatDate(item.startDate) || null,
        endDate: formatDate(item.endDate) || null,
        deliveryMode: item.deliveryMode === "in_house" ? "in_house" : "partner",
        institution: item.institution || null,
        programName: item.programName || null,
        itpDetails: item.itpDetails || null,
        wageSubsidyDetails: item.wageSubsidyDetails || null,
        interventionNoc: item.interventionNoc || null,
        interventionNocVersion: item.interventionNocVersion || null,
        suggestionsSeeded: Boolean(item.suggestionsSeeded),
        costLines: Array.isArray(item.costLines) ? item.costLines.map(serializeCostLine) : [],
      }));
    },
    [serializeCostLine]
  );

  const validateProposal = useCallback(
    () => {
      const errors = {};
      const interventionErrors = {};
      const costLineErrors = {};
      if (!form.actionPlanId) {
        errors.actionPlanId = "Action Plan is required.";
      }
      if (isRevisionMode && !form.rationale.trim()) {
        errors.rationale = "Reason for this revision is required.";
      }
      INTERVENTION_NARRATIVE_FIELDS.forEach(({ key, label }) => {
        if (!String(form[key] || '').trim()) errors[key] = `${label} is required.`;
      });
      if (!narrativeLoading.ready) errors.narratives = 'Assessment wording must finish loading before submission.';
      if (!proposedInterventions.length) {
        interventionErrors._global = "Add at least one proposed intervention.";
      }
      proposedInterventions.forEach(intervention => {
        const entryErrors = {};
        if (!intervention.code) entryErrors.code = "Select an intervention code.";
        if (!intervention.startDate) entryErrors.startDate = "Start date is required.";
        const startUtc = parseIsoDateToUtc(intervention.startDate);
        const endUtc = parseIsoDateToUtc(intervention.endDate);
        if (startUtc !== null && endUtc !== null && endUtc < startUtc) {
          entryErrors.endDate = "End date cannot be before start date.";
        }
        const requiresNocCode = requiresNocForCode(intervention.code);
        if (requiresNocCode) {
          if (!intervention.interventionNocVersion) {
            entryErrors.interventionNocVersion = "Select a NOC version for this intervention.";
          }
          if (!intervention.interventionNoc) {
            entryErrors.interventionNoc = "Select a NOC code for this intervention.";
          }
        }
        const educationCode = isEducationCode(intervention.code);
        const employerCode = isEmployerCode(intervention.code);
        const wageSubsidyCode = isWageSubsidyCode(intervention.code);
        if (educationCode) {
          if (!intervention.institution || !intervention.institution.trim()) {
            entryErrors.institution = "Training institution is required for this intervention code.";
          }
          if (!intervention.itpDetails || !intervention.itpDetails.trim()) {
            entryErrors.itpDetails = "Enter Training plan details for this intervention.";
          }
        }
        if (employerCode) {
          if (!intervention.institution || !intervention.institution.trim()) {
            entryErrors.institution = "Employer / delivery partner is required for this intervention code.";
          }
          if (wageSubsidyCode && (!intervention.wageSubsidyDetails || !intervention.wageSubsidyDetails.trim())) {
            entryErrors.wageSubsidyDetails = "Wage subsidy details are required for this intervention code.";
          }
        }
        if (!educationCode && !employerCode && intervention.deliveryMode !== "in_house") {
          if (!intervention.institution || !intervention.institution.trim()) {
            entryErrors.institution = "Delivery partner is required when using external delivery.";
          }
        }
        if (Object.keys(entryErrors).length) {
          interventionErrors[intervention.id] = entryErrors;
        }
        const lines = Array.isArray(intervention.costLines) ? intervention.costLines : [];
        const lineErrors = {};
        lines.forEach(line => {
          const detailErrors = {};
          if (!line.type) detailErrors.type = "Select a cost item.";
          const amount = parseCurrencyInput(line.amount);
          if (line.amount === "" || amount === null || !Number.isFinite(amount) || amount < 0) {
            detailErrors.amount = "Enter a valid amount in dollars.";
          }
          const recurrenceMode = getRecurrenceModeForType(line.type);
          const recurrenceRequired = recurrenceMode === RECURRENCE_MODE_REQUIRED;
          const recurrenceEnabled = Boolean(line.recurrence?.enabled);
          if ((recurrenceRequired || recurrenceEnabled) && !isRecurrenceScheduleComplete(line)) {
            detailErrors.recurrence = "Complete the installments schedule.";
          }
          if (Object.keys(detailErrors).length) {
            lineErrors[line.id] = detailErrors;
          }
        });
        if (Object.keys(lineErrors).length) {
          costLineErrors[intervention.id] = lineErrors;
        }
      });
      if (Object.keys(interventionErrors).length) {
        errors.interventions = interventionErrors;
      }
      if (Object.keys(costLineErrors).length) {
        errors.costLines = costLineErrors;
      }
      return errors;
    },
    [form, isRevisionMode, narrativeLoading.ready, proposedInterventions, getRecurrenceModeForType]
  );

  const buildProposalPayload = useCallback(
    statusValue => {
      const normalizedStatus = String(statusValue || "").trim().toLowerCase();
      const isDecisionPayload = ["approved", "rejected", "changes_requested"].includes(normalizedStatus);
      const proposedPayload = serializeProposedInterventions(
        isRevisionMode ? proposedInterventions.slice(0, 1) : proposedInterventions
      );
      const primary = primaryIntervention;
      const primaryCostLines = Array.isArray(primary?.costLines)
        ? primary.costLines.map(serializeCostLine)
        : [];
      const primaryStartDate = primary?.startDate || "";
      const primaryEndDate = primary?.endDate || "";
      const interventionDuration = clampInterventionDurationDaysForIlmp(
        calculateDurationDays(primaryStartDate, primaryEndDate)
      );
      const primaryCost = Number.isFinite(interventionTotals.get(primary?.id)) ? interventionTotals.get(primary?.id) : 0;
      const normalizedOtherFunding = normalizeOtherFundingDetails(
        {
          involved: form.otherFundingInvolved,
          sources: form.otherFundingSources,
          nwacCoverage: form.otherFundingNwacCoverage,
          notes: form.otherFundingNotes,
        }
      );
      return {
        code: primary?.code || null,
        title: resolveInterventionLabel(primary?.code) || "Draft intervention",
        status: statusValue,
        startDate: primaryStartDate || null,
        endDate: primaryEndDate || null,
        durationDays: interventionDuration !== null ? String(interventionDuration) : null,
        cost: primaryCost,
        notes: (isRevisionMode ? form.rationale : form.recommendationJustification) || "",
        noc: primary?.interventionNoc || null,
        nocVersion: primary?.interventionNocVersion || null,
        metadata: {
          ...(isRevisionMode && revisionContext
            ? {
                revision: {
                  kind: revisionContext.kind || "approved_intervention",
                  sourceInterventionId: revisionContext.sourceInterventionId,
                  sourceActionPlanId: revisionContext.sourceActionPlanId || null,
                  sourceStatus: revisionContext.sourceStatus || null,
                  sourceTitle: revisionContext.sourceTitle || null,
                  openedAt: revisionContext.openedAt || null,
                },
              }
            : {}),
          snapshot: {
            code: primary?.code || null,
            startDate: primaryStartDate || null,
            endDate: primaryEndDate || null,
            deliveryMode: primary?.deliveryMode === "in_house" ? "in_house" : "partner",
            institution: primary?.institution || "",
            programName: primary?.programName || "",
            itpDetails: primary?.itpDetails || "",
            wageSubsidyDetails: primary?.wageSubsidyDetails || "",
            nocVersion: primary?.interventionNocVersion || "",
            nocCode: primary?.interventionNoc || "",
            costLines: primaryCostLines,
          },
          costLines: primaryCostLines,
          proposedInterventions: proposedPayload.length ? proposedPayload : null,
          rationale: (isRevisionMode ? form.rationale : form.recommendationJustification) || "",
          ...readInterventionAssessmentNarratives(form),
          barriers: Array.isArray(form.barriers) ? form.barriers.map(item => item.value || item) : [],
          otherFundingDetails: normalizedOtherFunding,
          childcareNeed: form.childcareNeed || "",
          childcareFunding: form.childcareFunding || "",
          review: {
            eiStatus: form.eiVerificationStatus || "",
            eiNotes: form.eiVerificationNotes || "",
            decision: isDecisionPayload ? (form.decisionOutcome || normalizedStatus) : "",
            decisionNotes: isDecisionPayload ? (form.decisionNotes || "") : "",
            eiDocumentId: form.eiVerificationDocumentId || null,
          },
        },
      };
    },
    [
      form,
      isRevisionMode,
      primaryIntervention,
      proposedInterventions,
      revisionContext,
      serializeProposedInterventions,
      serializeCostLine,
      interventionTotals,
      resolveInterventionLabel,
    ]
  );

  const buildDecisionPayload = useCallback(
    (decision, { potId = null, postingContext = null, eiDocumentId = null } = {}) => ({
      status: decision,
      expectedDecisionVersion: activeInterventionRecord?.decisionVersion,
      ...(potId ? { potId } : {}),
      ...(postingContext ? { postingContext } : {}),
      metadata: {
        review: {
          eiStatus: form.eiVerificationStatus || "",
          eiNotes: form.eiVerificationNotes || "",
          decision,
          decisionNotes: form.decisionNotes || "",
          eiDocumentId: eiDocumentId || form.eiVerificationDocumentId || null,
        },
      },
    }),
    [activeInterventionRecord?.decisionVersion, form.decisionNotes, form.eiVerificationDocumentId, form.eiVerificationNotes, form.eiVerificationStatus]
  );

  const dispatchCaseNotesRefresh = useCallback(() => {
    if (!caseId || typeof window === "undefined") return;
    const detail = { caseId };
    window.dispatchEvent(new CustomEvent("case-notes-refresh", { detail }));
    window.dispatchEvent(new CustomEvent("case-events-refresh", { detail }));
  }, [caseId]);

  const handleSave = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setError(null);
        setSuccessMessage("");
      }
      if (!isEditable) {
        const message = "This proposal is read-only and cannot be updated.";
        if (!silent) {
          setError(message);
        }
        return { ok: false, error: new Error(message) };
      }
      if (!form.actionPlanId) {
        const message = "Select an Action Plan before saving.";
        if (!silent) {
          setError(message);
        }
        return { ok: false, error: new Error(message) };
      }
      if (!narrativeLoading.ready) {
        const message = narrativeLoading.error || 'Assessment wording is loading. Try saving again shortly.';
        if (!silent) setError(message);
        return { ok: false, error: new Error(message) };
      }
      const targetStatus = isChangesRequestedStatus
        ? "changes_requested"
        : isReviewStageStatus
          ? statusValue
          : "draft";
      const payload = buildProposalPayload(targetStatus);
      if (isDecisionRevisionEditing) {
        const errors = validateProposal();
        if (Object.keys(errors).length) {
          const feedback = getProposalValidationFeedback(errors);
          if (feedback) {
            setAttemptedSteps(previous => ({ ...previous, [feedback.step]: true }));
            setCurrentStep(feedback.step);
          }
          setError(feedback?.message || 'Review the highlighted assessment fields before saving.');
          return { ok: false };
        }
        payload.assessmentDecisionRevision = true;
        payload.expectedDecisionVersion = decisionRevisionBaseline.current;
        delete payload.metadata.revision;
        delete payload.metadata.review.decision;
        delete payload.metadata.review.decisionNotes;
      }
      setIsSubmitting(true);
      try {
        const reviewTargetId =
          activeInterventionIdValue
            ? Number(activeInterventionIdValue)
            : null;
        const actionPlanId = Number(form.actionPlanId);
        if (
          Number.isInteger(reviewTargetId) &&
          reviewTargetId > 0 &&
          typeof updateInterventionRecord === "function"
        ) {
          const updated = await updateInterventionRecord(actionPlanId, reviewTargetId, payload);
          if (!isCurrentScope()) return { ok: false };
          setSelectedDraftId(updated?.id || reviewTargetId);
          setHydratedDraftId(updated?.id || reviewTargetId);
          setHydratedDraftUpdatedAt(updated?.updatedAt || updated?.createdAt || null);
        } else if (typeof createIntervention === "function") {
          const created = await createIntervention(actionPlanId, payload);
          if (!isCurrentScope()) return { ok: false };
          setSelectedDraftId(created?.id || null);
          setHydratedDraftId(created?.id || null);
          setHydratedDraftUpdatedAt(created?.updatedAt || created?.createdAt || null);
          if (typeof setSelectedInterventionId === "function") {
            setSelectedInterventionId(created?.id || null);
          }
        }
        setCurrentInterventionStatus(targetStatus);
        if (!silent) {
          setSuccessMessage(targetStatus === "draft" ? "Progress saved." : "Changes saved.");
        }
        initialFormRef.current = form;
        if (isDecisionRevisionEditing) {
          setDecisionRevisionEditing(false); setCurrentStep('decision');
          dispatchCaseNotesRefresh();
          window.dispatchEvent(new CustomEvent('iset:supporting-documents:refresh'));
        }
        return { ok: true };
      } catch (err) {
        if (!isCurrentScope()) return { ok: false, error: err };
        const message = err?.message || "Failed to save progress.";
        if (!silent) {
          setError(message);
        }
        return { ok: false, error: err || new Error(message) };
      } finally {
        if (isCurrentScope()) setIsSubmitting(false);
      }
    },
    [
      buildProposalPayload,
      isCurrentScope,
      narrativeLoading.ready,
      narrativeLoading.error,
      isDecisionRevisionEditing, validateProposal, dispatchCaseNotesRefresh,
      createIntervention,
      form,
      activeInterventionIdValue,
      isEditable,
      isChangesRequestedStatus,
      isReviewStageStatus,
      statusValue,
      setSelectedInterventionId,
      updateInterventionRecord,
    ]
  );

  const validateStep = useCallback(
    stepId => {
      const errors = validateProposal();
      if (stepId === "plan") return !errors.actionPlanId;
      if (stepId === "framing") {
        return !errors.interventions || Object.keys(errors.interventions).length === 0;
      }
      if (stepId === "rationale") return !errors.rationale && !errors.narratives && INTERVENTION_NARRATIVE_FIELDS.every(({ key }) => !errors[key]);
      if (stepId === "cost") return !errors.costLines || Object.keys(errors.costLines).length === 0;
      return true;
    },
    [validateProposal]
  );

  const handleSubmitProposal = useCallback(
    async event => {
      if (event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }
      setError(null);
      setSuccessMessage("");
      if (!isEditable) {
        setError("This proposal is read-only and cannot be submitted.");
        return;
      }
      if (!form.actionPlanId) {
        setError("Select an Action Plan before submitting the proposal.");
        return;
      }
      const invalidSteps = REQUIRED_STEP_IDS.filter(stepId => !validateStep(stepId));
      if (invalidSteps.length > 0) {
        setAttemptedSteps(prev => ({ ...prev, [invalidSteps[0]]: true }));
        setCurrentStep(invalidSteps[0]);
        setError("Complete required fields before submitting.");
        return;
      }
      setIsSubmitting(true);
      try {
        const payload = buildProposalPayload("submitted");
        const activeTargetId =
          activeInterventionIdValue && Number.isInteger(Number(activeInterventionIdValue)) && Number(activeInterventionIdValue) > 0
            ? Number(activeInterventionIdValue)
            : null;
        const actionPlanId = Number(form.actionPlanId);
        const saved = activeTargetId && typeof updateInterventionRecord === "function"
          ? await updateInterventionRecord(actionPlanId, activeTargetId, payload)
          : await createIntervention(actionPlanId, payload);
        if (!isCurrentScope()) return;
        if (saved?.id) {
          setSelectedDraftId(saved.id);
          setHydratedDraftId(saved.id);
          setHydratedDraftUpdatedAt(saved.updatedAt || saved.createdAt || null);
          if (typeof setSelectedInterventionId === "function") {
            setSelectedInterventionId(saved.id);
          }
        }
        setCurrentInterventionStatus("submitted");
        const submittedToRegionalReview = Boolean(
          saved?.twoStepReviewEnabled ||
            saved?.two_step_review_enabled ||
            saved?.reviewWorkflow ||
            saved?.review_workflow ||
            twoStepReviewEnabled
        );
        const submittedSubject = isRevisionMode ? "Intervention change" : "Intervention proposal";
        setSuccessMessage(
          submittedToRegionalReview
            ? `${submittedSubject} submitted to Regional Manager review.`
            : `${submittedSubject} submitted for review.`
        );
      } catch (err) {
        if (!isCurrentScope()) return { ok: false, error: err };
        setError(err?.message || "Failed to submit proposal.");
      } finally {
        if (isCurrentScope()) setIsSubmitting(false);
      }
    },
    [
      buildProposalPayload,
      isCurrentScope,
      createIntervention,
      activeInterventionIdValue,
      form.actionPlanId,
      isRevisionMode,
      isEditable,
      setSelectedInterventionId,
      twoStepReviewEnabled,
      updateInterventionRecord,
      validateStep,
    ]
  );

  const handleRecallSubmission = useCallback(async () => {
    if (!activeInterventionIdValue) {
      setError("Select a submitted proposal before recalling it.");
      return;
    }
    setError(null);
    setSuccessMessage("");
    setIsRecallingSubmission(true);
    try {
      const response = await apiFetch(`/api/interventions/${activeInterventionIdValue}/assessment/recall`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || payload?.success === false) {
        throw new Error(payload?.message || payload?.error || "Failed to recall submission.");
      }
      if (!isCurrentScope()) return;
      const recalledIntervention = payload?.intervention || {};
      const recalledId = recalledIntervention.id || activeInterventionIdValue;
      setSelectedDraftId(recalledId);
      setHydratedDraftId(recalledId);
      setHydratedDraftUpdatedAt(recalledIntervention.updatedAt || recalledIntervention.createdAt || null);
      setCurrentInterventionStatus(recalledIntervention.status || "draft");
      setCompletionNote(null);
      setCurrentStep("review");
      setShowRecallConfirmModal(false);
      if (typeof setSelectedInterventionId === "function") {
        setSelectedInterventionId(recalledId);
      }
      if (typeof refresh === "function") {
        await refresh().catch(() => {});
      }
      if (!isCurrentScope()) return;
      setSuccessMessage("Submission recalled. You can make corrections and submit it again when ready.");
    } catch (err) {
      if (!isCurrentScope()) return;
      setError(err?.message || "Failed to recall submission.");
    } finally {
      if (isCurrentScope()) setIsRecallingSubmission(false);
    }
  }, [
    activeInterventionIdValue,
    isCurrentScope,
    refresh,
    setSelectedInterventionId,
  ]);



  const handleReviewWorkflowAction = useCallback(
    async action => {
      if (!activeInterventionIdValue) {
        setError("Select an intervention request before reviewing it.");
        return;
      }
      const note = String(reviewWorkflowNote || "").trim();
      const requiresNote =
        action === INTERVENTION_REVIEW_ACTIONS.rmReturnToSubmitter ||
        action === INTERVENTION_REVIEW_ACTIONS.rmForwardChangesToSubmitter;
      if (requiresNote && !note) {
        setError("A note is required before returning this request.");
        return;
      }
      setError(null);
      setSuccessMessage("");
      setReviewWorkflowActionLoading(action);
      try {
        const response = await apiFetch(`/api/interventions/${activeInterventionIdValue}/review-workflow/action`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action, note }),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || payload?.success === false) {
          throw new Error(payload?.message || payload?.error || "Failed to update Regional Manager review.");
        }
        if (!isCurrentScope()) return;
        const intervention = payload?.intervention || null;
        if (intervention?.id) {
          setSelectedDraftId(intervention.id);
          setHydratedDraftId(intervention.id);
          setHydratedDraftUpdatedAt(intervention.updatedAt || intervention.createdAt || null);
          setCurrentInterventionStatus(intervention.status || intervention.reviewStatus || "submitted");
          if (typeof setSelectedInterventionId === "function") {
            setSelectedInterventionId(intervention.id);
          }
        }
        setReviewWorkflowNote("");
        if (typeof refresh === "function") {
          await refresh().catch(() => {});
        }
        if (!isCurrentScope()) return;
        if (note) {
          dispatchCaseNotesRefresh();
        }
        setSuccessMessage(
          action === INTERVENTION_REVIEW_ACTIONS.rmSubmitToNwac
            ? "Intervention request submitted for final decision."
            : action === INTERVENTION_REVIEW_ACTIONS.rmForwardChangesToSubmitter
              ? "Requested changes forwarded to the submitter."
              : "Intervention request returned to the submitter."
        );
      } catch (err) {
        if (!isCurrentScope()) return { ok: false, error: err };
        setError(err?.message || "Failed to update Regional Manager review.");
      } finally {
        if (isCurrentScope()) setReviewWorkflowActionLoading(null);
      }
    },
    [
      activeInterventionIdValue,
      isCurrentScope,
      dispatchCaseNotesRefresh,
      refresh,
      reviewWorkflowNote,
      setSelectedInterventionId,
    ]
  );

  const buildApprovedInterventionPayload = useCallback(
    (intervention, { approvedStatus = "approved", potId = null, postingContext = null, eiDocumentId = null } = {}) => {
      const totalCost = interventionTotals.get(intervention.id) || 0;
      const durationDays = clampInterventionDurationDaysForIlmp(
        calculateDurationDays(intervention.startDate, intervention.endDate)
      );
      const approvedCostLines = Array.isArray(intervention.costLines)
        ? intervention.costLines.filter(hasApprovedFundingAmount).map(serializeCostLine)
        : [];
      const approvedProposedInterventions = [
        {
          id: intervention.id || buildUuid(),
          code: intervention.code || null,
          startDate: intervention.startDate || null,
          endDate: intervention.endDate || null,
          deliveryMode: intervention.deliveryMode === "in_house" ? "in_house" : "partner",
          institution: intervention.institution || null,
          programName: intervention.programName || null,
          itpDetails: intervention.itpDetails || null,
          wageSubsidyDetails: intervention.wageSubsidyDetails || null,
          interventionNoc: intervention.interventionNoc || null,
          interventionNocVersion: intervention.interventionNocVersion || null,
          cost: totalCost,
          costLines: approvedCostLines,
        },
      ];
      return {
        code: intervention.code || null,
        title: resolveInterventionLabel(intervention.code) || "Intervention",
        status: approvedStatus,
        startDate: intervention.startDate || null,
        endDate: intervention.endDate || null,
        durationDays: durationDays !== null ? String(durationDays) : null,
        cost: totalCost,
        notes: (isRevisionMode ? form.rationale : form.recommendationJustification) || "",
        noc: intervention.interventionNoc || null,
        nocVersion: intervention.interventionNocVersion || null,
        potId: potId || null,
        postingContext: postingContext || null,
        metadata: {
          snapshot: {
            code: intervention.code || null,
            startDate: intervention.startDate || null,
            endDate: intervention.endDate || null,
            deliveryMode: intervention.deliveryMode,
            institution: intervention.institution || "",
            programName: intervention.programName || "",
            itpDetails: intervention.itpDetails || "",
            wageSubsidyDetails: intervention.wageSubsidyDetails || "",
            nocVersion: intervention.interventionNocVersion || "",
            nocCode: intervention.interventionNoc || "",
            costTotal: totalCost,
            costLines: approvedCostLines,
          },
          costLines: approvedCostLines,
          proposedInterventions: approvedProposedInterventions,
          rationale: (isRevisionMode ? form.rationale : form.recommendationJustification) || "",
          ...readInterventionAssessmentNarratives(form),
          barriers: Array.isArray(form.barriers) ? form.barriers.map(item => item.value || item) : [],
          otherFundingDetails: normalizeOtherFundingDetails(
            {
              involved: form.otherFundingInvolved,
              sources: form.otherFundingSources,
              nwacCoverage: form.otherFundingNwacCoverage,
              notes: form.otherFundingNotes,
            }
          ),
          childcareNeed: form.childcareNeed || "",
          childcareFunding: form.childcareFunding || "",
          review: {
            eiStatus: form.eiVerificationStatus || "",
            eiNotes: form.eiVerificationNotes || "",
            decision: "approved",
            decisionNotes: form.decisionNotes || "",
            eiDocumentId: eiDocumentId || form.eiVerificationDocumentId || null,
          },
        },
      };
    },
    [form, isRevisionMode, interventionTotals, resolveInterventionLabel, serializeCostLine]
  );

  const getDecisionBlockingIssues = useCallback(
    ({ requireActiveIntervention = true } = {}) => {
      const outcome = String(form.decisionOutcome || "").trim().toLowerCase();
      const reasons = [];
      let targetStep = null;
      if (!outcome) {
        reasons.push(`Select a decision for ${decisionSubjectThis}.`);
        targetStep = "decision";
      }
      if (outcome === "approved") {
        if (highValueFundingApprovalMessage) {
          reasons.push(highValueFundingApprovalMessage);
          targetStep = targetStep || "decision";
        }
        const planFundingIssues = getInterventionPlanFundingIssues(selectedPlan);
        if (planFundingIssues.length) {
          reasons.push(...planFundingIssues);
          targetStep = targetStep || "plan";
        }
      }
      if (outcome === "changes_requested" && !form.decisionNotes.trim()) {
        reasons.push(`Requesting changes to ${decisionSubjectThis} requires a note.`);
        targetStep = targetStep || "decision";
      }
      if (outcome === "rejected" && !form.decisionNotes.trim()) {
        reasons.push(`Denying ${decisionSubjectThis} requires a note.`);
        targetStep = targetStep || "decision";
      }
      if (requireActiveIntervention && !activeInterventionIdValue) {
        reasons.push("Select a proposal in review before submitting a decision.");
        targetStep = targetStep || "decision";
      }
      if (!Number(form.actionPlanId)) {
        reasons.push("Action Plan is required.");
        targetStep = targetStep || "plan";
      }
      return { reasons, targetStep };
    },
    [
      activeInterventionIdValue,
      decisionSubjectThis,
      form.actionPlanId,
      form.decisionNotes,
      form.decisionOutcome,
      highValueFundingApprovalMessage,
      selectedPlan,
    ]
  );

  const handleSubmitDecision = useCallback(
    async event => {
      if (event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }
      setError(null);
      setSuccessMessage("");
      setAttemptedSteps(prev => ({ ...prev, decision: true }));
      if (!canDecideSubmittedProposal) {
        setError("Only Decision Makers can record a decision on a proposal in review.");
        return { ok: false };
      }
      if (!isEditable) {
        setError("This proposal is read-only and cannot be updated.");
        return { ok: false };
      }
      if (!isReviewStageStatus) {
        setError("Only proposals in review can be decided.");
        return { ok: false };
      }
      const outcome = form.decisionOutcome;
      const { reasons, targetStep } = getDecisionBlockingIssues({ requireActiveIntervention: true });
      if (reasons.length) {
        setDecisionBlockerReasons(reasons);
        setDecisionBlockerTargetStep(targetStep);
        setDecisionBlockerVisible(true);
        return { ok: false };
      }
      const actionPlanId = Number(form.actionPlanId);
      setIsSubmitting(true);
      try {
        if (outcome === "approved") {
          const approvalPotId = selectedPlanBudgetPot || null;
          const approvalPostingContext = selectedPlanPostingContext || "external";
          const approvalEiDocumentId = form.eiVerificationDocumentId || null;
          const interventionsToCreate = proposedInterventions.length
            ? (isRevisionMode ? proposedInterventions.slice(0, 1) : proposedInterventions)
            : [];
          if (!interventionsToCreate.length) {
            setError("Add at least one proposed intervention before approving.");
            return { ok: false };
          }
          if (isRevisionMode && revisionSourceInterventionId) {
            const [primary] = interventionsToCreate;
            const sourceActionPlanId = Number(revisionSourceActionPlanId || form.actionPlanId);
            const primaryPayload = buildApprovedInterventionPayload(primary, {
              approvedStatus: revisionSourceStatus || "approved",
              potId: approvalPotId,
              postingContext: approvalPostingContext,
              eiDocumentId: approvalEiDocumentId,
            });
            // Approval changes the approved facts, while the server preserves
            // the operational delivery state held at the moment of approval.
            delete primaryPayload.status;
            delete primaryPayload.deliveryStatus;
            delete primaryPayload.delivery_status;
            const updated = await updateInterventionRecord(
              sourceActionPlanId,
              Number(revisionSourceInterventionId),
              {
                ...primaryPayload,
                revisionAppliedFromInterventionId: Number(activeInterventionIdValue),
                expectedRevisionDecisionVersion: activeInterventionRecord?.decisionVersion,
              }
            );
            if (!isCurrentScope()) return { ok: false };
            setCurrentInterventionStatus(updated?.status || revisionSourceStatus || "approved");
            setApprovalLetterPackGenerated(false);
            setApprovalLetterPackTabId("client");
            setClientLetterBody("");
            setSendingLetterError(null);
            setCompletionNote({
              type: "success",
              header: "Revision workflow complete",
              body: approvalHasFundingPackage
                ? `The approved revision was applied to ${revisionSourceTitle}. The final revision record was retained, and the funding revision letter can now be prepared or sent from here.`
                : `The approved change was applied to ${revisionSourceTitle}. The final revision record was retained, and the client intervention change approval letter can now be prepared or sent from here.`,
              showDecisionLetters: true,
            });
            // The decided revision draft is retained by the server as audit evidence,
            // but it is intentionally partitioned out of operational intervention rows.
            // Refresh immediately so the client does not keep showing the pre-decision
            // draft as a second operational intervention until the next page load.
            await refresh().catch(() => {});
          } else {
            const [, ...rest] = interventionsToCreate;
            const primaryPayload = buildDecisionPayload("approved", {
              potId: approvalPotId,
              postingContext: approvalPostingContext,
              eiDocumentId: approvalEiDocumentId,
            });
            await updateInterventionRecord(actionPlanId, Number(activeInterventionIdValue), primaryPayload);
            for (const intervention of rest) {
              const payload = buildApprovedInterventionPayload(intervention, {
                potId: approvalPotId,
                postingContext: approvalPostingContext,
                eiDocumentId: approvalEiDocumentId,
              });
              await createIntervention(actionPlanId, {
                ...payload,
                approvalSourceInterventionId: Number(activeInterventionIdValue),
                approvalSourceItemId: intervention.id,
              });
            }
            if (!isCurrentScope()) return { ok: false };
            setCurrentInterventionStatus("approved");
            setSuccessMessage("Interventions approved and created.");
            setApprovalLetterPackGenerated(false);
            setApprovalLetterPackTabId("client");
            setClientLetterBody("");
            setSendingLetterError(null);
            setCompletionNote({
              type: "success",
              header: "Intervention proposal approved",
              body: approvalHasFundingPackage
                ? "Approved interventions were created. Prepare or send the client approval letter and related funding documents from here. Further changes can be proposed from the approved intervention."
                : "Approved interventions were created. Prepare or send the client approval letter from here. Further changes can be proposed from the approved intervention.",
              showDecisionLetters: true,
            });
          }
        } else {
          const payload = buildDecisionPayload(outcome);
          const updated = await updateInterventionRecord(actionPlanId, Number(activeInterventionIdValue), payload);
          if (!isCurrentScope()) return { ok: false };
          const updatedReviewWorkflow = updated?.reviewWorkflow || updated?.review_workflow || null;
          const updatedReviewStage = updatedReviewWorkflow?.currentStage || updatedReviewWorkflow?.current_stage || null;
          const returnedToRm = updatedReviewStage === INTERVENTION_REVIEW_STAGES.returnedToRm;
          setCurrentInterventionStatus(updated?.status || updated?.reviewStatus || outcome);
          setSuccessMessage(
            returnedToRm
              ? "Changes requested. The request is back with the Regional Manager."
              : "Decision submitted."
          );
          if (outcome === "rejected") {
            setApprovalLetterPackGenerated(false);
            setApprovalLetterPackTabId("client");
            setClientLetterBody("");
            setSendingLetterError(null);
            setCompletionNote({
              type: "info",
              header: isRevisionMode ? "Revision closed" : "Proposal closed",
              body: isRevisionMode
                ? `The revision was denied and ${revisionSourceTitle} was left unchanged.`
                : "The proposal was denied and closed. Start a new proposal from the Interventions table when needed.",
              showDecisionLetters: false,
            });
          }
        }
        if (form.decisionNotes.trim()) {
          dispatchCaseNotesRefresh();
        }
        return { ok: true };
      } catch (err) {
        if (!isCurrentScope()) return { ok: false, error: err };
        setError(err?.message || "Failed to submit decision.");
        return { ok: false, error: err };
      } finally {
        if (isCurrentScope()) setIsSubmitting(false);
      }
    },
    [
      activeInterventionIdValue,
      isCurrentScope,
      activeInterventionRecord?.decisionVersion,
      approvalHasFundingPackage,
      buildApprovedInterventionPayload,
      buildDecisionPayload,
      canDecideSubmittedProposal,
      createIntervention,
      dispatchCaseNotesRefresh,
      form,
      isEditable,
      isRevisionMode,
      isReviewStageStatus,
      proposedInterventions,
      refresh,
      selectedPlanBudgetPot,
      selectedPlanPostingContext,
      revisionSourceActionPlanId,
      revisionSourceInterventionId,
      revisionSourceStatus,
      revisionSourceTitle,
      updateInterventionRecord,
      getDecisionBlockingIssues,
    ]
  );

  const validationErrors = useMemo(() => validateProposal(), [validateProposal]);
  const showPlanErrors = Boolean(attemptedSteps.plan);
  const showFramingErrors = Boolean(attemptedSteps.framing);
  const showRationaleErrors = Boolean(attemptedSteps.rationale);
  const showCostErrors = Boolean(attemptedSteps.cost);
  const showDecisionErrors = Boolean(attemptedSteps.decision);

  const institutionApprovalLetters = useMemo(
    () =>
      buildInstitutionApprovalLetters({
        interventions: approvalLetterItems,
        applicantName: participantLegalName || applicantSalutationName || "Client",
        decisionDate: formatDate(new Date()),
        caseManagerName: currentUserName || "",
        caseManagerEmail: currentUserEmail || "",
        caseManagerPhone: "",
        isRevision: isRevisionMode,
      }),
    [applicantSalutationName, currentUserEmail, currentUserName, isRevisionMode, participantLegalName, approvalLetterItems]
  );
  const coFunderApprovalLetters = useMemo(
    () =>
      buildCoFunderApprovalLetters({
        fundingSources: normalizeOtherFundingSources(form.otherFundingSources),
        nwacCoverage: form.otherFundingNwacCoverage,
        notes: form.otherFundingNotes,
        interventions: approvalLetterItems,
        applicantName: participantLegalName || applicantSalutationName || "Client",
        trackingReference,
        decisionDate: formatDate(new Date()),
        caseManagerName: currentUserName || "",
        caseManagerEmail: currentUserEmail || "",
        caseManagerPhone: "",
        isRevision: isRevisionMode,
      }),
    [
      applicantSalutationName,
      currentUserEmail,
      currentUserName,
      form.otherFundingNotes,
      form.otherFundingNwacCoverage,
      form.otherFundingSources,
      isRevisionMode,
      participantLegalName,
      approvalLetterItems,
      trackingReference,
    ]
  );
  const loanProviderApprovalLetters = useMemo(
    () =>
      buildLoanProviderApprovalLetters({
        interventions: approvalLetterItems,
        applicantName: participantLegalName || applicantSalutationName || "Client",
        trackingReference,
        decisionDate: formatDate(new Date()),
        caseManagerName: currentUserName || "",
        caseManagerEmail: currentUserEmail || "",
        caseManagerPhone: "",
        isRevision: isRevisionMode,
      }),
    [
      applicantSalutationName,
      currentUserEmail,
      currentUserName,
      isRevisionMode,
      participantLegalName,
      approvalLetterItems,
      trackingReference,
    ]
  );
  const institutionApprovalLettersForDisplay = approvalLetterPackGenerated
    ? editableInstitutionApprovalLetters
    : institutionApprovalLetters;
  const coFunderApprovalLettersForDisplay = approvalLetterPackGenerated
    ? editableCoFunderApprovalLetters
    : coFunderApprovalLetters;
  const loanProviderApprovalLettersForDisplay = approvalLetterPackGenerated
    ? editableLoanProviderApprovalLetters
    : loanProviderApprovalLetters;
  useEffect(() => {
    if (approvalLetterPackGenerated) return;
    setEditableInstitutionApprovalLetters([]);
    setEditableCoFunderApprovalLetters([]);
    setEditableLoanProviderApprovalLetters([]);
  }, [approvalLetterPackGenerated]);
  const updateEditableLetterBody = useCallback((setter, targetLetter, nextBody, targetIndex = null) => {
    if (!targetLetter) return;
    const targetKey = targetLetter.id || targetLetter.fileName || targetLetter.title || "";
    setter(current => {
      const source = Array.isArray(current) ? current : [];
      return source.map((letter, index) => {
        const letterKey = letter?.id || letter?.fileName || letter?.title || "";
        const isMatch = targetKey ? letterKey === targetKey : index === targetIndex;
        if (!isMatch) return letter;
        return { ...letter, body: nextBody };
      });
    });
  }, []);
  const buildApprovedClientLetterBody = useCallback(() => {
    const recipient = String(applicantSalutationName || "").trim() || "Client";
    const interventions = approvalLetterItems;
    const primary = interventions[0] || null;
    const requestPhrase = (() => {
      if (interventions.length > 1) return "to receive support for the approved interventions in your plan";
      const program = String(primary?.programName || "").trim();
      const institution = String(primary?.institution || "").trim();
      if (program && institution) return `to pursue ${program} at ${institution}`;
      if (program) return `to pursue ${program}`;
      if (institution) return `to receive support for training at ${institution}`;
      return "to receive Indigenous Skills and Employment Training (ISET) support";
    })();
    const costLines = interventions.flatMap(intervention =>
      (Array.isArray(intervention?.costLines) ? intervention.costLines : []).map(line => ({ intervention, line }))
    );
    const fundingLines = costLines
      .map(({ intervention, line }) => {
        const amount = parseCurrencyToNumber(line?.amount);
        if (!(amount > 0)) return "";
        const label = formatCostTypeForLetter(line?.type);
        const payee = line?.payee && typeof line.payee === "object" ? line.payee : {};
        const payeeType = String(payee.type || deriveDefaultPayeeTypeForCostLine(line?.type) || "").trim();
        const explicitPayeeName = String(payee.name || "").trim();
        const fallbackPayeeName = deriveDefaultPayeeNameForCostLine(payeeType, intervention, participantLegalName || "");
        const payeeName = explicitPayeeName || fallbackPayeeName;
        const payeeTypeKey = normalizePayeeTypeKey(payeeType);
        const payeePhrase = (() => {
          if (payeeName) return payeeName;
          if (payeeTypeKey === "participantclient" || payeeTypeKey === "client") return "you";
          const target = PAYEE_TYPE_DETAIL_TARGET_BY_KEY[payeeTypeKey] || "";
          return target ? `the ${target}` : "the approved payee";
        })();
        const payableDate = formatInterventionDates(intervention?.startDate, intervention?.endDate);
        const dateClause = payableDate && payableDate !== "—" ? ` for ${payableDate}` : "";
        return `- ${label}: $${Number(amount).toFixed(2)} payable to ${payeePhrase}${dateClause}.`;
      })
      .filter(Boolean);
    const fundingParagraph = approvalHasFundingPackage
      ? fundingLines.length
        ? [
            isRevisionMode ? "The revised approved funding is:" : "The approved funding is:",
            ...fundingLines,
          ].join("\n")
        : isRevisionMode
          ? "The approved funding for your eligible supports under this intervention has been updated."
          : "Funding has been approved for your eligible supports under this intervention."
      : "This approval does not include funded cost lines.";
    const fundingFormsParagraph = approvalRemovesFunding
      ? "I have attached a revised Client Funding Agreement recording the removal of the previous funding commitment. Please review and complete the agreement. No Banking Details form is required for this change."
      : approvalHasFundingPackage
      ? isRevisionMode
        ? "I have attached a red-line revised Client Funding Agreement for your review, along with a Banking Details form. When you have a moment, please review and complete any required attachments so we can keep your funding moving without delay."
        : "I have attached the Client Funding Agreement for your review, along with a Banking Details form. When you have a moment, please review and complete the attachments so we can move ahead with your funding deposit."
      : "Because this approval does not include funded cost lines, no Client Funding Agreement or banking/funding forms are attached.";
    return [
      isRevisionMode
        ? approvalHasFundingPackage
          ? "Funding Revision Letter"
          : "Intervention Change Approval Letter"
        : "Letter of Approval",
      `Date: ${formatDate(new Date())}`,
      "",
      `Dear ${recipient},`,
      "",
      isRevisionMode
        ? approvalHasFundingPackage
          ? `I’m writing with an update about your ISET support. After reviewing the requested changes to your approved intervention, I’m pleased to confirm that the funding for your intervention has been changed as follows. This revised approval will continue to support you ${requestPhrase}.`
          : `I’m writing with an update about your ISET support. After reviewing the requested changes to your approved intervention, I’m pleased to confirm that the intervention change has been approved. This approval will continue to support you ${requestPhrase}.`
        : approvalHasFundingPackage
          ? `I’m pleased to let you know that the Native Women's Association of Canada (NWAC), through its Indigenous Skills and Employment Training (ISET) Program, has approved funding to support you ${requestPhrase}.`
          : `I’m pleased to let you know that the Native Women's Association of Canada (NWAC), through its Indigenous Skills and Employment Training (ISET) Program, has approved your intervention ${requestPhrase}.`,
      "",
      fundingParagraph,
      "",
      "This letter covers the approved support for:",
      ...interventions.map(intervention => `- ${intervention.title || `Intervention ${intervention.code || ''}`}.`),
      "",
      fundingFormsParagraph,
      "",
      "If you have any questions, please reach out to me directly. I look forward to continuing to support you through your ISET intervention.",
      "",
      "Sincerely,",
      DEFAULT_ORG_NAME,
    ].join("\n");
  }, [
    applicantSalutationName,
    approvalHasFundingPackage,
    approvalRemovesFunding,
    isRevisionMode,
    participantLegalName,
    approvalLetterItems,
  ]);
  const canGenerateLetterDrafts = isApprovedDecisionOutcome && !sendingLetter;
  const generateLetterPackDrafts = useCallback(() => {
    if (isApprovedDecisionOutcome) {
      setLetterApprovalVersion(headerRevisionSource?.approvalVersion || null);
      setPreparedApprovalCoverage(approvalLetterCoverage);
      setClientLetterBody(buildApprovedClientLetterBody());
      setEditableInstitutionApprovalLetters(
        approvalHasFundingPackage ? institutionApprovalLetters : []
      );
      setEditableCoFunderApprovalLetters(
        approvalHasFundingPackage ? coFunderApprovalLetters : []
      );
      setEditableLoanProviderApprovalLetters(
        approvalHasFundingPackage ? loanProviderApprovalLetters : []
      );
      generatedSupplementaryLettersRef.current = JSON.stringify(approvalHasFundingPackage
        ? [institutionApprovalLetters, coFunderApprovalLetters, loanProviderApprovalLetters] : [[], [], []]);
      setApprovalLetterPackGenerated(true);
      return;
    }
  }, [
    approvalHasFundingPackage,
    buildApprovedClientLetterBody,
    coFunderApprovalLetters,
    institutionApprovalLetters,
    isApprovedDecisionOutcome,
    loanProviderApprovalLetters,
    headerRevisionSource?.approvalVersion,
    approvalLetterCoverage,
  ]);
  const activeLetterWorkflowId = isApprovedDecisionOutcome
    ? letterWorkflows.approval
    : null;
  const handleSendDecisionLetter = useCallback(async ({ interventionId: requestedInterventionId } = {}) => {
    if (decisionLetterAlreadySent) return { ok: false, reason: "already_sent" };
    if (!approvalLetterCoverageCurrent) {
      setSendingLetterError('The selected approvals changed. Review the selection and send again; your wording is preserved.');
      return { ok: false };
    }
    if (!caseId) {
      setSendingLetterError("The case is not available for this approval letter.");
      return { ok: false };
    }
    if (!activeLetterWorkflowId) {
      setSendingLetterError("Letter workflow is not configured yet.");
      return { ok: false };
    }
    const parsedInterventionId = Number(requestedInterventionId);
    if (!Number.isInteger(parsedInterventionId) || parsedInterventionId < 1) {
      setSendingLetterError("Select the exact approved intervention before sending its approval letter.");
      return { ok: false, reason: "intervention_id_required" };
    }
    const subject = isApprovedDecisionOutcome
      ? (isRevisionMode
        ? (approvalHasFundingPackage ? "Funding Revision Approval" : "Intervention Change Approval")
        : "Letter of Approval")
      : "";
    const body = String(clientLetterBody || "").trim()
      || (isApprovedDecisionOutcome
        ? (isRevisionMode
          ? (approvalHasFundingPackage
            ? "Please review your funding revision letter in the portal."
            : "Please review your intervention change approval letter in the portal.")
          : "Please review your approval letter in the portal.")
        : "");
    const sendIntent = {
      caseId,
      applicationId: applicationId || null,
      interventionId: parsedInterventionId,
      expectedInterventionApprovalVersion: letterApprovalVersion,
      ...(preparedApprovalCoverage.length ? { coveredApprovals: preparedApprovalCoverage } : {}),
      subject,
      body,
      urgent: false,
      toDisplayName: participantLegalName || applicantSalutationName || "Applicant",
      fromDisplayName: currentUserName || "Case Worker",
      attachments: [{ workflow_id: activeLetterWorkflowId }],
    };
    const sendIntentFingerprint = JSON.stringify(sendIntent);
    const sendAttempt = beginRetainedSecureMessageSendAttempt({
      inFlightRef: decisionLetterSendInFlightRef,
      attemptRef: decisionLetterSendAttemptRef,
      fingerprint: sendIntentFingerprint,
      createPayload: () => ({
        subject: sendIntent.subject,
        body: sendIntent.body,
        urgent: sendIntent.urgent,
        toDisplayName: sendIntent.toDisplayName,
        fromDisplayName: sendIntent.fromDisplayName,
        attachments: sendIntent.attachments,
        clientOperationId: buildUuid(),
        ...(sendIntent.applicationId ? { applicationId: sendIntent.applicationId } : {}),
        interventionId: sendIntent.interventionId,
        expectedInterventionApprovalVersion: sendIntent.expectedInterventionApprovalVersion,
        ...(sendIntent.coveredApprovals ? { coveredApprovals: sendIntent.coveredApprovals } : {}),
      }),
    });
    if (!sendAttempt) {
      return { ok: false, reason: "in_flight" };
    }
    setSendingLetter(true);
    setSendingLetterError(null);
    try {
      const response = await apiFetch(`/api/cases/${caseId}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sendAttempt.payload),
      });
      if (!response.ok) {
        let detail = "";
        try {
          const data = await response.json();
          if (data && typeof data === "object") {
            if (data.error === "funding_forms_workflows_missing" && Array.isArray(data.missing) && data.missing.length) {
              detail = `Required funding-form workflows are missing: ${data.missing.join(", ")}.`;
            } else {
              detail = data.message || data.error || "";
            }
          }
        } catch (_) {
          detail = await response.text().catch(() => "");
        }
        throw new Error(detail || "Failed to send the approval letter.");
      }
      sendAttempt.finish({ committed: true });
      return { ok: true };
    } catch (err) {
      setSendingLetterError(err?.message || "Failed to send the approval letter.");
      return { ok: false, error: err };
    } finally {
      sendAttempt.finish();
      setSendingLetter(false);
    }
  }, [
    activeLetterWorkflowId,
    decisionLetterAlreadySent,
    applicantSalutationName,
    approvalHasFundingPackage,
    applicationId,
    caseId,
    clientLetterBody,
    letterApprovalVersion,
    preparedApprovalCoverage,
    approvalLetterCoverageCurrent,
    currentUserName,
    isApprovedDecisionOutcome,
    isRevisionMode,
    participantLegalName,
  ]);
  const buildSentLetterCompletionNote = useCallback(() => {
    if (isApprovedDecisionOutcome) {
      if (isRevisionMode) {
        if (completionNote?.followUpIssue === "draft_cleanup_failed") {
          return {
            type: "info",
            header: "Client letter sent with follow-up still needed",
            body: approvalHasFundingPackage
              ? `The approved revision was applied to ${revisionSourceTitle}, and the client funding revision letter was sent to the client's secure messages. Remove the leftover temporary revision draft from the interventions table if needed.`
              : `The approved change was applied to ${revisionSourceTitle}, and the client intervention change approval letter was sent to the client's secure messages. Remove the leftover temporary revision draft from the interventions table if needed.`,
            followUpIssue: completionNote.followUpIssue,
            decisionLetterSent: true,
            showDecisionLetters: false,
          };
        }
        return {
          type: "success",
          header: "Revision complete",
          body: approvalHasFundingPackage
            ? `The approved revision was applied to ${revisionSourceTitle}, and the client funding revision letter was sent to the client's secure messages.`
            : `The approved change was applied to ${revisionSourceTitle}, and the client intervention change approval letter was sent to the client's secure messages.`,
          decisionLetterSent: true,
          showDecisionLetters: false,
        };
      }
      return {
        type: "success",
        header: "Intervention workflow complete",
        body: approvalHasFundingPackage
          ? "Approved interventions were created, and the client approval letter was sent to the client's secure messages. Supporting letters remain available below if you need them."
          : "Approved interventions were created, and the client approval letter was sent to the client's secure messages.",
        decisionLetterSent: true,
        showDecisionLetters: approvalHasFundingPackage,
      };
    }
    return completionNote || {
      type: "info",
      header: isRevisionMode ? "Revision closed" : "Proposal closed",
      body: isRevisionMode
        ? `The revision was denied and ${revisionSourceTitle} was left unchanged.`
        : "The proposal was denied and closed.",
      showDecisionLetters: false,
    };
  }, [
    approvalHasFundingPackage,
    completionNote,
    isApprovedDecisionOutcome,
    isRevisionMode,
    revisionSourceTitle,
  ]);
  const handleSendPreparedDecisionLetter = useCallback(async () => {
    if (sendingLetter) return;
    if (isApprovedDecisionOutcome) {
      setSendingLetterError(null);
      try {
        const contexts = await Promise.all(approvalLetterCoverage.map(async selection => {
          const response = await apiFetch(`/api/interventions/${selection.interventionId}/approval-letter-context`);
          if (!response.ok) throw new Error('The selected approval could not be loaded. Refresh its details and try again; your wording is preserved.');
          const context = await response.json();
          if (String(context.caseId) !== String(caseId) || Number(context.interventionId) !== selection.interventionId ||
              !context.approvalVersion) throw new Error('The selected approval context changed. Refresh its details.');
          return { interventionId: selection.interventionId, approvalVersion: context.approvalVersion };
        }));
        if (!contexts.length) throw new Error('Refresh the approved intervention before sending its letter.');
        const changed = contexts.some(context => {
          const selected = approvalLetterCoverage.find(item => item.interventionId === context.interventionId);
          const prepared = preparedApprovalCoverage.find(item => item.interventionId === context.interventionId);
          return selected.approvalVersion !== context.approvalVersion || (prepared && prepared.approvalVersion !== context.approvalVersion);
        });
        setPreparedApprovalCoverage(contexts);
        setLetterApprovalVersion(contexts.find(context => String(context.interventionId) === String(headerRevisionSource.id)).approvalVersion);
        if (changed) {
          if (typeof refresh === 'function') await refresh();
          throw new Error('Approval details have changed. Review the current details and your letter, then send again. Your wording is preserved.');
        }
        setShowSendApprovalLetterConfirmModal(true);
      } catch (error) { setSendingLetterError(error.message); }
      return;
    }
    const result = await handleSendDecisionLetter({
      interventionId:
        isRevisionMode && revisionSourceInterventionId
          ? revisionSourceInterventionId
          : activeInterventionIdValue,
    });
    if (result?.ok) {
      setSendingLetterError(null);
      setCompletionNote(buildSentLetterCompletionNote());
      if (typeof refresh === "function") {
        refresh().catch(() => {});
      }
    }
  }, [
    activeInterventionIdValue,
    approvalLetterCoverage,
    preparedApprovalCoverage,
    headerRevisionSource,
    caseId,
    buildSentLetterCompletionNote,
    handleSendDecisionLetter,
    isApprovedDecisionOutcome,
    isRevisionMode,
    refresh,
    revisionSourceInterventionId,
    sendingLetter,
  ]);
  const handleConfirmSendApprovalLetter = useCallback(async () => {
    if (sendingLetter) return;
    setShowSendApprovalLetterConfirmModal(false);
    const result = await handleSendDecisionLetter({
      interventionId:
        isRevisionMode && revisionSourceInterventionId
          ? revisionSourceInterventionId
          : activeInterventionIdValue,
    });
    if (result?.ok) {
      setSendingLetterError(null);
      setCompletionNote(buildSentLetterCompletionNote());
      if (typeof refresh === "function") {
        refresh().catch(() => {});
      }
    }
  }, [
    activeInterventionIdValue,
    buildSentLetterCompletionNote,
    handleSendDecisionLetter,
    isRevisionMode,
    refresh,
    revisionSourceInterventionId,
    sendingLetter,
  ]);
  useEffect(() => {
    if (isApprovedDecisionOutcome) return;
    setApprovalLetterPackGenerated(false);
    setApprovalLetterPackTabId("client");
    setClientLetterBody("");
    setSendingLetterError(null);
    setShowSendApprovalLetterConfirmModal(false);
  }, [isApprovedDecisionOutcome]);

  const interventionWidgetTitle = "Intervention assessment";
  const headerDescription = completionNote
    ? null
    : canRegionalManagerReview
      ? reviewStage === INTERVENTION_REVIEW_STAGES.returnedToRm
        ? "Review the Decision Maker's note, then forward the requested changes to the submitter."
        : "Review the submitted request, then return it to the submitter or submit it for final decision."
    : isReviewStageStatus && isEditable && isRevisionMode && !canDecideSubmittedProposal
      ? `Update the proposed change to ${revisionSourceTitle}. Final decision is completed by a Decision Maker.`
      : isReviewStageStatus && isEditable && !canDecideSubmittedProposal
        ? "Update the submitted proposal. Final decision is completed by a Decision Maker."
    : isReviewStageStatus && isEditable && isRevisionMode
      ? `Review the proposed change to ${revisionSourceTitle}, and record whether the change is approved, denied, or returned for changes.`
      : isReviewStageStatus && isEditable
        ? "Review the submitted new intervention proposal, and record whether the proposal is approved, denied, or returned for changes."
        : isEditable && isRevisionMode
          ? `Draft a proposed change to ${revisionSourceTitle}. The approved intervention stays unchanged until this change is approved.`
          : isEditable
            ? "Draft a new intervention proposal for this client. Save progress to finish later. Only one draft proposal can exist at a time."
            : statusValue && isRevisionMode
              ? `Viewing the proposed change to ${revisionSourceTitle} in read-only mode.`
              : statusValue
                ? "Viewing this submitted proposal in read-only mode."
    : "Select a draft or proposal in review from the Interventions table to view it here.";

  const infoLink = metadata.helpComponent && toggleHelpPanel ? (
        <Link
          variant="info"
          onFollow={event => {
            event.preventDefault();
            const helpContent = React.createElement(metadata.helpComponent);
            toggleHelpPanel(helpContent, interventionWidgetTitle, metadata.aiContext ?? "");
          }}
        >
          Info
        </Link>
  ) : undefined;

  const planStepContent = (
    <SpaceBetween size="m">
      {isRevisionMode && (
        <Alert type="info">
          This draft revises {revisionSourceTitle}. The approved intervention remains the current record until this revision is approved.
        </Alert>
      )}
      {!planOptions.length ? (
        <Alert type="info">
          No active or draft action plans are available. Create an action plan before proposing interventions.
        </Alert>
      ) : (
        <FormField
          label="Action Plan"
          description={isRevisionMode ? "Choose the action plan this revision belongs to." : "Choose the action plan this proposal belongs to."}
          errorText={showPlanErrors && !form.actionPlanId ? "Action Plan is required." : undefined}
        >
          <Select
            selectedOption={planOptions.find(option => option.value === form.actionPlanId) || null}
            onChange={({ detail }) => {
              const value = detail?.selectedOption?.value || "";
              handleChange("actionPlanId", value);
              if (value) {
                const numericValue = Number(value);
                if (typeof setSelectedActionPlanId === "function") {
                  setSelectedActionPlanId(Number.isFinite(numericValue) ? numericValue : value);
                }
              }
            }}
            options={planOptions}
            placeholder={planOptions.length ? "Select plan" : "No plans available"}
            disabled={isFormLocked || !planOptions.length}
          />
        </FormField>
      )}
      {hasBlockingReviewStage && !isReviewStageStatus && (
        <Alert type="warning">
          A proposal is awaiting review. Resolve it before starting a new proposal.
        </Alert>
      )}
      {hasBlockingDraft && !isDraftStatus && !isChangesRequestedStatus && (
        <Alert type="warning">
          A draft proposal already exists. Resume it from the interventions table.
        </Alert>
      )}
    </SpaceBetween>
  );

  const framingErrors = showFramingErrors ? validationErrors.interventions || {} : {};
  const framingGlobalError = showFramingErrors ? framingErrors._global : null;

  const framingStepContent = (
    <SpaceBetween size="l">
      {isRevisionMode && (
        <Alert type="info">
          This revision updates a single approved intervention. Adding or removing interventions is not available in this flow.
        </Alert>
      )}
      <Table
        key={`proposed-interventions-${proposedInterventionsTableVersion}`}
        stripedRows
        variant="embedded"
        trackBy="id"
        items={proposedInterventions}
        resizableColumns
        columnDefinitions={[
          {
            id: "intervention",
            header: "Intervention",
            cell: item => {
              const entryErrors = framingErrors[item.id] || {};
              return (
                <SpaceBetween size="xxs">
                  <Link
                    onFollow={event => {
                      event.preventDefault();
                      openViewInterventionModal(item.id);
                    }}
                  >
                    {resolveInterventionLabel(item.code) || "—"}
                  </Link>
                  {showFramingErrors && Object.entries(entryErrors).filter(([key]) => !['startDate', 'endDate'].includes(key)).map(([key, message]) => (
                    <Box key={key} color="text-status-error" fontSize="body-s">{message}</Box>
                  ))}
                </SpaceBetween>
              );
            },
          },
          {
            id: "dates",
            header: "Dates",
            minWidth: 140,
            cell: item => {
              const entryErrors = framingErrors[item.id] || {};
              const dateError = entryErrors.startDate || entryErrors.endDate;
              return (
                <SpaceBetween size="xxs">
                  <Box>{formatInterventionDates(item.startDate, item.endDate)}</Box>
                  {showFramingErrors && dateError && (
                    <Box color="text-status-error" fontSize="body-s">
                      {dateError}
                    </Box>
                  )}
                </SpaceBetween>
              );
            },
          },
          {
            id: "actions",
            header: "Actions",
            minWidth: 90,
            width: 90,
            cell: item => (
              <Button
                variant="inline-icon"
                iconName="remove"
                ariaLabel="Delete intervention"
                onClick={() => setInterventionDeleteId(item.id)}
                disabled={isFormLocked || isRevisionMode}
              />
            ),
          },
        ]}
        header={
          <Header
            variant="h3"
            actions={
              <Button onClick={openAddInterventionModal} disabled={isFormLocked || isRevisionMode}>
                Add intervention
              </Button>
            }
          >
            Proposed interventions
          </Header>
        }
        empty={<Box textAlign="center">No proposed interventions.</Box>}
      />
      {framingGlobalError && (
        <Box color="text-status-error" fontSize="body-s">
          {framingGlobalError}
        </Box>
      )}
    </SpaceBetween>
  );

  const rationaleStepContent = (
    <SpaceBetween size="m">
      {narrativeLoading.error && <Alert type="error" action={<Button onClick={narrativeLoading.retry}>Retry</Button>}>{narrativeLoading.error}</Alert>}
      {!narrativeLoading.ready && !narrativeLoading.error && <StatusIndicator type="loading">Loading assessment wording</StatusIndicator>}
      {INTERVENTION_NARRATIVE_FIELDS.map(({ key, label }) => (
        <FormField key={key} label={label}
          description={key === 'recommendationJustification' ? 'Explain why the proposed support should be approved.' : 'Review the prefilled wording and update it to reflect the current proposal.'}
          errorText={showRationaleErrors ? validationErrors[key] : undefined}>
          <Textarea value={form[key]} ariaLabel={label} rows={4} spellcheck={true}
            disabled={isFormLocked || !narrativeLoading.ready}
            onChange={({ detail }) => handleChange(key, detail.value)} />
        </FormField>
      ))}
      {isRevisionMode && (
      <FormField
        label="Reason for this revision"
        description="Explain what has changed since approval and why this revision is needed."
        errorText={showRationaleErrors ? validationErrors.rationale : undefined}
        constraintText={`${form.rationale.split(/\s+/).filter(Boolean).length}/${RATIONALE_WORD_LIMIT} words maximum`}
      >
        <Textarea
          ariaLabel="Reason for this revision"
          value={form.rationale}
          rows={4}
          onChange={({ detail }) => handleChange("rationale", detail.value)}
          placeholder="Describe the change and why it is needed."
          spellcheck={true}
          disabled={isFormLocked}
        />
      </FormField>
      )}
      <FormField label="Barriers to employment (optional)">
        <Multiselect
          options={BARRIER_OPTIONS}
          selectedOptions={form.barriers}
          onChange={({ detail }) => handleChange("barriers", detail.selectedOptions || [])}
          placeholder="Select barriers"
          disabled={isFormLocked}
        />
      </FormField>
    </SpaceBetween>
  );

  const otherFundingSourceItems = useMemo(
    () => normalizeOtherFundingSources(form.otherFundingSources, { keepEmpty: true }),
    [form.otherFundingSources]
  );

  const otherFundingSourceTableColumns = useMemo(
    () => [
      {
        id: "name",
        header: "Funder name",
        minWidth: 180,
        cell: item => item.name || "—",
      },
      {
        id: "type",
        header: "Funder type",
        minWidth: 140,
        cell: item => resolveOtherFunderTypeLabel(item.type),
      },
      {
        id: "status",
        header: "Status",
        minWidth: 150,
        cell: item => resolveOtherFunderStatusLabel(item.status),
      },
      {
        id: "amount",
        header: "Amount",
        minWidth: 120,
        cell: item => formatOtherFundingAmountDisplay(item.amount) || "—",
      },
      {
        id: "coverage",
        header: "What this funder covers",
        minWidth: 260,
        cell: item => (
          <SpaceBetween size="xxs">
            <Box>{item.coverage || "—"}</Box>
            {item.notes ? <Box fontStyle="italic">{item.notes}</Box> : null}
          </SpaceBetween>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        width: 92,
        minWidth: 92,
        cell: item => (
          <SpaceBetween direction="horizontal" size="xxs">
            <Button
              variant="inline-icon"
              iconName="edit"
              ariaLabel={`Edit ${item.name || "other funder"}`}
              onClick={() => openEditOtherFundingSourceModal(item.id)}
              disabled={isFormLocked}
            />
            <Button
              variant="inline-icon"
              iconName="remove"
              ariaLabel={`Delete ${item.name || "other funder"}`}
              onClick={() => removeOtherFundingSource(item.id)}
              disabled={isFormLocked}
            />
          </SpaceBetween>
        ),
      },
    ],
    [isFormLocked, openEditOtherFundingSourceModal, removeOtherFundingSource]
  );

  const otherFundingStepContent = (
    <SpaceBetween size="m">
      <FormField
        label="Other funding involved?"
        description="Identify whether other funding is part of this request."
      >
        <Select
          selectedOption={
            OTHER_FUNDING_INVOLVED_OPTIONS.find(option => option.value === form.otherFundingInvolved) || null
          }
          onChange={({ detail }) => {
            const nextValue = detail.selectedOption?.value || "";
            if (!nextValue || nextValue === "no") {
              resetOtherFundingSourceModal();
            }
            updateOtherFundingFields(prev => {
              const resetValues =
                !nextValue || nextValue === "no"
                  ? {
                    otherFundingSources: [],
                    otherFundingNwacCoverage: "",
                  }
                  : {};
              return {
                ...prev,
                otherFundingInvolved: nextValue,
                ...resetValues,
              };
            });
          }}
          options={OTHER_FUNDING_INVOLVED_OPTIONS}
          placeholder="Select"
          disabled={isFormLocked}
        />
      </FormField>
      {form.otherFundingInvolved && form.otherFundingInvolved !== "no" && (
        <SpaceBetween size="m">
          <Table
            stripedRows
            variant="embedded"
            trackBy="id"
            items={otherFundingSourceItems}
            columnDefinitions={otherFundingSourceTableColumns}
            resizableColumns
            header={
              <Header
                variant="h3"
                actions={
                  <Button onClick={openAddOtherFundingSourceModal} disabled={isFormLocked}>
                    Add other funder
                  </Button>
                }
              >
                Other funders
              </Header>
            }
            empty={
              <Alert type="info">
                Add each non-NWAC funder so coordination is clear.
              </Alert>
            }
          />
          <FormField
            label="What NWAC funding will cover"
            description="Describe the NWAC-funded supports to avoid overlap."
          >
            <Textarea
              value={form.otherFundingNwacCoverage || ""}
              onChange={({ detail }) => updateOtherFundingFields({ otherFundingNwacCoverage: detail.value })}
              rows={3}
              spellcheck={true}
              disabled={isFormLocked}
            />
          </FormField>
        </SpaceBetween>
      )}
      <FormField label="Additional notes (optional)">
        <Textarea
          value={form.otherFundingNotes || ""}
          onChange={({ detail }) => updateOtherFundingFields({ otherFundingNotes: detail.value })}
          rows={3}
          spellcheck={true}
          disabled={isFormLocked}
        />
      </FormField>
    </SpaceBetween>
  );

  const childcareStepContent = (
    <SpaceBetween size="m">
      <FormField label="Childcare need" description="Indicate if childcare is required to participate in the interventions.">
        <Select
          selectedOption={
            form.childcareNeed
              ? { value: form.childcareNeed, label: form.childcareNeed === "yes" ? "Yes" : "No" }
              : null
          }
          onChange={({ detail }) => handleChange("childcareNeed", detail.selectedOption?.value || "")}
          options={[
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" },
          ]}
          placeholder="Select"
          readOnly={isFormLocked}
        />
      </FormField>
      <FormField label="Childcare funding details (optional)">
        <Textarea
          value={form.childcareFunding || ""}
          onChange={({ detail }) => handleChange("childcareFunding", detail.value)}
          rows={3}
          spellcheck={true}
          disabled={isFormLocked || form.childcareNeed !== "yes"}
        />
      </FormField>
    </SpaceBetween>
  );

  const costErrors = showCostErrors ? validationErrors.costLines || {} : {};
  const overallCostDisplay = formatCurrencyDisplay(overallCostTotal) || "$ 0.00";

  const costStepContent = (
    <SpaceBetween size="l">
      <Box fontWeight="bold">Total proposed cost: {overallCostDisplay}</Box>
      {proposedInterventions.map(intervention => {
        const costLines = Array.isArray(intervention.costLines) ? intervention.costLines : [];
        const interventionTotal = interventionTotals.get(intervention.id) || 0;
        const interventionTotalDisplay = formatCurrencyDisplay(interventionTotal) || "$ 0.00";
        const costItemOptions = buildCostItemOptions(intervention);
        const interventionLabel = resolveInterventionLabel(intervention.code) || "Intervention";
        return (
          <SpaceBetween key={intervention.id} size="s">
            <Header
              variant="h3"
              actions={
                <Button
                  onClick={() => openAddCostLineModal(intervention.id)}
                  disabled={isFormLocked || costItemOptions.length === 0}
                >
                  Add cost item
                </Button>
              }
            >
              {interventionLabel}
            </Header>
            <Table
              stripedRows
              variant="embedded"
              trackBy="id"
              items={costLines}
              resizableColumns
              columnDefinitions={[
                {
                  id: "type",
                  header: "Cost item",
                  cell: item => {
                    const lineErrors = costErrors[intervention.id]?.[item.id] || {};
                    const label = paymentTypeLabelLookup.get(item.type) || item.type || "—";
                    return (
                      <SpaceBetween size="xxs">
                        <Link
                          onFollow={event => {
                            event.preventDefault();
                            openCostLineModal(intervention.id, item.id);
                          }}
                        >
                          {label}
                        </Link>
                        {showCostErrors && lineErrors.type && (
                          <Box color="text-status-error" fontSize="body-s">
                            {lineErrors.type}
                          </Box>
                        )}
                      </SpaceBetween>
                    );
                  },
                },
                {
                  id: "amount",
                  header: "Amount",
                  cell: item => {
                    const lineErrors = costErrors[intervention.id]?.[item.id] || {};
                    const displayValue = inlineAmountEditingId === item.id
                      ? sanitizeCurrencyInput(item.amount)
                      : getCurrencyInputDisplayValue(parseCurrencyInput(item.amount) ?? "", false);
                    return (
                      <SpaceBetween size="xxs">
                        <Input
                          inputMode="decimal"
                          value={displayValue}
                          onFocus={() => {
                            if (!isFormLocked) setInlineAmountEditingId(item.id);
                          }}
                          onChange={({ detail }) => handleInlineAmountChange(intervention.id, item.id, detail.value)}
                          onBlur={() => handleInlineAmountBlur(item.id)}
                          placeholder="0.00"
                          spellcheck={false}
                          readOnly={isFormLocked}
                        />
                        {showCostErrors && lineErrors.amount && (
                          <Box color="text-status-error" fontSize="body-s">
                            {lineErrors.amount}
                          </Box>
                        )}
                      </SpaceBetween>
                    );
                  },
                },
                {
                  id: "details",
                  header: "Details",
                  cell: item => {
                    const lineErrors = costErrors[intervention.id]?.[item.id] || {};
                    const details = getCostLineDetailsText(item, intervention);
                    return (
                      <SpaceBetween size="xxs">
                        <Box>{details.text}</Box>
                        {details.notesText && (
                          <Box fontStyle="italic">{details.notesText}</Box>
                        )}
                        {showCostErrors && lineErrors.recurrence && (
                          <Box color="text-status-error" fontSize="body-s">
                            {lineErrors.recurrence}
                          </Box>
                        )}
                      </SpaceBetween>
                    );
                  },
                },
                {
                  id: "actions",
                  header: "",
                  minWidth: 64,
                  width: 64,
                  cell: item => (
                    isFormLocked ? null : (
                      <Button
                        variant="inline-icon"
                        iconName="remove"
                        ariaLabel="Delete cost item"
                        onClick={() => removeCostLine(intervention.id, item.id)}
                      />
                    )
                  ),
                },
              ]}
              empty={<Box padding={{ vertical: "s" }}>Intervention has no cost items.</Box>}
              footer={
                <Box textAlign="right" fontWeight="bold">
                  TOTAL: {interventionTotalDisplay}
                </Box>
              }
            />
          </SpaceBetween>
        );
      })}
    </SpaceBetween>
  );

  const docsStepContent = (
    <SpaceBetween size="m">
      <Alert type="info">No checklist items are required yet.</Alert>
    </SpaceBetween>
  );

  const reviewOtherFundingInvolved =
    form.otherFundingInvolved === "yes"
      ? "Yes"
      : form.otherFundingInvolved === "no"
        ? "No"
        : form.otherFundingInvolved === "unknown"
          ? "Unknown"
          : "";
  const reviewOtherFundingSources = normalizeOtherFundingSources(form.otherFundingSources);
  const reviewOtherFundingNotes = String(form.otherFundingNotes || "").trim();
  const reviewOtherFundingNwacCoverage = String(form.otherFundingNwacCoverage || "").trim();
  const reviewWorkflowNotice = (() => {
    if (!twoStepReviewEnabled || !hasReviewWorkflow) return null;
    if (reviewStage === INTERVENTION_REVIEW_STAGES.nwacReview) {
      return {
        key: `intervention-${reviewStage}`,
        type: "info",
        header: "Ready for Decision Maker",
        content: `${reviewAttribution.reviewerName ? `Regional Manager ${reviewAttribution.reviewerName}` : 'The Regional Manager'} has reviewed this request and submitted it for final decision.`,
        dismissible: true,
      };
    }
    if (reviewStage === INTERVENTION_REVIEW_STAGES.rmReview && !canRegionalManagerReview) {
      return {
        key: `intervention-${reviewStage}`,
        type: "info",
        header: "Waiting for Regional Manager review",
        content: "The Regional Manager reviews this request before the Decision Maker records the final decision.",
        dismissible: true,
      };
    }
    if (reviewStage === INTERVENTION_REVIEW_STAGES.returnedToRm && !canRegionalManagerReview) {
      return {
        key: `intervention-${reviewStage}`,
        type: "warning",
        header: "Decision Maker requested changes",
        content: "The request is back with the Regional Manager before it returns to the submitter.",
        dismissible: false,
      };
    }
    return null;
  })();
  const reviewWorkflowStageAlert =
    reviewWorkflowNotice && dismissedReviewNoticeKey !== reviewWorkflowNotice.key ? (
      <Alert
        type={reviewWorkflowNotice.type}
        header={reviewWorkflowNotice.header}
        dismissible={reviewWorkflowNotice.dismissible}
        onDismiss={
          reviewWorkflowNotice.dismissible
            ? () => setDismissedReviewNoticeKey(reviewWorkflowNotice.key)
            : undefined
        }
      >
        {reviewWorkflowNotice.content}
      </Alert>
    ) : null;
  const regionalManagerReviewContent = canRegionalManagerReview ? (
    <SpaceBetween size="s">
      {reviewStage === INTERVENTION_REVIEW_STAGES.returnedToRm && nwacDecisionNote ? (
        <Alert type="warning" header="Decision Maker requested changes">
          <Box whiteSpace="pre-wrap">{nwacDecisionNote}</Box>
        </Alert>
      ) : null}
      {rmReviewNote ? (
        <Box variant="small" color="text-body-secondary">
          {attributedLabel('Latest Regional Manager note', reviewAttribution.reviewerName)}: {rmReviewNote}
        </Box>
      ) : null}
      <FormField
        label={
          reviewStage === INTERVENTION_REVIEW_STAGES.returnedToRm
            ? "Note to submitter"
            : "Review note"
        }
        description={
          reviewStage === INTERVENTION_REVIEW_STAGES.returnedToRm
            ? "Required when forwarding the Decision Maker's requested changes."
            : "Required when returning this request to the submitter."
        }
      >
        <Textarea
          value={reviewWorkflowNote}
          onChange={({ detail }) => setReviewWorkflowNote(detail.value)}
          rows={3}
          placeholder={
            reviewStage === INTERVENTION_REVIEW_STAGES.returnedToRm
              ? "Add any Regional Manager context before forwarding the requested changes."
              : "Explain what the submitter needs to change, if returning the request."
          }
          spellcheck={true}
          disabled={Boolean(reviewWorkflowActionLoading)}
        />
      </FormField>
      <SpaceBetween size="s" direction="horizontal">
        {reviewStage === INTERVENTION_REVIEW_STAGES.returnedToRm ? (
          <Button
            variant="primary"
            onClick={() => handleReviewWorkflowAction(INTERVENTION_REVIEW_ACTIONS.rmForwardChangesToSubmitter)}
            loading={reviewWorkflowActionLoading === INTERVENTION_REVIEW_ACTIONS.rmForwardChangesToSubmitter}
            disabled={Boolean(reviewWorkflowActionLoading)}
          >
            Forward changes to submitter
          </Button>
        ) : (
          <>
            <Button
              onClick={() => handleReviewWorkflowAction(INTERVENTION_REVIEW_ACTIONS.rmReturnToSubmitter)}
              loading={reviewWorkflowActionLoading === INTERVENTION_REVIEW_ACTIONS.rmReturnToSubmitter}
              disabled={Boolean(reviewWorkflowActionLoading)}
            >
              Return to submitter
            </Button>
            <Button
              variant="primary"
              onClick={() => handleReviewWorkflowAction(INTERVENTION_REVIEW_ACTIONS.rmSubmitToNwac)}
              loading={reviewWorkflowActionLoading === INTERVENTION_REVIEW_ACTIONS.rmSubmitToNwac}
              disabled={Boolean(reviewWorkflowActionLoading)}
            >
              Submit for final decision
            </Button>
          </>
        )}
      </SpaceBetween>
    </SpaceBetween>
  ) : null;

  const reviewStepContent = (
    <SpaceBetween size="m">
      {reviewWorkflowStageAlert}
      {regionalManagerReviewContent}
      <ColumnLayout columns={2} variant="text-grid">
        <Box>
          {INTERVENTION_NARRATIVE_FIELDS.map(({ key, label }) => <React.Fragment key={key}>
            <Header variant="h4">{label}</Header><div style={{ whiteSpace: 'pre-wrap' }}>{form[key] || '—'}</div>
          </React.Fragment>)}
          {isRevisionMode && <><Header variant="h4">Reason for this revision</Header><div>{form.rationale || '—'}</div></>}
          <div>Barriers: {form.barriers.length ? form.barriers.map(item => item.label || item.value).join(", ") : "None"}</div>
        </Box>
        <Box>
          <Header variant="h4">Other funding</Header>
          {reviewOtherFundingInvolved ? (
            <div>Other funding involved: {reviewOtherFundingInvolved}</div>
          ) : null}
          {reviewOtherFundingSources.length ? (
            <SpaceBetween size="xxs">
              {reviewOtherFundingSources.map((source, index) => (
                <div key={source.id || `${source.name}-${index}`}>
                  {resolveOtherFunderTypeLabel(source.type)}: {source.name || "Unnamed funder"} ({resolveOtherFunderStatusLabel(source.status)})
                  {formatOtherFundingAmountDisplay(source.amount) ? ` — ${formatOtherFundingAmountDisplay(source.amount)}` : ""}
                  {source.coverage ? ` — ${source.coverage}` : ""}
                  {source.notes ? ` — ${source.notes}` : ""}
                </div>
              ))}
            </SpaceBetween>
          ) : null}
          {reviewOtherFundingNwacCoverage ? (
            <div>NWAC funding covers: {reviewOtherFundingNwacCoverage}</div>
          ) : null}
          {reviewOtherFundingNotes ? (
            <div>Notes: {reviewOtherFundingNotes}</div>
          ) : null}
          {!reviewOtherFundingInvolved &&
            !reviewOtherFundingSources.length &&
            !reviewOtherFundingNwacCoverage &&
            !reviewOtherFundingNotes && <div>—</div>}
        </Box>
        <Box>
          <Header variant="h4">Proposed interventions</Header>
          {proposedInterventions.length === 0 ? (
            <div>—</div>
          ) : (
            <SpaceBetween size="s">
              {proposedInterventions.map(intervention => (
                <Box key={intervention.id}>
                  <Box fontWeight="bold">{resolveInterventionLabel(intervention.code) || "Intervention"}</Box>
                  {intervention.startDate ? <div>Start: {intervention.startDate}</div> : null}
                  {intervention.endDate ? <div>End: {intervention.endDate}</div> : null}
                  {intervention.institution ? <div>Provider: {intervention.institution}</div> : null}
                </Box>
              ))}
            </SpaceBetween>
          )}
        </Box>
        <Box>
          <Header variant="h4">Costs</Header>
          <div>Overall proposed cost: {overallCostDisplay}</div>
        </Box>
      </ColumnLayout>
    </SpaceBetween>
  );

  const inferredCaseManagerRecommendation = isRevisionMode
    ? "Recommend approving this proposed intervention change"
    : "Recommend approving this new intervention proposal";
  const decisionNoteLabel = form.decisionOutcome === "approved"
    ? "Approval note (optional)"
    : form.decisionOutcome === "changes_requested"
    ? `Changes requested for this ${decisionSubjectLabel}`
    : `Reason for denying this ${decisionSubjectLabel}`;
  const decisionNoteErrorText = form.decisionOutcome === "changes_requested"
    ? "A request-changes note is required."
    : "A denial reason is required.";
  const decisionNotePlaceholder = form.decisionOutcome === "approved"
    ? `Explain the approval of ${decisionSubjectWithArticle} to the caseworker.`
    : form.decisionOutcome === "changes_requested"
    ? `Explain what must change before ${decisionSubjectWithArticle} can be resubmitted.`
    : `Explain why ${decisionSubjectWithArticle} is being denied.`;
  const caseManagerRecommendationSummary = (
    <SpaceBetween size="xs">
      <Header variant="h3">Proposal recommendation</Header>
      <ColumnLayout columns={2} variant="text-grid" minColumnWidth={260}>
        <Box>
          <Box fontWeight="bold">
            Recommendation
          </Box>
          <div>{inferredCaseManagerRecommendation}</div>
        </Box>
        <Box>
          <Box fontWeight="bold">Rationale</Box>
          <div style={{ whiteSpace: "pre-wrap" }}>
            {String(form.recommendationJustification || "").trim() || "No justification recorded."}
          </div>
        </Box>
      </ColumnLayout>
    </SpaceBetween>
  );
  const regionalManagerDecisionSummary =
    twoStepReviewEnabled && hasReviewWorkflow && !isReviewWithRegionalManager ? (
      <SpaceBetween size="xs">
        <Box fontWeight="bold">Reviewer&apos;s note</Box>
        <Box whiteSpace="pre-wrap">{rmReviewNote || "No Regional Manager note recorded."}</Box>
      </SpaceBetween>
    ) : null;
  const submitterChangeInstructionsAlert =
    twoStepReviewEnabled && reviewStage === INTERVENTION_REVIEW_STAGES.returnedToSubmitter ? (
      <Alert type="warning" header="Changes requested" statusIconAriaLabel="Warning">
        <SpaceBetween size="s">
          <Box>
            Review the notes below, update the {isRevisionMode ? "change request" : "proposal"}, then resubmit it for review when ready.
          </Box>
          {nwacDecisionNote ? (
            <Box>
              <Box fontWeight="bold">{attributedLabel('Decision Maker note', reviewAttribution.decisionMakerName)}</Box>
              <Box whiteSpace="pre-wrap">{nwacDecisionNote}</Box>
            </Box>
          ) : null}
          {rmReviewNote ? (
            <Box>
              <Box fontWeight="bold">{attributedLabel('Regional Manager note', reviewAttribution.reviewerName)}</Box>
              <Box whiteSpace="pre-wrap">{rmReviewNote}</Box>
            </Box>
          ) : null}
          {!nwacDecisionNote && !rmReviewNote ? (
            <Box>No detailed change note was recorded. Contact the Regional Manager before resubmitting.</Box>
          ) : null}
        </SpaceBetween>
      </Alert>
    ) : null;

  const decisionStepContent = (
    <SpaceBetween size="m">
      {caseManagerRecommendationSummary}
      {regionalManagerDecisionSummary}
      {reviewAmendmentDeltaSummary ? (
        <Box variant="small" color="text-body-secondary">
          {reviewAmendmentDeltaSummary}
        </Box>
      ) : null}
      <Header variant="h3">Record of Decision</Header>
      {highValueFundingApprovalMessage ? (
        <Alert type="warning" header="Shelley approval required">
          {highValueFundingApprovalMessage}
        </Alert>
      ) : null}
      <FormField
        label={`Decision on ${decisionSubjectWithArticle}`}
        description={`Choose whether to approve ${decisionSubjectWithArticle}, deny it, or request changes from the case manager.`}
        errorText={showDecisionErrors && !form.decisionOutcome ? `Decision on ${decisionSubjectWithArticle} is required.` : undefined}
      >
        <Select
          selectedOption={decisionOptionsForApproval.find(option => option.value === form.decisionOutcome) || null}
          onChange={({ detail }) => {
            const nextOutcome = detail.selectedOption?.value || "";
            if (nextOutcome === "approved" && highValueFundingApprovalMessage) {
              setError(highValueFundingApprovalMessage);
              return;
            }
            handleChange("decisionOutcome", nextOutcome);
          }}
          options={decisionOptionsForApproval}
          placeholder="Select decision outcome"
          readOnly={isDecisionReadOnly}
        />
      </FormField>
      {["approved", "changes_requested", "rejected"].includes(form.decisionOutcome) && (
        <FormField
          label={decisionNoteLabel}
          stretch
          description={form.decisionOutcome === "approved" ? "For the caseworker; not included in the client letter." : undefined}
          errorText={
            showDecisionErrors && form.decisionOutcome !== "approved" && !form.decisionNotes.trim()
              ? decisionNoteErrorText
              : undefined
          }
        >
          <Textarea
            value={form.decisionNotes}
            onChange={({ detail }) => handleChange("decisionNotes", detail.value)}
            rows={4}
            placeholder={decisionNotePlaceholder}
            spellcheck={true}
            disabled={isDecisionReadOnly}
          />
        </FormField>
      )}
      {form.decisionOutcome === "approved" && (
        <Box variant="small" color="text-body-secondary">
          Action Plan classification: {resolveInterventionReviewEiEligibility({
            actionPlanEiClaimant: selectedPlan?.eiClaimant,
          }) || "Not recorded"}. Funding stream: {selectedPlanFundingStream || "Not recorded"}.
        </Box>
      )}
    </SpaceBetween>
  );

  const renderEditableLetters = (letters, emptyMessage, onBodyChange, options = {}) => {
    if (options.requireDraftGeneration && !approvalLetterPackGenerated && !decisionLetterAlreadySent) {
      return (
        <Alert type="info" statusIconAriaLabel="Info">
          Click <strong>Generate drafts</strong> to create the approval letter pack.
        </Alert>
      );
    }
    if (!Array.isArray(letters) || !letters.length) {
      return (
        <Alert type="info" statusIconAriaLabel="Info">
          {emptyMessage}
        </Alert>
      );
    }
    return (
      <SpaceBetween size="l">
        {letters.map((letter, letterIndex) => (
          <Box key={letter.id || letter.title}>
            <Header variant="h4">
              {letter.title}
            </Header>
            <LetterEditor
              label={letter.title || "Letter"}
              value={letter.body || ""}
              onChange={({ detail }) => onBodyChange(letter, detail.value || "", letterIndex)}
              rows={14}
            />
          </Box>
        ))}
      </SpaceBetween>
    );
  };

  const communicationStepContent = (
    <SpaceBetween size="m">
      <Box>
        <Header
          variant="h3"
          actions={!decisionLetterAlreadySent && (
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                onClick={generateLetterPackDrafts}
                disabled={!canGenerateLetterDrafts || letterWorkflowsLoading || decisionLetterAlreadySent}
                loading={false}
                iconAlign="left"
                iconName="gen-ai"
              >
                Generate drafts
              </Button>
              <Button
                variant="primary"
                onClick={handleSendPreparedDecisionLetter}
                disabled={
                  !activeLetterWorkflowId ||
                  sendingLetter ||
                  decisionLetterAlreadySent ||
                  (!approvalLetterPackGenerated && !String(clientLetterBody || "").trim())
                }
                loading={sendingLetter}
              >
                {isRevisionMode
                  ? approvalHasFundingPackage
                    ? "Send client funding revision letter"
                    : "Send client intervention change approval letter"
                  : "Send client approval letter"}
              </Button>
            </SpaceBetween>
          )}
        >
          {isRevisionMode
            ? approvalHasFundingPackage
              ? "Funding revision letters"
              : "Intervention change approval letter"
            : "Approval letters"}
        </Header>
        {letterWorkflowsError && (
          <Alert type="error" statusIconAriaLabel="Error" dismissible onDismiss={() => setLetterWorkflowsError(null)}>
            {letterWorkflowsError}
          </Alert>
        )}
        {sendingLetterError && (
          <Alert type="error" statusIconAriaLabel="Error" dismissible onDismiss={() => setSendingLetterError(null)}>
            {sendingLetterError}
          </Alert>
        )}
        {isApprovedDecisionOutcome ? (
          <SpaceBetween size="m">
            {additionalLetterOptions.length > 0 && !decisionLetterAlreadySent && (
              <FormField label="Include other approved interventions" description="Choose the interventions covered by this letter. You decide the wording; changing the selection preserves your text.">
                <Multiselect
                  options={additionalLetterOptions}
                  selectedOptions={selectedAdditionalLetters}
                  disabled={sendingLetter}
                  onChange={({ detail }) => {
                    setAdditionalLetterSelections({ primaryId: headerRevisionSource.id, approvalVersion: headerRevisionSource.approvalVersion, ids: detail.selectedOptions.map(option => option.value) });
                  }}
                  deselectAriaLabel={option => `Remove ${option.label}`}
                  placeholder="Select additional approved interventions"
                />
              </FormField>
            )}
            {!completionNote && form.decisionNotes?.trim() && (
              <FormField label="Approval note from the Decision Maker" stretch>
                <Box whiteSpace="pre-wrap">{form.decisionNotes}</Box>
              </FormField>
            )}
            {approvalHasFundingPackage ? (
              <Tabs
                activeTabId={approvalLetterPackTabId}
                onChange={({ detail }) => setApprovalLetterPackTabId(detail.activeTabId)}
                tabs={[
                  {
                    id: "client",
                    label: "Client letter",
                    content: (
                      <LetterEditor
                label="Client letter"
                        readOnly={decisionLetterAlreadySent}
                value={clientLetterBody}
                        onChange={({ detail }) => setClientLetterBody(detail.value || "")}
                        rows={18}
                      />
                    ),
                  },
                  {
                    id: "institution",
                    label: "Institution letter",
                    content: renderEditableLetters(
                      institutionApprovalLettersForDisplay,
                      "No institution-directed funding was identified from intervention delivery details and cost lines.",
                      (letter, value, index) => updateEditableLetterBody(setEditableInstitutionApprovalLetters, letter, value, index),
                      { requireDraftGeneration: true }
                    ),
                  },
                  {
                    id: "loan-provider",
                    label: "Loan provider letters",
                    content: renderEditableLetters(
                      loanProviderApprovalLettersForDisplay,
                      "No student loan repayment lines were identified in the approved cost items.",
                      (letter, value, index) => updateEditableLetterBody(setEditableLoanProviderApprovalLetters, letter, value, index),
                      { requireDraftGeneration: true }
                    ),
                  },
                  {
                    id: "other-funding",
                    label: "Letters to other funders",
                    content: renderEditableLetters(
                      coFunderApprovalLettersForDisplay,
                      "No other funding sources were provided in the Other funding sources step.",
                      (letter, value, index) => updateEditableLetterBody(setEditableCoFunderApprovalLetters, letter, value, index),
                      { requireDraftGeneration: true }
                    ),
                  },
                ]}
              />
            ) : (
              <LetterEditor
                label="Client letter"
                        readOnly={decisionLetterAlreadySent}
                value={clientLetterBody}
                onChange={({ detail }) => setClientLetterBody(detail.value || "")}
                rows={18}
              />
            )}
          </SpaceBetween>
        ) : (
          <LetterEditor
            label="Client letter"
                        readOnly={decisionLetterAlreadySent}
            value={clientLetterBody}
            onChange={({ detail }) => setClientLetterBody(detail.value || "")}
            rows={18}
          />
        )}
      </Box>
    </SpaceBetween>
  );

  const sendApprovalLetterConfirmModal = (
    <Modal
      visible={showSendApprovalLetterConfirmModal}
      onDismiss={() => {
        if (sendingLetter) return;
        setShowSendApprovalLetterConfirmModal(false);
      }}
      header={isRevisionMode
        ? approvalHasFundingPackage
          ? "Send client funding revision letter?"
          : "Send client intervention change approval letter?"
        : "Send client approval letter?"}
      footer={
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            variant="primary"
            onClick={handleConfirmSendApprovalLetter}
            loading={sendingLetter}
            disabled={sendingLetter}
          >
            {isRevisionMode
              ? approvalHasFundingPackage
                ? "Send client funding revision letter"
                : "Send client intervention change approval letter"
              : "Send client approval letter"}
          </Button>
          <Button
            variant="normal"
            onClick={() => setShowSendApprovalLetterConfirmModal(false)}
            disabled={sendingLetter}
          >
            Cancel
          </Button>
        </SpaceBetween>
      }
    >
      <SpaceBetween size="s">
        <Box>
          {`This letter covers: ${[headerRevisionSource?.title || 'the selected intervention', ...selectedAdditionalLetters.map(option => option.label)].join('; ')}. `}
          {approvalRemovesFunding
            ? "This will send the client letter and a revised Client Funding Agreement recording the removed funding. No Banking Details form will be attached."
            : approvalHasFundingPackage
            ? isRevisionMode
              ? "This will send the client funding revision letter with the red-line revised Client Funding Agreement and EFT & Wire Transfer Direct Debit form attached."
              : "This will send the client approval letter with the Client Funding Agreement and EFT & Wire Transfer Direct Debit form attached."
            : isRevisionMode
              ? "This will send the client intervention change approval letter. No Client Funding Agreement or funding forms will be attached because the approved change does not include funded cost lines."
              : "This will send the client approval letter. No Client Funding Agreement or funding forms will be attached because the approved intervention does not include funded cost lines."}
        </Box>
        {approvalHasFundingPackage ? (
          <Box>
            Institution letters and letters to other funders are not sent automatically by the
            system and should be sent manually.
          </Box>
        ) : null}
      </SpaceBetween>
    </Modal>
  );

  const decisionBlockerModal = (
    <Modal
      visible={decisionBlockerVisible}
      onDismiss={() => setDecisionBlockerVisible(false)}
      header="Cannot submit decision"
      footer={
        <SpaceBetween direction="horizontal" size="xs">
          {decisionBlockerTargetStep && (
            <Button
              variant="primary"
              onClick={() => {
                setDecisionBlockerVisible(false);
                setCurrentStep(decisionBlockerTargetStep);
              }}
            >
              Go to step
            </Button>
          )}
          <Button variant="normal" onClick={() => setDecisionBlockerVisible(false)}>Close</Button>
        </SpaceBetween>
      }
    >
      <SpaceBetween size="s">
        {decisionBlockerReasons.map(reason => (
          <Box key={reason}>{reason}</Box>
        ))}
      </SpaceBetween>
    </Modal>
  );

  const otherFundingSourceModalDraft = otherFundingSourceModal.draft
    ? buildEmptyOtherFundingSource(otherFundingSourceModal.draft)
    : null;
  const otherFundingSourceModalDirty =
    otherFundingSourceModal.mode === "edit"
      ? JSON.stringify(otherFundingSourceModalDraft || {}) !==
        JSON.stringify(otherFundingSourceModal.original || {})
      : true;

  const otherFundingSourceModalContent = (
    <Modal
      visible={otherFundingSourceModal.visible}
      onDismiss={resetOtherFundingSourceModal}
      header={otherFundingSourceModal.mode === "add" ? "Add other funder" : "Edit other funder"}
      footer={
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            variant="primary"
            onClick={saveOtherFundingSourceModal}
            disabled={isFormLocked || (otherFundingSourceModal.mode === "edit" && !otherFundingSourceModalDirty)}
          >
            {otherFundingSourceModal.mode === "add" ? "Add funder" : "Save changes"}
          </Button>
          <Button variant="link" onClick={resetOtherFundingSourceModal}>Cancel</Button>
        </SpaceBetween>
      }
    >
      {otherFundingSourceModalDraft && (
        <SpaceBetween size="s">
          <FormField label="Funder name" errorText={otherFundingSourceModalErrors.name}>
            <Input
              value={otherFundingSourceModalDraft.name || ""}
              onChange={({ detail }) => {
                updateOtherFundingSourceModalDraft({ name: detail.value });
                setOtherFundingSourceModalErrors(prev => {
                  const next = { ...prev };
                  delete next.name;
                  return next;
                });
              }}
              spellcheck={false}
              readOnly={isFormLocked}
            />
          </FormField>
          <FormField label="Funder type">
            <Select
              selectedOption={
                OTHER_FUNDER_TYPE_OPTIONS.find(option => option.value === otherFundingSourceModalDraft.type) ||
                OTHER_FUNDER_TYPE_OPTIONS.find(option => option.value === "other") ||
                OTHER_FUNDER_TYPE_OPTIONS[0]
              }
              onChange={({ detail }) => {
                updateOtherFundingSourceModalDraft({ type: detail.selectedOption?.value || "other" });
              }}
              options={OTHER_FUNDER_TYPE_OPTIONS}
              placeholder="Select funder type"
              readOnly={isFormLocked}
            />
          </FormField>
          <FormField label="Funding status">
            <Select
              selectedOption={
                OTHER_FUNDER_STATUS_OPTIONS.find(option => option.value === otherFundingSourceModalDraft.status) ||
                OTHER_FUNDER_STATUS_OPTIONS.find(option => option.value === OTHER_FUNDER_STATUS_CONFIRMED)
              }
              onChange={({ detail }) => {
                updateOtherFundingSourceModalDraft({ status: detail.selectedOption?.value || OTHER_FUNDER_STATUS_CONFIRMED });
                setOtherFundingSourceModalErrors(prev => {
                  const next = { ...prev };
                  delete next.coverage;
                  return next;
                });
              }}
              options={OTHER_FUNDER_STATUS_OPTIONS}
              placeholder="Select funding status"
              readOnly={isFormLocked}
            />
          </FormField>
          <FormField
            label="Amount (optional)"
            description="Leave blank if the amount is not known or not applicable."
            errorText={otherFundingSourceModalErrors.amount}
          >
            <Input
              inputMode="decimal"
              value={otherFundingSourceModalDraft.amount || ""}
              onChange={({ detail }) => {
                updateOtherFundingSourceModalDraft({ amount: detail.value });
                setOtherFundingSourceModalErrors(prev => {
                  const next = { ...prev };
                  delete next.amount;
                  return next;
                });
              }}
              placeholder="0.00"
              spellcheck={false}
              readOnly={isFormLocked}
            />
          </FormField>
          <FormField
            label="What this funder covers"
            description="Required only when funding is confirmed."
            errorText={otherFundingSourceModalErrors.coverage}
          >
            <Textarea
              value={otherFundingSourceModalDraft.coverage || ""}
              rows={4}
              onChange={({ detail }) => {
                updateOtherFundingSourceModalDraft({ coverage: detail.value });
                setOtherFundingSourceModalErrors(prev => {
                  const next = { ...prev };
                  delete next.coverage;
                  return next;
                });
              }}
              spellcheck={true}
              readOnly={isFormLocked}
            />
          </FormField>
          <FormField label="Funder notes (optional)">
            <Textarea
              value={otherFundingSourceModalDraft.notes || ""}
              rows={3}
              onChange={({ detail }) => {
                updateOtherFundingSourceModalDraft({ notes: detail.value });
              }}
              spellcheck={true}
              readOnly={isFormLocked}
            />
          </FormField>
        </SpaceBetween>
      )}
    </Modal>
  );

  const interventionModalDraft = interventionModal.draft || null;
  const interventionModalMode = interventionModal.mode;
  const interventionModalEditable = interventionModalMode === "add" || interventionModalMode === "edit";
  const interventionModalDirty =
    interventionModalMode === "edit"
      ? JSON.stringify(interventionModalDraft || {}) !== JSON.stringify(interventionModal.original || {})
      : true;
  const interventionCodeLabel = interventionModalDraft
    ? resolveInterventionLabel(interventionModalDraft.code) || interventionModalDraft.code || ""
    : "";
  const interventionModalEducationCode = interventionModalDraft
    ? isEducationCode(interventionModalDraft.code)
    : false;
  const interventionModalEmployerCode = interventionModalDraft
    ? isEmployerCode(interventionModalDraft.code)
    : false;
  const interventionModalWageSubsidyCode = interventionModalDraft
    ? isWageSubsidyCode(interventionModalDraft.code)
    : false;
  const interventionModalNeedsNoc = interventionModalDraft
    ? requiresNocForCode(interventionModalDraft.code)
    : false;
  const interventionModalRequiresExternal = interventionModalDraft
    ? requiresExternalPartnerForCode(interventionModalDraft.code)
    : false;
  const interventionModalDeliveryMode =
    interventionModalDraft?.deliveryMode === "in_house" ? "in_house" : "partner";

  const interventionModalContent = (
    <Modal
      visible={interventionModal.visible}
      onDismiss={resetInterventionModal}
      header={interventionModalMode === "add" ? "Add intervention" : "Intervention details"}
      footer={
        interventionModalMode === "view" ? (
          <SpaceBetween direction="horizontal" size="xs">
            {!isFormLocked && (
              <Button variant="primary" onClick={startInterventionEdit}>Edit</Button>
            )}
            {!isFormLocked && (
              <Button
                variant="normal"
                onClick={() => {
                  if (!interventionModal.interventionId) return;
                  setInterventionDeleteId(interventionModal.interventionId);
                  resetInterventionModal();
                }}
              >
                Delete
              </Button>
            )}
            <Button variant="link" onClick={resetInterventionModal}>Close</Button>
          </SpaceBetween>
        ) : (
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="primary"
              onClick={saveInterventionModal}
              disabled={isFormLocked || (interventionModalMode === "edit" && !interventionModalDirty)}
            >
              {interventionModalMode === "add" ? "Add intervention" : "Save changes"}
            </Button>
            <Button variant="link" onClick={interventionModalMode === "add" ? resetInterventionModal : cancelInterventionEdit}>
              Cancel
            </Button>
          </SpaceBetween>
        )
      }
    >
      {interventionModalDraft && (
        <SpaceBetween size="s">
          <FormField
            label="Intervention code"
            description={
              interventionModalMode !== "add"
                ? "To change the code, delete this intervention and add a new one."
                : undefined
            }
            errorText={interventionModalErrors.code}
          >
            {interventionModalMode === "add" ? (
              <Select
                selectedOption={codeOptions.find(option => String(option.value) === String(interventionModalDraft.code)) || null}
                onChange={({ detail }) => {
                  updateInterventionModalDraft({ code: detail.selectedOption?.value || "" });
                  setInterventionModalErrors({});
                }}
                options={codeOptions}
                placeholder={interventionCodesLoading ? "Loading intervention codes" : "Select intervention"}
                statusType={interventionCodesLoading ? "loading" : "finished"}
                readOnly={isFormLocked}
              />
            ) : (
              <Input value={interventionCodeLabel} readOnly />
            )}
          </FormField>
          <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
            <FormField label="Start date" errorText={interventionModalErrors.startDate}>
              <DatePicker
                value={interventionModalDraft.startDate || ""}
                onChange={({ detail }) => {
                  updateInterventionModalDraft({ startDate: detail.value });
                  setInterventionModalErrors(prev => {
                    const next = { ...prev };
                    delete next.startDate;
                    delete next.endDate;
                    return next;
                  });
                }}
                readOnly={!interventionModalEditable || isFormLocked}
              />
            </FormField>
            <FormField label="End date" errorText={interventionModalErrors.endDate}>
              <DatePicker
                value={interventionModalDraft.endDate || ""}
                onChange={({ detail }) => {
                  updateInterventionModalDraft({ endDate: detail.value });
                  setInterventionModalErrors(prev => {
                    const next = { ...prev };
                    delete next.endDate;
                    return next;
                  });
                }}
                readOnly={!interventionModalEditable || isFormLocked}
              />
            </FormField>
          </Grid>
          {!interventionModalRequiresExternal && (
            <ColumnLayout columns={2} variant="text-grid">
              <FormField label="Delivery mode" description="Choose how this will run.">
                <Select
                  selectedOption={
                    interventionModalDeliveryMode === "in_house"
                      ? { value: "in_house", label: "In-house (no external partner)" }
                      : { value: "partner", label: "External delivery partner" }
                  }
                  onChange={({ detail }) => {
                    updateInterventionModalDraft({ deliveryMode: detail.selectedOption?.value || "partner" });
                    setInterventionModalErrors(prev => {
                      const next = { ...prev };
                      delete next.institution;
                      return next;
                    });
                  }}
                  options={[
                    { value: "partner", label: "External delivery partner" },
                    { value: "in_house", label: "In-house (no external partner)" },
                  ]}
                  disabled={!interventionModalEditable || isFormLocked}
                />
              </FormField>
              {interventionModalDeliveryMode === "partner" ? (
                <FormField
                  label="Delivery partner / provider"
                  description="The training provider or employer."
                  errorText={interventionModalErrors.institution}
                >
                  <Input
                    value={interventionModalDraft.institution}
                    onChange={({ detail }) => {
                      updateInterventionModalDraft({ institution: detail.value });
                      setInterventionModalErrors(prev => {
                        const next = { ...prev };
                        delete next.institution;
                        return next;
                      });
                    }}
                    spellcheck={false}
                    readOnly={!interventionModalEditable || isFormLocked}
                  />
                </FormField>
              ) : (
                <Box />
              )}
            </ColumnLayout>
          )}
          {interventionModalEducationCode && (
            <SpaceBetween size="s">
              <ColumnLayout columns={2} variant="text-grid">
                <FormField
                  label="Institution"
                  description="Training provider or school delivering the program."
                  errorText={interventionModalErrors.institution}
                >
                  <Input
                    value={interventionModalDraft.institution}
                    onChange={({ detail }) => {
                      updateInterventionModalDraft({ institution: detail.value });
                      setInterventionModalErrors(prev => {
                        const next = { ...prev };
                        delete next.institution;
                        return next;
                      });
                    }}
                    spellcheck={false}
                    readOnly={!interventionModalEditable || isFormLocked}
                  />
                </FormField>
                <FormField label="Program name (optional)" description="Course, credential, or stream name.">
                  <Input
                    value={interventionModalDraft.programName}
                    onChange={({ detail }) => updateInterventionModalDraft({ programName: detail.value })}
                    spellcheck={false}
                    readOnly={!interventionModalEditable || isFormLocked}
                  />
                </FormField>
              </ColumnLayout>
              <FormField
                label="Training plan details"
                description="Describe what the client will learn, key milestones, any supports or materials needed, and how the training supports their employment goal."
                errorText={interventionModalErrors.itpDetails}
              >
                <Textarea
                  value={interventionModalDraft.itpDetails || ""}
                  rows={3}
                  onChange={({ detail }) => {
                    updateInterventionModalDraft({ itpDetails: detail.value });
                    setInterventionModalErrors(prev => {
                      const next = { ...prev };
                      delete next.itpDetails;
                        return next;
                      });
                    }}
                  spellcheck={true}
                  readOnly={!interventionModalEditable || isFormLocked}
                />
              </FormField>
            </SpaceBetween>
          )}
          {interventionModalEmployerCode && (
            <SpaceBetween size="s">
              <ColumnLayout columns={2} variant="text-grid">
                <FormField
                  label="Employer / delivery partner"
                  description="Employer or host organization providing the placement."
                  errorText={interventionModalErrors.institution}
                >
                  <Input
                    value={interventionModalDraft.institution}
                    onChange={({ detail }) => {
                      updateInterventionModalDraft({ institution: detail.value });
                      setInterventionModalErrors(prev => {
                        const next = { ...prev };
                        delete next.institution;
                        return next;
                      });
                    }}
                    spellcheck={false}
                    readOnly={!interventionModalEditable || isFormLocked}
                  />
                </FormField>
                <FormField label="Program name (optional)" description="Job title, role, or program name.">
                  <Input
                    value={interventionModalDraft.programName}
                    onChange={({ detail }) => updateInterventionModalDraft({ programName: detail.value })}
                    spellcheck={false}
                    readOnly={!interventionModalEditable || isFormLocked}
                  />
                </FormField>
              </ColumnLayout>
              {interventionModalWageSubsidyCode && (
                <FormField
                  label="Wage subsidy details"
                  errorText={interventionModalErrors.wageSubsidyDetails}
                >
                  <Textarea
                    value={interventionModalDraft.wageSubsidyDetails || ""}
                    rows={3}
                    onChange={({ detail }) => {
                      updateInterventionModalDraft({ wageSubsidyDetails: detail.value });
                      setInterventionModalErrors(prev => {
                        const next = { ...prev };
                        delete next.wageSubsidyDetails;
                        return next;
                      });
                    }}
                    spellcheck={true}
                    readOnly={!interventionModalEditable || isFormLocked}
                  />
                </FormField>
              )}
            </SpaceBetween>
          )}
          {interventionModalNeedsNoc && (
            <ColumnLayout columns={2} variant="text-grid">
                <FormField
                  label="NOC version"
                  description="Select the NOC version used for this job/placement."
                  errorText={interventionModalErrors.interventionNocVersion}
                >
                  <Select
                    selectedOption={
                      nocVersionOptions.find(option => option.value === interventionModalDraft.interventionNocVersion) ||
                      null
                    }
                    onChange={({ detail }) => {
                      updateInterventionModalDraft({
                        interventionNocVersion: detail.selectedOption?.value || "",
                        interventionNoc: "",
                      });
                      setNocSuggestions([]);
                      setInterventionModalErrors(prev => {
                        const next = { ...prev };
                        delete next.interventionNocVersion;
                        delete next.interventionNoc;
                        return next;
                      });
                    }}
                    options={nocVersionOptions}
                    placeholder={nocVersionsLoading ? "Loading NOC versions..." : "Select NOC version"}
                    statusType={nocVersionsLoading ? "loading" : "finished"}
                    filteringType="auto"
                    disabled={!interventionModalEditable || isFormLocked || nocVersionsLoading}
                  />
                </FormField>
                <FormField
                  label="NOC code"
                  description="Search by code or title; aligns to the job/placement."
                  errorText={interventionModalErrors.interventionNoc}
                >
                  <Autosuggest
                    value={interventionModalDraft.interventionNoc || ""}
                    onChange={({ detail }) => {
                      const inputValue = detail.value || "";
                      updateInterventionModalDraft({ interventionNoc: inputValue });
                      if (inputValue.length >= 2 && interventionModalDraft.interventionNocVersion) {
                        fetchNocSuggestions(inputValue, interventionModalDraft.interventionNocVersion);
                      } else {
                        setNocSuggestions([]);
                      }
                      setInterventionModalErrors(prev => {
                        const next = { ...prev };
                        delete next.interventionNoc;
                        return next;
                      });
                    }}
                    onSelect={({ detail }) => updateInterventionModalDraft({ interventionNoc: detail.value || "" })}
                    onLoadItems={({ detail }) => {
                      if (detail.filteringText && interventionModalDraft.interventionNocVersion) {
                        fetchNocSuggestions(detail.filteringText, interventionModalDraft.interventionNocVersion);
                      }
                    }}
                    options={nocSuggestions}
                    statusType={nocSuggestionsLoading ? "loading" : "finished"}
                    expandToViewport
                    placeholder={
                      interventionModalDraft.interventionNocVersion
                        ? "Type to search NOC code"
                        : "Select a NOC version first"
                    }
                    empty="No NOC codes found."
                    disabled={
                      !interventionModalEditable ||
                      isFormLocked ||
                      !interventionModalDraft.interventionNocVersion
                    }
                    enteredTextLabel={value => `Use "${value}"`}
                    spellcheck={false}
                  />
                </FormField>
            </ColumnLayout>
          )}
        </SpaceBetween>
      )}
    </Modal>
  );

  const interventionToDelete = interventionDeleteId
    ? proposedInterventions.find(item => idsMatch(item.id, interventionDeleteId))
    : null;
  const interventionDeleteModal = (
    <Modal
      visible={Boolean(interventionDeleteId)}
      onDismiss={() => setInterventionDeleteId(null)}
      header="Delete intervention?"
      footer={
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            variant="primary"
            onClick={confirmInterventionDelete}
            disabled={isFormLocked || isRevisionMode}
          >
            Delete intervention
          </Button>
          <Button variant="normal" onClick={() => setInterventionDeleteId(null)}>Cancel</Button>
        </SpaceBetween>
      }
    >
      <SpaceBetween size="s">
        <Alert type="warning" statusIconAriaLabel="Warning">
          Deleting this intervention will remove all cost items linked to it.
        </Alert>
        <Box>
          {interventionToDelete
            ? `Delete ${resolveInterventionLabel(interventionToDelete.code) || "this intervention"}?`
            : "Delete this intervention?"}
        </Box>
      </SpaceBetween>
    </Modal>
  );

  const costLineDraft = costLineModal.draft || null;
  const costLineMode = costLineModal.mode;
  const isCostLineEditable = costLineMode === "add" || costLineMode === "edit";
  const costLineIntervention = costLineModal.interventionId
    ? proposedInterventions.find(item => idsMatch(item.id, costLineModal.interventionId))
    : null;
  const costLineTypeOptions = costLineIntervention ? buildCostItemOptions(costLineIntervention) : [];
  const costLineTypeLabel = costLineDraft
    ? paymentTypeLabelLookup.get(costLineDraft.type) || costLineDraft.type || ""
    : "";
  const costLineRecurrenceMode = getRecurrenceModeForType(costLineDraft?.type);
  const costLineRecurrenceRequired = costLineRecurrenceMode === RECURRENCE_MODE_REQUIRED;
  const costLineRecurrenceDisabled = costLineRecurrenceMode === RECURRENCE_MODE_NOT_ALLOWED;
  const costLineRecurrenceEnabled = costLineRecurrenceDisabled
    ? false
    : costLineRecurrenceRequired || Boolean(costLineDraft?.recurrence?.enabled);
  const costLineAmountDisplay = costLineDraft
    ? getCurrencyInputDisplayValue(
        sanitizeCurrencyInput(costLineDraft.amount, {
          preserveTrailingDecimal: isCostLineEditable ? costLineAmountFocused : false,
        }),
        isCostLineEditable ? costLineAmountFocused : false,
      )
    : "";
  const costLineAmountPerPeriodDisplay = costLineDraft
    ? getCurrencyInputDisplayValue(
        sanitizeCurrencyInput(costLineDraft.recurrence?.amountPerPeriod, {
          preserveTrailingDecimal: isCostLineEditable ? costLineAmountPerPeriodFocused : false,
        }),
        isCostLineEditable ? costLineAmountPerPeriodFocused : false,
      )
    : "";
  const costLineRecurrenceStart =
    costLineDraft?.recurrence?.startDate || costLineIntervention?.startDate || "";
  const costLineRecurrenceEnd =
    costLineDraft?.recurrence?.endDate || costLineIntervention?.endDate || "";
  const costLinePayeeType = String(costLineDraft?.payee?.type || "").trim();
  const costLineIsStudentLoanRepayment =
    normalizePaymentTypeCode(costLineDraft?.type) === "StudentLoanRepayment" ||
    normalizePayeeTypeKey(costLinePayeeType) === "studentloanservicer";
  const isParticipantPayeeType = costLinePayeeType === PAYEE_TYPE_PARTICIPANT_CLIENT;
  const lockParticipantPayeeName = isParticipantPayeeType && Boolean(participantLegalName);
  const costLinePayeeNamePlaceholder = isParticipantPayeeType
    ? participantLegalName
      ? "Auto-filled from participant legal name"
      : "Participant legal name unavailable - enter full legal name"
    : costLineIsStudentLoanRepayment
      ? "Enter loan provider or servicer name"
    : "Enter payee name";

  const costLineModalContent = (
    <Modal
      visible={costLineModal.visible}
      onDismiss={resetCostLineModal}
      header={costLineMode === "add" ? "Add cost item" : "Cost item details"}
      footer={
        costLineMode === "view" ? (
          <SpaceBetween direction="horizontal" size="xs">
            {!isFormLocked && (
              <Button variant="primary" onClick={() => setCostLineModal(prev => ({ ...prev, mode: "edit" }))}>Edit</Button>
            )}
            {!isFormLocked && (
              <Button
                variant="normal"
                onClick={() => {
                  if (!costLineModal.interventionId || !costLineModal.lineId) return;
                  removeCostLine(costLineModal.interventionId, costLineModal.lineId);
                  resetCostLineModal();
                }}
              >
                Delete
              </Button>
            )}
            <Button variant="link" onClick={resetCostLineModal}>Close</Button>
          </SpaceBetween>
        ) : (
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="primary"
              onClick={commitCostLine}
              disabled={isFormLocked}
            >
              {costLineMode === "add" ? "Add cost item" : "Save changes"}
            </Button>
            <Button variant="link" onClick={resetCostLineModal}>Cancel</Button>
          </SpaceBetween>
        )
      }
    >
      {costLineDraft && (
        <SpaceBetween size="m">
          <FormField label="Cost item" errorText={costLineModalErrors.type}>
            {isCostLineEditable ? (
              <Select
                selectedOption={
                  costLineTypeOptions.find(option => option.value === costLineDraft.type) ||
                  (costLineDraft.type
                    ? { value: costLineDraft.type, label: paymentTypeLabelLookup.get(costLineDraft.type) || costLineDraft.type }
                    : null)
                }
                onChange={({ detail }) => updateCostLineType(detail.selectedOption?.value || "")}
                options={costLineTypeOptions}
                placeholder="Select cost item"
                readOnly={isFormLocked}
              />
            ) : (
              <Input value={costLineTypeLabel} readOnly />
            )}
          </FormField>
          <FormField label="Total amount" errorText={costLineModalErrors.amount}>
            <Input
              inputMode="decimal"
              value={costLineAmountDisplay}
              onChange={({ detail }) => updateCostLineAmount(detail.value)}
              onFocus={() => setCostLineAmountFocused(true)}
              onBlur={blurCostLineAmount}
              placeholder="0.00"
              spellcheck={false}
              readOnly={!isCostLineEditable || isFormLocked}
            />
          </FormField>
          <FormField label="Payee type">
            <Select
              selectedOption={findOptionByValue(configuredPayeeTypeOptions, costLineDraft.payee?.type)}
              onChange={({ detail }) => updateCostLinePayeeType(detail.selectedOption?.value || "")}
              options={configuredPayeeTypeOptions}
              placeholder="Select payee type"
              readOnly={!isCostLineEditable || isFormLocked}
            />
          </FormField>
          <FormField label={costLineIsStudentLoanRepayment ? "Loan provider / servicer name" : "Payee name"}>
            <Input
              value={costLineDraft.payee?.name || ""}
              onChange={({ detail }) =>
                updateCostLineDraft({
                  payee: {
                    ...(costLineDraft.payee || {}),
                    name: detail.value,
                  },
                })
              }
              placeholder={costLinePayeeNamePlaceholder}
              spellcheck={false}
              readOnly={!isCostLineEditable || isFormLocked || lockParticipantPayeeName}
            />
          </FormField>
          {costLinePayeeType && !isParticipantPayeeType && (
            <FormField label={costLineIsStudentLoanRepayment ? "Loan account number (optional)" : "Payee reference (optional)"}>
              <Input
                value={costLineDraft.payee?.reference || ""}
                onChange={({ detail }) =>
                  updateCostLineDraft({
                    payee: {
                      ...(costLineDraft.payee || {}),
                      reference: detail.value,
                    },
                  })
                }
                placeholder={costLineIsStudentLoanRepayment ? "Enter loan account number" : "Vendor/account reference"}
                spellcheck={false}
                readOnly={!isCostLineEditable || isFormLocked}
              />
            </FormField>
          )}
          <FormField label="Installments (monthly)" errorText={costLineModalErrors.recurrence}>
            <Checkbox
              checked={costLineRecurrenceEnabled}
              onChange={({ detail }) => toggleCostLineRecurrence(detail.checked)}
              disabled={
                !isCostLineEditable ||
                costLineRecurrenceRequired ||
                costLineRecurrenceDisabled ||
                isFormLocked
              }
            >
              Enable installments
            </Checkbox>
          </FormField>
          {costLineRecurrenceEnabled && (
            <SpaceBetween size="s">
              <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
                <FormField label="Start date">
                  <DatePicker
                    value={costLineRecurrenceStart}
                    onChange={({ detail }) => updateCostLineRecurrenceStart(detail.value)}
                    readOnly={!isCostLineEditable || isFormLocked}
                  />
                </FormField>
                <FormField label="End date (optional)">
                  <DatePicker
                    value={costLineRecurrenceEnd}
                    onChange={({ detail }) => updateCostLineRecurrenceEnd(detail.value)}
                    readOnly={!isCostLineEditable || isFormLocked}
                  />
                </FormField>
              </Grid>
              <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
                <FormField label="Number of installments">
                  <Input
                    inputMode="numeric"
                    value={costLineDraft.recurrence?.occurrences || ""}
                    onChange={({ detail }) => updateCostLineOccurrences(detail.value)}
                    spellcheck={false}
                    readOnly={!isCostLineEditable || isFormLocked}
                  />
                </FormField>
                <FormField label="Amount per month">
                  <Input
                    inputMode="decimal"
                    value={costLineAmountPerPeriodDisplay}
                    onChange={({ detail }) => updateCostLineAmountPerPeriod(detail.value)}
                    onFocus={() => setCostLineAmountPerPeriodFocused(true)}
                    onBlur={blurCostLineAmountPerPeriod}
                    spellcheck={false}
                    readOnly={!isCostLineEditable || isFormLocked}
                  />
                </FormField>
              </Grid>
            </SpaceBetween>
          )}
          <FormField label="Notes (optional)">
            <Textarea
              value={costLineDraft.notes || ""}
              rows={3}
              onChange={({ detail }) => updateCostLineDraft({ notes: detail.value })}
              spellcheck={true}
              readOnly={!isCostLineEditable || isFormLocked}
            />
          </FormField>
        </SpaceBetween>
      )}
    </Modal>
  );

  const handleFinishDecisionRevision = async () => {
    if (!isDecisionRevisionEditing || isSubmitting) return;
    if (!isDirty) {
      setError(null);
      setDecisionRevisionEditing(false);
      setCurrentStep('decision');
      return;
    }
    await handleSave();
  };

  const wizardStepIds = isDecisionRevisionEditing ? BASE_STEP_IDS : activeStepIds;
  const steps = wizardStepIds
    .map(stepId => ({
      id: stepId,
      title: stepId === 'review' && isDecisionRevisionEditing ? 'Review changes' : STEP_LABELS[stepId],
      content: {
        plan: planStepContent,
        framing: framingStepContent,
        rationale: rationaleStepContent,
        otherFunding: otherFundingStepContent,
        childcare: childcareStepContent,
        cost: costStepContent,
        docs: docsStepContent,
        review: reviewStepContent,
        decision: decisionStepContent,
      }[stepId],
      isOptional: false,
    }))
    .filter(Boolean);

  const activeStepIndex = Math.max(wizardStepIds.indexOf(currentStep), 0);
  const submitterSubmitLabel = isChangesRequestedStatus ? "Resubmit for review" : "Submit for review";
  const submittedReviewLabel = isRevisionMode ? "Submit change for review" : "Submit proposal for review";
  const wizardSubmitLabel = isDecisionRevisionEditing ? (isDirty ? "Save revision" : "Return to decision") : isReviewStageStatus
    ? canDecideSubmittedProposal
      ? "Submit decision"
      : submittedReviewLabel
    : submitterSubmitLabel;
  const wizardSubmitHandler = isDecisionRevisionEditing ? handleFinishDecisionRevision : isReviewStageStatus && canDecideSubmittedProposal
    ? handleSubmitDecision
    : handleSubmitProposal;
  const wizardIsWorking = !formScopeReady || isSubmitting || cancellingRevision ||
    Boolean(reviewWorkflowActionLoading) || isRecallingSubmission || sendingLetter || openingRevision;
  const hasUnsavedLetters = Boolean(clientLetterBody.trim() && !decisionLetterAlreadySent) ||
    (approvalLetterPackGenerated && JSON.stringify([
      editableInstitutionApprovalLetters, editableCoFunderApprovalLetters, editableLoanProviderApprovalLetters,
    ]) !== generatedSupplementaryLettersRef.current);
  const { returnToWorkQueue, returnConfirmation } = useWorkQueueReturn(
    role, hasUnsavedLetters, reviewStage,
    (!completionNote && isEditable && isDirty) || (canRegionalManagerReview && Boolean(reviewWorkflowNote.trim())),
    { busy: wizardIsWorking, widgetId: 'interventionAssessment', onDiscard: () => {
      discardingDraftRef.current = true;
      for (const key of new Set([wizardStepKey, `${caseId}:${activeInterventionIdValue}`])) {
        if (key) clearInterventionWizardDraft(key);
      }
    } }
  );
  const canCancelRevision = Boolean(isRevisionMode && isProposalBodyEditable &&
    String(statusValue).toLowerCase() === 'draft' && activeInterventionIdValue &&
    !idsMatch(activeInterventionIdValue, revisionSourceInterventionId) &&
    (!hasReviewWorkflow || reviewStage === 'withdrawn'));
  const leaveAssessment = () => {
    setSelectedInterventionId(null);
    const query = new URLSearchParams();
    if (applicationId) query.set('applicationId', String(applicationId));
    if (selectedActionPlanId) query.set('planId', String(selectedActionPlanId));
    history.replace(carryWorkQueueOrigin(`/cases/${caseId}?${query.toString()}`, history.location));
    window.dispatchEvent(new CustomEvent('iset-case-workspace:set-layout', { detail: { layoutId: 'caseWorkspace', afterSavedAction: true } }));
  };
  const cancelRevision = async () => {
    if (!canCancelRevision || wizardIsWorking || cancellingRevisionRef.current) return;
    cancellingRevisionRef.current = true;
    setCancellingRevision(true);
    setCancelRevisionError(null);
    try {
      await deleteIntervention(Number(activeInterventionIdValue));
      cancelledRevisionRef.current = true;
      for (const key of new Set([wizardStepKey, `${caseId}:${activeInterventionIdValue}`])) {
        if (key) {
          clearInterventionWizardStep(key);
          clearInterventionWizardDraft(key);
        }
      }
      setShowCancelRevision(false);
      setForm(defaultFormState);
      initialFormRef.current = defaultFormState;
      setSelectedDraftId(null);
      setHydratedDraftId(null);
      setRevisionContext(null);
      setCompletionNote(null);
      leaveAssessment();
      dispatchCaseNotesRefresh();
    } catch (err) {
      setCancelRevisionError(err?.message || 'Unable to cancel the revision. Your draft has been kept.');
    } finally {
      cancellingRevisionRef.current = false;
      setCancellingRevision(false);
    }
  };
  const hasAssessmentWork = Boolean(explicitlyCreatingProposal || selectedDraftId || hydratedDraftId || activeInterventionRecord ||
    hasBlockingProposal || pendingCompletions.length || completionNote);


  return (
    <BoardItem header={
      <Header
        variant="h2"
        info={infoLink}
        actions={
          <SpaceBetween direction="horizontal" size="s">
            {!decisionLetterAlreadySent && headerRevisionAction.available && !idsMatch(headerRevisionAction.draft?.id, activeInterventionIdValue) && (
              <Button loading={openingRevision} disabled={openingRevision || sendingLetter || isSubmitting}
                onClick={() => clientLetterBody.trim() && !decisionLetterAlreadySent
                  ? setShowRevisionNavigationConfirm(true) : openHeaderRevision()}>
                {headerRevisionAction.label}
              </Button>
            )}
            {canReviseAssessment && !isDecisionRevisionEditing && <AssessmentEditButton onConfirm={() => {
              decisionRevisionBaseline.current = activeInterventionRecord.decisionVersion;
              setDecisionRevisionEditing(true); setCurrentStep('framing');
            }} />}
            {isDecisionRevisionEditing && <>
              <Button disabled={isSubmitting} onClick={() => {
                setForm(initialFormRef.current); setDecisionRevisionEditing(false); setCurrentStep('decision');
              }}>Cancel edits</Button>
              <Button variant="primary" disabled={isSubmitting || !isDirty} loading={isSubmitting} onClick={handleFinishDecisionRevision}>Save revision</Button>
            </>}
            {canRecallSubmittedProposal && (
              <Button
                variant="normal"
                onClick={() => setShowRecallConfirmModal(true)}
                loading={isRecallingSubmission}
                disabled={isRecallingSubmission}
              >
                Recall submission
              </Button>
            )}
            {hasAssessmentWork && isEditable && !completionNote && (!isReviewStageStatus || !canDecideSubmittedProposal) && (
              <Button
                variant="primary"
                disabled={wizardIsWorking || (!isDirty && !isReviewStageStatus)}
                onClick={isReviewStageStatus ? handleSubmitProposal : handleSave}
              >
                {isReviewStageStatus
                  ? isRevisionMode
                    ? "Submit change"
                    : "Submit proposal"
                  : "Save progress"}
              </Button>
            )}
          </SpaceBetween>
        }
      >
        {interventionWidgetTitle}
      </Header>
    } i18nStrings={boardItemI18nStrings} settings={
      <ButtonDropdown
        items={[{ id: "remove", text: "Remove" }]}
        ariaLabel="Board item settings"
        variant="icon"
        onItemClick={() => actions && actions.removeItem && actions.removeItem()}
      />
    }>
      <div id="intervention-assessment-widget">
        <Modal visible={showRevisionNavigationConfirm} onDismiss={() => setShowRevisionNavigationConfirm(false)}
          header="Leave this unsent letter draft?"
          footer={<SpaceBetween direction="horizontal" size="s">
            <Button onClick={() => setShowRevisionNavigationConfirm(false)}>Keep editing letter</Button>
            <Button variant="primary" onClick={openHeaderRevision}>Discard letter draft and open revision</Button>
          </SpaceBetween>}>
          The letter has not been sent. Copy any wording you want to keep before opening the revision.
          The recorded approval and its pending communication will remain in the case history.
        </Modal>
        {returnConfirmation}
        {hasAssessmentWork && headerDescription ? (
          <Box variant="small" margin={{ bottom: "s" }}>
            {headerDescription}
          </Box>
        ) : null}
        {error || (successMessage && !completionNote) || submitterChangeInstructionsAlert ? (
          <Box margin={{ bottom: "m" }}>
            <SpaceBetween size="s">
              {error && (
                <Alert type="error" dismissible onDismiss={() => setError(null)} statusIconAriaLabel="Error">
                  {error}
                </Alert>
              )}
              {successMessage && !completionNote && (
                <Alert type="success" dismissible onDismiss={() => setSuccessMessage("")} statusIconAriaLabel="Success">
                  {successMessage}
                </Alert>
              )}
              {submitterChangeInstructionsAlert}
            </SpaceBetween>
          </Box>
        ) : null}
        {!hasAssessmentWork ? (
          <Box color="text-body-secondary">No intervention assessment in progress.</Box>
        ) : !selectedDraftId && !hydratedDraftId && !hasBlockingProposal && !explicitlyCreatingProposal && pendingCompletions.length > 1 ? (
          <SpaceBetween size="s">
            <Box>Select an approved intervention to complete its follow-up.</Box>
            {pendingCompletions.map(item => (
              <Button key={item.id} onClick={() => {
                setSelectedDraftId(item.id);
                setSelectedInterventionId(item.id);
              }}>{item.title || `Intervention ${item.id}`}</Button>
            ))}
          </SpaceBetween>
        ) : completionNote ? (
          <SpaceBetween size="m">
            {decisionLetterAlreadySent ? <StatusIndicator type="success">Client letter sent.</StatusIndicator> : <RecordedDecision
              title={isApprovedDecisionOutcome && decisionMakerDisplayName
                ? `${isRevisionMode ? "Revision approved" : "Intervention approved"} by ${decisionMakerDisplayName}${form.decisionNotes?.trim() ? " with the following note:" : ""}`
                : completionNote.header}
              description={isApprovedDecisionOutcome && !completionNote.followUpIssue
                ? (decisionLetterAlreadySent ? "Client letter sent." : undefined)
                : completionNote.body}
              note={form.decisionNotes}
              noteLabel={null}
            />}
            {showDecisionLettersPanel ? communicationStepContent : null}
            <WorkflowExitActions onReturn={returnToWorkQueue} disabled={wizardIsWorking} />
          </SpaceBetween>
        ) : (
          <WorkflowWizard
            className={isPlanStepBlocked || isFramingStepBlocked ? styles.blockNext : undefined}
            activeStepIndex={activeStepIndex}
            isLoadingNextStep={wizardIsWorking}
            onNavigate={async ({ detail }) => {
              if (wizardIsWorking) return;
              const requestedStepIndex = detail?.requestedStepIndex;
              if (requestedStepIndex < 0 || requestedStepIndex >= wizardStepIds.length) return;
              const requestedStepId = wizardStepIds[requestedStepIndex];
              const currentIdx = wizardStepIds.indexOf(currentStep);
              const movingForward = requestedStepIndex > currentIdx;
              if (movingForward && isPlanStepBlocked) {
                setAttemptedSteps(prev => ({ ...prev, plan: true }));
                setError("Create an action plan before proposing interventions.");
                return;
              }
              if (movingForward && isFramingStepBlocked) {
                setAttemptedSteps(prev => ({ ...prev, framing: true }));
                setError("Add at least one proposed intervention before continuing.");
                return;
              }
              if (movingForward) {
                setAttemptedSteps(prev => ({ ...prev, [currentStep]: true }));
                if (!validateStep(currentStep)) {
                  setError("Complete required fields before continuing.");
                  return;
                }
              }
              if (
                requestedStepIndex !== currentIdx &&
                !isDecisionRevisionEditing &&
                !isReviewStageStatus &&
                isEditable &&
                isDirty &&
                !isSubmitting &&
                canAutoSave
              ) {
                const saveResult = await handleSave({ silent: true });
                if (!saveResult?.ok) {
                  setError(saveResult?.error?.message || "Failed to save progress.");
                  return;
                }
              }
              setError(null);
              setCurrentStep(requestedStepId);
            }}
            onCancel={canCancelRevision ? () => {
              if (wizardIsWorking) return;
              setCancelRevisionError(null);
              setShowCancelRevision(true);
            } : undefined}
            i18nStrings={{ cancelButton: 'Cancel revision' }}
            onReturn={returnToWorkQueue}
            showReturnAction
            taskComplete={currentStep === 'review' && !isEditable && !canRegionalManagerReview}
            hasInlineActions={currentStep === 'review' && canRegionalManagerReview}
            onSubmit={isEditable && !wizardIsWorking ? wizardSubmitHandler : undefined}
            submitButtonText={wizardIsWorking ? "Working" : wizardSubmitLabel}
            steps={steps}
          />
        )}
        <Modal
          visible={showCancelRevision}
          header="Cancel this revision?"
          onDismiss={() => { if (!cancellingRevision) setShowCancelRevision(false); }}
          closeAriaLabel="Keep editing revision"
          footer={<SpaceBetween direction="horizontal" size="xs">
            <Button disabled={cancellingRevision} onClick={() => setShowCancelRevision(false)}>Keep editing</Button>
            <Button variant="primary" loading={cancellingRevision} disabled={wizardIsWorking} onClick={cancelRevision}>Cancel revision</Button>
          </SpaceBetween>}
        >
          <SpaceBetween size="s">
            <Box>The proposed changes will be discarded. The existing approved intervention will remain unchanged.</Box>
            {cancelRevisionError && <Alert type="error">{cancelRevisionError}</Alert>}
          </SpaceBetween>
        </Modal>
        <Modal
          visible={showRecallConfirmModal}
          onDismiss={() => {
            if (!isRecallingSubmission) {
              setShowRecallConfirmModal(false);
            }
          }}
          header="Recall submission?"
          footer={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="primary"
                onClick={handleRecallSubmission}
                loading={isRecallingSubmission}
                disabled={isRecallingSubmission}
              >
                Recall submission
              </Button>
              <Button
                variant="normal"
                onClick={() => setShowRecallConfirmModal(false)}
                disabled={isRecallingSubmission}
              >
                Cancel
              </Button>
            </SpaceBetween>
          }
        >
          <Box>
            This will move the proposal back to Draft, remove the submitted assessment PDFs from the active document
            list, and let you make corrections before submitting again.
          </Box>
        </Modal>
        {sendApprovalLetterConfirmModal}
        {decisionBlockerModal}
        {otherFundingSourceModalContent}
        {interventionModalContent}
        {interventionDeleteModal}
        {costLineModalContent}
      </div>
    </BoardItem>
  );
};

export default InterventionAssessmentWidget;
